import {
  type BossTier,
  bossGoldForTier,
  CANNON_CATALOG,
  type CannonRarity,
  type CardLoadout,
  type CardSlot,
  cappedBossWidth,
  cardWorksIn,
  DEFAULT_BOSS_TIER,
  EMPTY_JEWEL_SOCKET_PROFILE,
  type EquipmentTotals,
  type JewelSocketProfile,
  NO_ARTIFACT_EFFECTS,
  NO_EQUIPMENT_TOTALS,
  type ScaledArtifactEffects,
  type SimulationSeed,
  shipCatalogEntry,
  shipDamageBonusBpsAtLevel,
} from '@kenocyber/shared';
import { cannonCountForLevel } from '../cannons';
import {
  CARD_CHARGE_FULL_BPS,
  type CardBalance,
  cardBalance,
  cardBeamWidthFor,
  cardChargeProgressBps,
  cardScaleByCharge,
  cardSphereRadiusFor,
} from '../cards/balance';
import {
  ageCardEffects,
  applyCardTimer,
  CARD_EFFECT_SLOTS,
  type CardEffectState,
  cardIsHittable,
  initialCardEffects,
} from '../cards/effects';
import {
  ageCardSummon,
  CARD_DRONE_HEALTH_BPS,
  CARD_DRONE_KAMIKAZE_BPS,
  CARD_DRONE_KAMIKAZE_REACH_BPS,
  CARD_LEVIATHAN_ENTRY_BPS,
  CARD_LEVIATHAN_IDLE_MS,
  CARD_MAX_SUMMONS,
  type CardSummonKind,
} from '../cards/summons';
import {
  type AtomicBombSelection,
  type AtomicBombSelectionWire,
  atomicBombFromWire,
  atomicBombToWire,
  centralVolleyIndex,
  HUNT_VOLLEY_BOMB_RNG_LABEL,
  selectVolleyBomb,
} from '../damage/atomic-bomb';
import { DAMAGE_MILLI_PER_UNIT } from '../damage/base';
import {
  bleedingDetonationMilli,
  HUNT_BLEEDING_RNG_LABEL,
  selectBleedingApplication,
} from '../damage/bleeding';
import { hostileDamageMultiplierBps, reduceHostileDamage } from '../damage/damage-reduction';
import { primordialHeartBps, resolveStrike, shotDamageMilli } from '../damage/exact';
import { resolveJewelEffects } from '../damage/jewel-effects';
import { HEAL_MILLI_PER_UNIT, resolveLifestealHeal } from '../damage/lifesteal';
import {
  chooseMissileTargetIndex,
  HUNT_MISSILE_TARGET_RNG_LABEL,
  MISSILE_LIFETIME_MS,
  type MissileSelection,
} from '../damage/missile';
import {
  ATOMIC_BLAST_EFFECT,
  type DamageOrigin,
  damageOriginMayTriggerJewel,
  jewelTriggersFor,
  LIFESTEAL_HEAL_EFFECT,
  MISSILE_EFFECT,
} from '../damage/origin';
import {
  HUNT_POISON_RNG_LABEL,
  POISON_TICK_INTERVAL_MS,
  poisonIsSpent,
  poisonPendingTicks,
  poisonTickDamageMilli,
  selectPoisonApplication,
} from '../damage/poison';
import {
  HUNT_OUTGOING_PROC_RNG_LABEL,
  type JewelProcSelection,
  jewelProcFromWire,
  jewelProcToWire,
  type OutgoingJewelProcId,
  PROC_CHANCE_DENOMINATOR,
  selectOutgoingProc,
} from '../damage/proc-lottery';
import {
  HUNT_SAW_RNG_LABEL,
  SAW_INITIAL_PHASE,
  SAW_ORBIT_PHASE_UNITS,
  SAW_VECTOR_SCALE,
  sawAdvancePhase,
  sawBladeCountForLevel,
  sawBladeRadius,
  sawBladeStep,
  sawContactDamageMilli,
  sawContactDue,
  sawDirection,
  sawLevelOf,
  sawOrbitRadius,
  sawSpeedNumerator,
} from '../damage/saw';
import {
  applyVitalHealFractional,
  HUNT_VITAL_HEART_RNG_LABEL,
  selectVitalHeal,
  type VitalHealSelection,
  vitalHealPoints,
} from '../damage/vital-heart';
import {
  selectVolleyMissile,
  type VolleyProcId,
  volleyProcInterval,
} from '../damage/volley-lottery';
import { fieldCadenceIsLimited, fieldEmittedVolleysPerSecond } from '../hull/attack-speed';
import { composeMoveSpeedBps, MOVE_SPEED_BPS_ONE } from '../hull/move-speed';
import { mapRewardBpsForMap, mobScaleBpsForMap } from '../maps';
import {
  createSimulationRng,
  deriveSimulationSeed,
  restoreSimulationRng,
  type SimulationRng,
} from '../simulation-rng';
import {
  type AccountMaxHealthSources,
  accountFlatMaxHealth,
  accountMaxHealth,
  accountMaxHealthBps,
} from './account-max-health';
import { effectiveBaseAttackSpeed, renderableAmount, renderablePoints } from './attributes';
import {
  bossHealth,
  HUNT_BALANCE,
  HUNT_BOMB_BLAST_RADIUS,
  HUNT_BOMB_PROXIMITY_RADIUS,
  HUNT_BOMB_SPEED,
  HUNT_CATOLE_SPEED,
  HUNT_FIREBALL_BODY_BPS,
  HUNT_FIREBALL_CHARGE_MS,
  HUNT_FIREBALL_REACH_BPS,
  HUNT_FIREBALL_SPEED,
  HUNT_MISSILE_HIT_RADIUS,
  HUNT_MISSILE_MAX_TURN_RAD_PER_SECOND,
  HUNT_MISSILE_SPEED,
  HUNT_SOLAR_LANCE_REACH_BPS,
  HUNT_SOLAR_LANCE_WIDTH_BPS,
  huntAimIsInsideTurn,
  huntMissileAimPoint,
  huntSweepCircle,
  mobDisplaySizeForMap,
  mobHealthForMap,
  mobLeakDamage,
  stepFacing,
} from './balance';
import {
  activateCardAbility,
  ageCardAbility,
  CATOLE_BLAST_EFFECT,
  type CardAbilityState,
  cardAbilityCanActivate,
  cardAbilityCanRelease,
  cardAbilityChargeEnded,
  cardAbilityIsActive,
  cardAbilityIsFiring,
  cardAbilityIsImplementedIn,
  cardBeginCooldown,
  FIREBALL_BLAST_EFFECT,
  initialCardAbilities,
  releaseCardCharge,
} from './card-abilities';
import { type CannonSlot, cannonFormation, slotMuzzle, slotToWorld } from './formation';
import {
  equipmentAttackSpeedPercent,
  equipmentBaseAttackSpeed,
  equipmentBonusAttack,
  equipmentDamagePercent,
  goldWithPassives,
  HEALTH_MILLI_PER_UNIT,
  huntShipBaseDamageMilli,
  type PassiveAttributes,
  passiveBaseAttackSpeed,
  passiveDamagePercent,
  passiveRegenPerSecond,
} from './passive-attributes';
import {
  PET_BALANCE,
  type PetBeamProfile,
  type PetTargetingMode,
  petBeamInstancesPerSecond,
  petBlastRadius,
  petBulletDamage,
  petBulletDamageMilli,
  petBulletsPerSecond,
  petCombatProfile,
  petDamagePerSecond,
  petMuzzleOffsets,
  petProjectileCount,
  petProjectilesPerGun,
  petSplashDamageMilli,
  petVolleysPerSecond,
} from './pet-balance';
import {
  allocatePoints as allocateProgressionPoints,
  applyXpGain,
  INITIAL_PROGRESSION,
  type ProgressionState,
  xpRequiredForLevel,
} from './progression';
import {
  fromProgressionState,
  HUNT_SNAPSHOT_VERSION,
  type HuntSnapshot,
  type HuntSnapshotBossEncounter,
  type HuntSnapshotProgression,
  parseDecimalString,
  toProgressionState,
} from './snapshot';

/**
 * The hunt simulation - pure, deterministic, renderer-agnostic.
 *
 * - Fixed integer timestep (`HUNT_BALANCE.simulation.tickMs`, 16 ms).
 *   `advance(deltaMs)` accumulates wall-clock time and runs whole ticks;
 *   chunking the same total time differently yields the exact same state
 *   (integer accumulator, no float time).
 * - All randomness comes from the SIMULATION RNG seeded at construction. The
 *   economic RNG is never touched here - nothing in a hunt is economic-grade
 *   in this phase, and the seed is deliberately client-visible.
 * - Rendering reads state and drains presentation events; it never writes.
 *
 * Progression (level, xp, gold, points) lives in memory only in this slice
 * and resets on page reload. It is NOT authoritative - the server will own it.
 */

/** Single source of truth for the fixed timestep. */
export const HUNT_TICK_MS = HUNT_BALANCE.simulation.tickMs;

/**
 * Total XP owed for `kills` normal mobs on `mapIndex`: 4 for every 3 on map 1,
 * times the map's reward curve.
 *
 * Integer arithmetic all the way. Taking the difference between two calls is
 * what turns the rate into a per-kill grant without rounding a single mob, and
 * on map 1 any three consecutive kills still add up to exactly 4.
 *
 * ## Uma divisão só, e o mapa DENTRO dela (gate V28I.BP)
 *
 * O multiplicador entra no numerador e o 10000 no denominador, para haver
 * exactamente uma truncagem no fim — a mesma disciplina que já fazia a fracção
 * de 4/3 sobreviver a um checkpoint, a um F5 e a uma morte. Multiplicar o
 * resultado já truncado por `bps/10000` perderia o resto de cada morte, que é
 * precisamente o que este acumulador existe para não perder.
 *
 * `mapIndex` é obrigatório e não tem valor por omissão: um recuo silencioso
 * para o mapa 1 seria pagar a tarifa errada sem ninguém dar por isso.
 */
export function normalKillXp(kills: number, mapIndex: number): bigint {
  return (
    (BigInt(kills) * BigInt(HUNT_BALANCE.mobs.xpRewardNumerator) * mapRewardBpsForMap(mapIndex)) /
    (BigInt(HUNT_BALANCE.mobs.xpRewardDenominator) * 10_000n)
  );
}

/**
 * Total Gold owed for `kills` normal mobs on `mapIndex`: 1 for every 2 on map 1,
 * times the map's reward curve.
 *
 * Gold is a ledger amount, and a fraction of one has no meaning there, so the
 * reward accumulates instead of rounding: the difference between two calls
 * lands on exactly the map's rate over enough kills. Ver a nota da XP acima
 * sobre porque a divisão é uma só.
 */
export function normalKillGold(kills: number, mapIndex: number): bigint {
  return (
    (BigInt(kills) * BigInt(HUNT_BALANCE.mobs.goldRewardNumerator) * mapRewardBpsForMap(mapIndex)) /
    (BigInt(HUNT_BALANCE.mobs.goldRewardDenominator) * 10_000n)
  );
}

/**
 * A recompensa-BASE de um chefe neste mapa, antes do multiplicador do nível.
 *
 * Uma truncagem, e antes do nível: o nível multiplica a base do MAPA, que é
 * exactamente o que `bossGoldForTier` já dizia fazer — "the map's base, times
 * the tier". Até este gate a base do mapa era a mesma em todos os vinte.
 */
export function bossBaseRewardForMap(base: number, mapIndex: number): bigint {
  return (BigInt(base) * mapRewardBpsForMap(mapIndex)) / 10_000n;
}

/**
 * **O raio do casco da caça, em unidades da arena.** Derivado, nunca escrito.
 *
 * Metade da largura desenhada da nave, e o raio REAL de toda a corrida:
 * {@link HuntSimulation.hullRadius} devolve exatamente este número, para
 * qualquer vida máxima. A caça não dimensiona o casco pela vida, e a razão está
 * lá.
 *
 * É dele que a órbita e o tamanho de uma lâmina saem, exatamente como o duelo
 * mede os seus contra o raio do casco de lá - é isso que faz as serras terem a
 * mesma PROPORÇÃO em relação à nave nos dois modos, apesar de as duas arenas
 * terem escalas completamente diferentes.
 */
export const HUNT_SHIP_BASE_RADIUS = HUNT_BALANCE.ship.displayWidth / 2;

/**
 * Em quantas posições a órbita é amostrada dentro de um tique (gate V28G.P).
 *
 * Quatro, o mesmo número de sub-passos que um projétil do duelo usa, e pela
 * mesma razão: uma coisa que se move depressa entre dois tiques tem de ser
 * testada onde ESTEVE, e não só onde acabou.
 *
 * A conta está fechada na nota de `resolveSawContacts` e a margem é folgada, o
 * que é deliberado - varrer é o que a mantém folgada no dia em que alguém
 * aumentar a velocidade da órbita.
 */
export const HUNT_SAW_SUBSTEPS = 4;

/** Nenhuma lâmina, congelado e partilhado: o caso comum não deve alocar. */
const NO_SAW_BLADES: readonly HuntSawBlade[] = Object.freeze([]);

export type HuntPhase = 'active' | 'dead';

export interface MobEntity {
  readonly id: number;
  readonly kind: 'mob';
  x: number;
  y: number;
  health: number;
  readonly maxHealth: number;
  readonly size: number;
  /** Set once the leak damage was applied; a mob can never damage twice. */
  leaked: boolean;
}

export type BossPhase = 'revealing' | 'descending';

export interface BossEntity {
  readonly id: number;
  readonly kind: 'boss';
  x: number;
  y: number;
  health: number;
  readonly maxHealth: number;
  /** Effective rendered scale relative to the map's mob size. */
  readonly scale: number;
  readonly size: number;
  phase: BossPhase;
  revealRemainingMs: number;
}

export interface ProjectileEntity {
  readonly id: number;
  x: number;
  y: number;
  dirX: number;
  dirY: number;
  /**
   * O que este tiro vai tirar, em MILLI, decidido quando ele NASCEU.
   *
   * ## Porque `bigint`, e porque milli
   *
   * `bigint` porque a contagem de Corações Primordiais incorporados não tem
   * tecto de jogo: o produto pode ter mais dígitos do que um `double` guarda, e
   * convertê-lo seria capar o efeito de um item num tipo.
   *
   * Milli porque um coração torna o valor fracionário em pontos inteiros, e uma
   * truncatura apagá-lo-ia nas contagens baixas - +1 % de 40 é 40,4, e 40 não é
   * uma resposta.
   *
   * ## Nunca recalculado
   *
   * É a fronteira temporal inteira do gate V28F.B. Um projétil que perguntasse
   * ao estado atual no impacto escalaria com o que a nave tivesse entretanto, e
   * o jogador veria um tiro mudar de ideias em voo.
   *
   * A instantânea guarda-o como STRING DECIMAL - ver `hunt/snapshot.ts`, versão
   * 3 - pela mesma regra dos saldos.
   */
  readonly damageMilli: bigint;
  /**
   * **O proc de joia que este tiro traz, decidido quando ele NASCEU.**
   *
   * `null` para um tiro comum, e para toda a bala do pet: a loteria corre uma
   * vez por nascimento de projétil de CANHÃO, e `pet` não pode originar nada.
   *
   * ## Porque é congelado, e não perguntado no impacto
   *
   * Pela mesma lei que congela `damageMilli`. Um sorteio no impacto teria de
   * reler o perfil, e o perfil pode ter mudado entre o disparo e a batida -
   * trocar de skin a meio decidiria o que já estava no ar.
   *
   * `entropy` viaja com a seleção para dar determinismo a decisões internas
   * futuras - uma direção, um subtipo - sem uma segunda rolagem no impacto, que
   * seria uma rolagem que a instantânea teria de preservar à parte.
   *
   * **Obrigatório, e nunca um campo opcional.** Um valor por omissão é
   * exatamente o mecanismo pelo qual um sítio de criação novo herdaria uma
   * classificação que ninguém escolheu para ele - a mesma disciplina que fez
   * `applyHit` deixar de ter `fromPet = false`.
   */
  readonly jewelProc: JewelProcSelection | null;
  /**
   * A BOMBA que este projétil É, ou ausente para um tiro comum (gate V28G.J).
   *
   * Presente quer dizer que a salva transformou ESTE projétil no Núcleo
   * Atômico. Ele deixa de perseguir, voa a 80 % da velocidade, deteta por
   * proximidade e explode - não é um tiro com um extra pendurado.
   *
   * Opcional e não obrigatório, ao contrário de `jewelProc`, e a diferença tem
   * uma razão: a caça cria projéteis de PET, e um pet não pode originar joia
   * nenhuma. Um campo obrigatório obrigaria cada boca de cada pet a escrever
   * `atomicBomb: undefined`, que é ruído sem informação. A ausência já significa
   * exatamente o que tem de significar.
   *
   * `blastMilli` foi congelado quando a salva nasceu, pela mesma lei que congela
   * `damageMilli`: um projétil no ar não relê o perfil.
   */
  readonly atomicBomb?: AtomicBombSelection;
  /** The only entity this projectile may ever damage. */
  readonly targetId: number;
  /** True once the original target died: keeps heading, damages nothing. */
  orphaned: boolean;
  /**
   * True for a bullet fired by the PET rather than the ship.
   *
   * The flight rules, the homing and the orphan rule are identical - a pet
   * bullet is a discrete projectile like every other, never a beam. This flag
   * exists so the renderer can paint it in the pet's own energy and so the
   * Overview can attribute the damage; it changes no physics.
   */
  readonly fromPet?: boolean;
  /**
   * Radius of the blast this projectile leaves, arena units. Absent or zero
   * means it does not explode and resolves exactly as every shot always has.
   *
   * This is SIMULATION state, not decoration: it decides who takes damage, so
   * it is part of the snapshot and part of the digest. Leaving it out would let
   * the server re-simulate a run in which the same ball killed a different set
   * of mobs.
   */
  readonly blastRadius?: number;
  /** Damage every OTHER live enemy in the radius takes, em MILLI e exato. */
  readonly splashDamageMilli?: bigint;
  /**
   * Where an explosive projectile detonates if its target dies first.
   *
   * Updated to the target's position while it lives, so an orphaned ball flies
   * on to the last place its target was and explodes there - which is what
   * makes a blast that was already "aimed" still clear the enemies around a mob
   * that someone else finished off. Absent on a non-explosive shot.
   */
  fuseX?: number;
  fuseY?: number;
  /**
   * True for a shot that ENDS IN AN EXPLOSION rather than simply landing.
   *
   * Separate from `blastRadius` on purpose: the Abyssal Drone's balls detonate
   * AND damage bystanders, while the Ballistic Drone's missiles detonate and
   * damage nobody but the enemy they struck. Both fly on to the last known
   * position of a target that died; only one of them hurts anything when it
   * gets there.
   */
  readonly detonates?: boolean;
  /**
   * A guided missile's current speed, arena units per second.
   *
   * Present only on a missile, and MUTATED per tick as the boost spends
   * itself. Simulation state: it decides where the shot is next tick, so it is
   * in the snapshot and a restore that dropped it would put the missile
   * somewhere the server's re-simulation does not.
   */
  speed?: number;
  /** Speed the boost is climbing toward. */
  readonly maxSpeed?: number;
  /** Units per second squared, applied until `maxSpeed` is reached. */
  readonly acceleration?: number;
  /** Radians per second this shot may turn while chasing. */
  readonly maxTurn?: number;
  /** Milliseconds of flight left before it expires harmlessly. */
  remainingMs?: number;
}

/**
 * **Um míssil do Arsenal Ígneo, vivo na caça** (gate V28G.L).
 *
 * ## Porque é uma entidade própria e não um `ProjectileEntity`
 *
 * Porque contradiz três coisas que um projétil desta caça É. Um tiro comum tem
 * um `targetId` **readonly** e um `orphaned` que diz "o alvo morreu, segue em
 * frente e não fere mais ninguém" - a regra do `CLAUDE.md` de que um projétil
 * órfão nunca reaquire alvo. O míssil **troca de alvo**, o que é exatamente a
 * regra oposta, e escrevê-lo por cima do campo `readonly` de um projétil poria a
 * exceção a um `if` de distância de todos os tiros do jogo.
 *
 * As outras duas: ele nasce **ao lado** da salva, sem ocupar um projétil, e tem
 * um teto próprio derivado da cadência com uma política de despejo própria.
 *
 * A Bomba pôde viver na lista dos projéteis porque ela É um projétil,
 * transformado. Este não é nenhum.
 *
 * ## Os campos são exatamente os que o contrato manda sobreviver
 *
 * Posição, velocidade (a direção; a magnitude é constante), alvo, tempo restante
 * e identidade - mais o dano congelado, pela lei que congela toda a magnitude no
 * nascimento. Nada visual, e nada que a instantânea possa reconstruir.
 */
export interface MissileEntity {
  readonly id: number;
  x: number;
  y: number;
  /** A direção do voo, unitária. Curva até 240 graus por segundo. */
  dirX: number;
  dirY: number;
  /**
   * O que ele vai tirar, em MILLI, decidido quando a salva o lançou.
   *
   * Noventa por cento do dano-base autoritativo naquele instante, e nunca
   * recalculado - nove segundos são tempo de sobra para uma fusão, uma troca de
   * skin ou um ponto alocado, e um míssil que perguntasse ao perfil no impacto
   * mudaria de ideias em voo.
   */
  readonly damageMilli: bigint;
  /** A entropia congelada com a seleção. Decisões internas, nunca uma rolagem. */
  readonly entropy: number;
  /**
   * O inimigo que ele persegue AGORA, ou `null` no instante em que ficou sem um.
   *
   * Mutável, porque o retarget é a regra desta entidade: quando o alvo morre ou
   * desaparece, o míssil escolhe imediatamente outro inimigo vivo. Um `null` não
   * sobrevive a um tique - um míssil sem alvo explode.
   */
  targetId: number | null;
  /** Milissegundos que faltam. Chegar a zero é explodir sem ferir ninguém. */
  remainingMs: number;
}

/**
 * The combat pet's live state.
 *
 * It has a position, a facing and a firing accumulator, and that is all: a pet
 * has no health, cannot be hit, cannot block and cannot shield. Nothing in the
 * hunt can damage it, so there is nothing here to damage.
 */
/**
 * **Um stack de veneno na caça** (gate V28G.M).
 *
 * ## Porque não tem campo de atacante
 *
 * Porque uma caçada tem UMA nave, e a autoria é implícita - exatamente como
 * já é no evento `proc`, cuja nota diz: "aqui há uma nave só, portanto o
 * atacante é implícito". Um campo que só pode tomar um valor não preserva
 * informação nenhuma; o que ele faria era entrar no digest canónico de toda a
 * corrida para dizer o que já se sabia.
 *
 * O duelo tem seis cascos e por isso o guarda - ver `pvp/poison.ts`. É a mesma
 * assimetria que o evento `proc` já tem entre os dois modos, e a chave efetiva
 * dos dois é a mesma: `(atacante, alvo, tique)`, com a parte que este modo não
 * tem.
 *
 * ## O relógio é `simTimeMs`
 *
 * `appliedAt` está em MILISSEGUNDOS simulados, e não em tiques: o tique da
 * caça é de 16 ms e um segundo não é um número inteiro deles (62,5). Contar em
 * tiques obrigaria a arredondar, e uma parcela cairia a 0,992 ou a 1,008
 * segundos. Em milissegundos a pergunta é exata: a parcela `i` vence em
 * `appliedAt + i x 1000`, e ela é cobrada no primeiro tique em que o relógio lá
 * chegou ou passou.
 */
export interface PoisonStack {
  /** Identidade estável, única por corrida e monotónica. */
  readonly id: number;
  /** O inimigo envenenado. A morte dele leva todos os stacks dele. */
  readonly targetId: number;
  /** O total congelado na aplicação. Nunca relido de um perfil. */
  readonly totalDamageMilli: bigint;
  /** O instante da aplicação, em ms simulados. */
  readonly appliedAt: number;
  /** Quantas das seis parcelas já foram cobradas. */
  ticksDone: number;
}

/**
 * **Uma marca de sangramento na caça** (gate V28G.N).
 *
 * ## Porque não tem campo de autor
 *
 * Pela mesma razão exata que um stack de veneno não tem: uma caçada tem UMA
 * nave, a autoria é implícita, e um campo que só pode tomar um valor não
 * preserva informação nenhuma - o que ele faria era entrar no digest canónico de
 * toda a corrida para dizer o que já se sabia.
 *
 * O duelo tem seis cascos e por isso o guarda, e ali ele decide QUEM PODE
 * DETONAR - ver `pvp/bleeding.ts`. Aqui só existe um que possa, e é o jogador.
 *
 * ## Porque não tem relógio nenhum
 *
 * Porque uma marca não expira e não cobra parcela. `appliedAt` está em
 * MILISSEGUNDOS simulados como o do veneno - é o relógio deste modo - e serve a
 * ORDEM e o registo, nunca uma contagem decrescente. O desempate é o `id`, que é
 * monotónico por construção.
 */
/**
 * **O que uma detonação rebentou num alvo** (gate V28G.N).
 *
 * O resultado que o caminho autoritativo da caça devolve, e a única coisa que
 * uma apresentação lê sobre uma detonação. Três números, e nenhum deles é arte.
 *
 * ## Porque `applied` viaja
 *
 * Porque na caça este facto não chega à cena por outro caminho. A detonação
 * acontece dentro da transação do servidor, que re-simula a janela e **drena** a
 * fila de eventos - o `hit` que `applyHit` emitiu nunca sai de lá. O número
 * canónico de dano tem de vir na resposta, ou o jogador via a barra descer sem
 * um número, que é o contrário do que o `CLAUDE.md` exige.
 *
 * Já depois do clamp de overkill: é o dano REALMENTE aplicado, e não a soma
 * bruta das marcas.
 */
export interface HuntBleedDetonationTarget {
  readonly targetId: number;
  /** Quantas marcas rebentaram neste alvo. A intensidade do efeito. */
  readonly marks: number;
  /** O dano realmente aplicado, depois do clamp. O número que a cena imprime. */
  readonly applied: number;
}

export interface BleedingMark {
  /** Identidade estável, única por corrida e monotónica. */
  readonly id: number;
  /** O inimigo marcado. A morte dele leva todas as marcas dele. */
  readonly targetId: number;
  /** O dano congelado na aplicação. Nunca relido de um perfil. */
  readonly damageMilli: bigint;
  /** O instante da aplicação, em ms simulados. Ordem, nunca temporizador. */
  readonly appliedAt: number;
}

/**
 * **O relógio de UM par lâmina→alvo** (gate V28G.P).
 *
 * ## Porque um por PAR, e não um por lâmina nem um por alvo
 *
 * Porque as duas alternativas produzem jogos diferentes. Um relógio por lâmina
 * faria a segunda a tocar o mesmo mob não doer, o que transformaria quatro
 * serras em uma; um por alvo faria o mesmo. Um por par é o que deixa quatro
 * lâminas a atravessar um boss cobrarem quatro vezes - e o orçamento continua
 * fechado porque o dano de CADA contacto já foi dividido por quatro.
 *
 * ## O que ele guarda, e o que ele deliberadamente não guarda
 *
 * O instante do último contacto, em ms simulados. Não guarda um temporizador a
 * decrementar - ver `sawContactDue` - nem a magnitude, nem o nível, nem quantas
 * vezes já bateu. Tudo isso ou é derivado ou não influencia o futuro.
 */
export interface HuntSawContact {
  /** Qual das lâminas da órbita, de `0` a `count - 1`. */
  readonly bladeIndex: number;
  /** O inimigo tocado. A morte dele leva este relógio. */
  readonly targetId: number;
  /** O instante da última instância de dano DESTE par, em ms simulados. */
  lastContactMs: number;
}

/**
 * **Uma lâmina, como uma CENA a lê** (gate V28G.P).
 *
 * Derivada do estado autoritativo e nunca guardada: a posição é a fase mais a
 * órbita, e recalculá-la a cada leitura é o que garante que a cena desenha
 * exatamente onde a colisão aconteceu. Nada aqui é presença visual - não há cor,
 * não há textura, não há alpha e não há tamanho de sprite.
 */
export interface HuntSawBlade {
  /** Qual das lâminas, para a cena reconhecer a mesma entre dois quadros. */
  readonly index: number;
  readonly x: number;
  readonly y: number;
  /** O passo angular, em graus inteiros. A rotação sai daqui. */
  readonly step: number;
  /** O raio DESENHADO, que é também o raio de colisão. */
  readonly radius: number;
}

/**
 * **Uma invocação de carta, na caça** (gate V28H.M).
 *
 * O Drone de Ataque e o Leviatã Aéreo. O catálogo diz que as duas valem
 * `hunt, pvp`, e valem: o que é da CARTA - a regra de sobrevivência, os treze
 * segundos aprovados, a proporção da vida - vive em `cards/summons.ts` e é a
 * mesma nos dois modos. O que está aqui é o que é desta arena.
 *
 * ## Porque uma entidade, e não um campo na nave
 *
 * Porque ela tem posição, vida e relógio seus e é desenhada à parte. É a mesma
 * razão pela qual o pet é uma entidade: uma nave que carregasse o drone dentro
 * dela teria de responder "quem levou este tiro" com "eu ou a minha invocação".
 */
export interface HuntSummonEntity {
  readonly id: number;
  readonly kind: CardSummonKind;
  x: number;
  y: number;
  /** Corpo, em radianos. As bocas apontam ao longo dele - como no pet. */
  facing: number;
  /**
   * A vida desta entidade, em MILLI e como o resto da caça a conta.
   *
   * Uma fracção da vida máxima da nave que a criou, congelada no nascimento: um
   * drone com vida fixa seria descartável no fim da progressão e imortal no
   * princípio dela.
   */
  healthMilli: number;
  readonly maxHealthMilli: number;
  /** Milissegundos de vida por gastar. Ver `cards/summons.ts`. */
  lifeMs: number;
  /** O relógio de disparo DESTA entidade. Próprio, e nunca o da nave. */
  attackAccumulatorMs: number;
  /**
   * A nave está lá dentro? **Só o Leviatã.**
   *
   * Um booleano e não um id, porque a caça tem uma nave só: perguntar "qual"
   * seria inventar uma pergunta que este modo não tem.
   */
  boarded: boolean;
  /** Milissegundos desde a ENTRADA, a contar para trás. **APROVADO: 13 s.** */
  boardedRemainingMs: number;
}

export interface PetEntity {
  /** Definition id, so the renderer knows which model or art to draw. */
  readonly definitionId: string;
  /** 1..3 - the evolution, which is also the vault instance's level. */
  readonly evolution: number;
  x: number;
  y: number;
  /** Body heading in radians; the muzzles point along it. */
  facing: number;
  /** Phase of the figure-of-eight drift, seconds. */
  driftSeconds: number;
  /** Milliseconds banked toward the next volley. */
  attackAccumulatorMs: number;
  /**
   * The enemy a BEAM is locked to, or null when the beam is off.
   *
   * Beam pets only - the Void Butterfly, and nothing else in the game. It is
   * authoritative state rather than a renderer's guess: the lock decides who
   * takes the next instance of damage, so the server's re-simulation and the
   * client must agree on it exactly, and a reload must resume the same lock
   * rather than choosing again.
   */
  beamTargetId: number | null;
  /** Milliseconds banked toward the next damage INSTANCE of a beam. */
  beamAccumulatorMs: number;
  /**
   * Milliseconds of enforced darkness before the beam may lock again.
   *
   * Set when a target dies or leaves. Without it the beam re-locks on the same
   * tick the old enemy vanished, and a wave being cleared reads as a strobe of
   * one-frame beams rather than as a weapon.
   */
  beamReacquireMs: number;
  /** Cumulative damage this pet has dealt, for the Overview. */
  damageDealt: number;
  /**
   * Permanent training, frozen with the session.
   *
   * On the ENTITY rather than looked up per tick: a checkpoint re-simulates a
   * window with the pet as it WAS, and a pet fed halfway through did not deal
   * the trained damage for the whole of it.
   */
  damageFoodCount: number;
  attackSpeedFoodCount: number;
}

export type HuntEvent =
  | {
      /**
       * **Um proc CONFIRMADO: o golpe adicional foi mesmo aplicado.**
       *
       * ## Porque o `hit` comum não chegava
       *
       * Porque um corte e um tiro produzem o mesmo `hit`, e uma apresentação que
       * os quisesse distinguir teria de os adivinhar - pelo dano, pela ordem, ou
       * por saber que joias a nave tem. Adivinhar pelo dano é errado no dia em
       * que dois números coincidem; ler o perfil é a decoração por passiva que
       * `jewel-presentation.test.ts` proíbe. Este evento é a autoridade a dizer
       * o facto, e é a única porta legítima.
       *
       * ## Só quando ACONTECEU
       *
       * Emitido depois de o dano ter sido aplicado, e nunca antes. Um proc
       * selecionado que erra, um alvo já morto pelo tiro comum e um corte de
       * magnitude zero não produzem evento nenhum - não houve facto.
       *
       * ## O que ele deliberadamente não carrega
       *
       * Magnitude, nível, cópias, identificador de inventário, cor, textura ou
       * escala. O dano viaja no `hit` que já existe, e a arte é do renderer. O
       * `procId` é o EFEITO (`cyclone_cut`) e nunca a joia: uma cena que
       * soubesse o nome de uma joia seria uma cena a decorar um perfil.
       *
       * ## Deduplicação
       *
       * `projectileId` é estável e único por corrida, e um projétil produz no
       * máximo um corte. Uma cena que receba o mesmo par duas vezes - por
       * replay de quadro - desenha uma vez.
       */
      readonly type: 'proc';
      readonly procId: string;
      readonly targetId: number;
      readonly projectileId: number;
      /**
       * **O tique da SIMULAÇÃO em que este evento nasceu** (gate V28G.I).
       *
       * A terceira metade da chave de consolidação da apresentação:
       * `(atacante, alvo, tique)`. Dez acertos no mesmo alvo no mesmo tique são
       * um sinal; dois tiques são dois sinais.
       *
       * Aqui há uma nave só, portanto o atacante é implícito e a chave efetiva
       * é `(alvo, tique)` - a MESMA chave que o duelo usa, com a parte que este
       * modo não tem. Uma cena partilhada não precisa de saber em que modo está.
       *
       * Apresentação, e só: não entra em dano, cura, resto, RNG, digest,
       * economia ou impressão digital. E é normalizado de fora do digest com o
       * resto da fila de eventos.
       */
      readonly tick: number;
      /**
       * **A vida REALMENTE acrescentada à nave, em milli** (gate V28G.I).
       *
       * O valor depois do clamp da vida máxima, em milli inteiro para que a
       * apresentação possa somar todas as curas do mesmo tique sem acumular erro
       * de vírgula flutuante - a divisão acontece uma vez, ao formatar.
       *
       * O cliente não pode recalculá-la: não tem perfil, nível nem percentagem,
       * e a diferença observada na barra move-se também por dano e por
       * regeneração. Ausente para um efeito que não cura.
       */
      readonly healedMilli?: number;
      readonly x: number;
      readonly y: number;
    }
  | {
      /**
       * **Uma CURA DE SUPORTE que a simulação confirmou** (gate V28G.U).
       *
       * ## Porque um evento próprio, e não o canal do `proc`
       *
       * Porque na caça o `proc` exige duas coisas que esta cura não tem: um
       * `targetId`, que ali significa "o inimigo em quem o efeito aconteceu", e
       * um `projectileId`, que ali significa "o tiro que o trouxe". Uma cura do
       * Coração Vital não acontece num inimigo e não é trazida por tiro nenhum -
       * ela acontece no DISPARO da salva, e o §"o proc não depende de acertar um
       * inimigo" é exatamente isso.
       *
       * Preencher os dois campos com o alvo da salva e com o primeiro projétil
       * dela seria escrever duas coisas falsas para caber num canal, e a segunda
       * delas colidiria com a chave de deduplicação de um corte que aquele mesmo
       * projétil ainda pode causar.
       *
       * No duelo a resposta é outra e está escrita em `damage/origin.ts`: lá o
       * canal do proc DIZ a verdade, porque o autor e o curado são os dois
       * participantes e a salva é um projétil.
       *
       * ## O que ele carrega
       *
       * A vida REALMENTE acrescentada, em milli inteiro, e onde a nave estava.
       * Não carrega nível, cópias, joia, chance nem parâmetro de arte - e nunca
       * é emitido com `healedMilli` zero: uma cura que não aconteceu não é um
       * facto.
       */
      readonly type: 'vital-heal';
      /** O tique da SIMULAÇÃO em que aconteceu. Nunca um relógio de parede. */
      readonly tick: number;
      /** A vida acrescentada, em milli inteiro. Sempre maior que zero. */
      readonly healedMilli: number;
      /** A nave, em unidades de mundo: onde o brilho corre. */
      readonly x: number;
      readonly y: number;
    }
  | {
      readonly type: 'volley';
      readonly projectileIds: readonly number[];
      /** One entry per firing cannon: the muzzle its shots left from. */
      readonly origins: readonly { readonly x: number; readonly y: number }[];
      readonly heading: number;
    }
  | {
      /**
       * The pet fired. Separate from `volley` so the renderer can give it its
       * own muzzle flash and recoil without touching the ship's.
       */
      readonly type: 'pet-volley';
      readonly projectileIds: readonly number[];
      /** One entry per machine gun that fired. */
      readonly origins: readonly { readonly x: number; readonly y: number }[];
      readonly heading: number;
    }
  | {
      readonly type: 'hit';
      readonly targetId: number;
      readonly amount: number;
      readonly x: number;
      readonly y: number;
      /**
       * Whether the pet landed it. PRESENTATION ONLY.
       *
       * The renderer paints the impact spark in the pet's energy rather than in
       * the cannon's colour, which is the only thing that reads it. The damage
       * itself already went through the one shared path above - this flag
       * cannot change what was applied, and events are normalised out of the
       * checkpoint digest, so it cannot change that either.
       */
      readonly fromPet: boolean;
      /**
       * The shot that landed it, when one exists. PRESENTATION ONLY.
       *
       * The renderer freezes a shot's colour at launch, and without the id it
       * could not find that colour again - so the impact spark would be drawn
       * in whatever energy the pet is wearing NOW, and changing colour while a
       * missile was in the air gave it a mismatched spark for a frame.
       *
       * Absent for a hit no projectile caused: a BEAM is connected to the pet
       * rather than launched from it, so it has no frozen colour to look up and
       * follows the live energy by design.
       */
      readonly projectileId?: number;
    }
  | {
      /**
       * An explosive pet projectile detonated. PRESENTATION ONLY.
       *
       * Every enemy the blast damaged already produced its own `hit` event with
       * its own number - this one exists so the renderer can draw the wave at
       * the right place and the right size, and it carries the radius the
       * SIMULATION used rather than one the client chose.
       */
      readonly type: 'explosion';
      readonly x: number;
      readonly y: number;
      readonly radius: number;
      /** How many enemies the blast touched, direct hit included. */
      readonly hits: number;
      /**
       * The shot that went off.
       *
       * Presentation only, and the reason it exists: the renderer freezes a
       * shot's colour when it launches, and without the id it could not find
       * that colour again - so an explosion would be painted in whatever
       * energy the pet is wearing NOW. Swapping colour mid-flight would then
       * give a violet missile a green blast.
       */
      readonly projectileId: number;
      /**
       * O EFEITO de joia que produziu esta explosão, se foi uma (gate V28G.J).
       *
       * `atomic_blast` para o Núcleo Atômico, e **ausente** para a bola do pet -
       * que continua a ser exatamente o que sempre foi. A distinção existe
       * porque as duas não se desenham da mesma maneira, e porque a cena não tem
       * outra forma honesta de as separar: adivinhar pelo raio seria decidir a
       * arte por um número de equilíbrio.
       *
       * É o EFEITO e nunca a joia. Uma cena que soubesse `jewel_atomic_bomb`
       * seria uma cena a decorar um perfil, que é o que
       * `jewel-presentation.test.ts` proíbe - a mesma razão pela qual o corte se
       * chama `cyclone_cut`.
       */
      readonly effectId?: string;
    }
  | {
      readonly type: 'kill';
      readonly targetId: number;
      readonly wasBoss: boolean;
      readonly x: number;
      readonly y: number;
    }
  | { readonly type: 'leak'; readonly mobId: number; readonly damage: number }
  | { readonly type: 'level-up'; readonly newLevel: string }
  | { readonly type: 'boss-spawned'; readonly bossId: number; readonly scale: number }
  | { readonly type: 'boss-defeated'; readonly bossId: number }
  | {
      /**
       * **Uma carta ACTIVOU** (gate V28H.H). Apresentação, e só.
       *
       * A cena precisa de saber o INSTANTE para tocar a montagem; o resto - se
       * está activa, quanto falta, onde estão os canhões - é derivável da
       * instantânea a cada quadro, e um evento por quadro seria protocolo para
       * decoração.
       *
       * Normalizado para fora do digest com o resto da fila, como todos os
       * eventos: ele não decide nada.
       */
      readonly type: 'card-activated';
      readonly slot: string;
      readonly definitionId: string;
      readonly durationMs: number;
      readonly tick: number;
    }
  | {
      /**
       * **Uma salva de armamento temporário** (gate V28H.H).
       *
       * O canal canónico das salvas, com a carta nomeada: a cena desenha o
       * clarão nas bocas certas sem ter de adivinhar de onde os dois projéteis
       * saíram. Separado de `volley` porque as bocas são outras e o recuo do
       * casco não deve acontecer.
       */
      readonly type: 'card-volley';
      readonly definitionId: string;
      readonly projectileIds: readonly number[];
      readonly origins: readonly { readonly x: number; readonly y: number }[];
      readonly heading: number;
    }
  | { readonly type: 'ship-death'; readonly cause: 'boss-breach' | 'mob-leaks' };

export interface HuntConfig {
  readonly mapIndex: number;
  readonly seed: SimulationSeed;
  readonly shipIndex: number;
  readonly shipLevel: number;
  readonly cannonRarity: CannonRarity;
  readonly cannonLevel: number;
  /**
   * Corações Primordiais incorporados na NAVE EQUIPADA, congelados para esta
   * corrida (gate V28F.B).
   *
   * ## Porque é da configuração e não dos passivos
   *
   * Porque não é um passivo da conta. Um coração é gasto numa nave, e o bónus é
   * uma propriedade DAQUELA nave - trocar de nave troca o bónus, vender a nave
   * leva-o com ela. Pô-lo em `artifactEffects` diria o contrário: que pertence
   * a quem joga, não ao que ele está a pilotar.
   *
   * É por isso que ele vive ao lado de `shipIndex` e `shipLevel`, que são as
   * outras duas coisas que descrevem a nave desta corrida.
   *
   * ## Congelado, como tudo o resto
   *
   * Resolvido pelo servidor a partir de `ship_instances.primordial_hearts` e
   * congelado na sessão. O domínio não sabe o que é um cofre e não vai buscar
   * nada: um checkpoint re-simula a janela com os corações que estavam na nave
   * durante ela, e um coração incorporado depois nunca paga retroativamente.
   *
   * Ausente para toda a sessão criada antes disto existir, e a ausência é
   * exata: essas corridas foram jogadas sem corações nenhuns.
   */
  readonly primordialHeartCount?: bigint;
  /**
   * As joias encaixadas na skin equipada desta corrida (gate V28G.A/B).
   *
   * ## Congelado, como tudo o resto
   *
   * Resolvido pelo servidor a partir da ocupacao real e congelado no loadout da
   * sessao. O dominio nao sabe o que e um cofre e nao vai buscar nada: um
   * checkpoint re-simula a janela com as joias que estavam na skin durante ela,
   * e uma joia encaixada depois nunca paga retroativamente.
   *
   * Ausente para toda a sessao criada antes disto existir, e a ausencia e
   * exata: essas corridas foram jogadas sem joias nenhumas.
   *
   * O que ele FAZ é uma coisa só: a Chama da Destruição soma `30 bps x levelSum`
   * ao tiro normal, na mesma parcela onde os Corações já entram. As outras treze
   * estão inertes, e `jewel-effects-absent.test.ts` é quem o afirma.
   */
  readonly jewels?: JewelSocketProfile;
  /**
   * As três cartas que esta corrida CONGELOU (gate V28H.H).
   *
   * Opcional, e a ausência é o estado normal: uma conta sem cartas equipadas
   * corre exactamente como corria. Equipar durante a caçada não mexe aqui - a
   * escolha nova vale na próxima sessão.
   */
  readonly cards?: CardLoadout;
  /**
   * The account's artifact passives, already resolved and FROZEN for this run.
   *
   * Never resolved from an inventory here: the domain is platform-neutral and
   * knows nothing about a vault. The server resolves them, freezes the summary
   * into the session, and hands the totals down - so a checkpoint re-simulates
   * a window with the artifacts that were live during it, and a copy obtained
   * afterwards can never pay retroactively.
   */
  readonly artifactEffects?: ScaledArtifactEffects;
  /**
   * The five equipped components' totals, resolved and FROZEN for this run.
   *
   * Same rule as the artifacts, and for the same reason: the domain knows
   * nothing about a vault, so the server resolves the five slots, freezes the
   * summary into the session and hands the totals down. A checkpoint therefore
   * re-simulates a window with the components that were equipped during it, and
   * a component equipped or upgraded afterwards can never pay retroactively.
   *
   * Absent for every session created before equipment existed, and for every
   * account with all five slots empty - which is the same answer, and the right
   * one: those runs were played with nothing equipped.
   */
  readonly equipmentTotals?: EquipmentTotals;
  /**
   * The ACTIVE pet this run fights with, resolved and frozen by the server.
   *
   * Absent when the account has no pet active, which is the normal state. Like
   * the artifacts, it is never resolved here: the domain knows nothing about a
   * vault, so the server reads the pet slot, freezes it into the session and
   * hands down the identity - which is what makes a checkpoint re-simulate a
   * window with the pet that was actually flying during it.
   */
  readonly pet?: {
    readonly definitionId: string;
    readonly evolution: number;
    /** Permanent training, frozen with the session. */
    readonly damageFoodCount?: number;
    readonly attackSpeedFoodCount?: number;
  };
  /**
   * DEV/DEBUG ONLY - overrides the production 3..16 min boss roll with a fixed
   * delay. Never set outside development tooling; the production rule stays
   * `rollBossDelaySeconds` untouched.
   */
  /**
   * The boss encounter this run is bound to, drawn and persisted by the server.
   *
   * Optional, and its absence is meaningful rather than sloppy: a run that
   * predates encounters, and every pure-domain test, falls back to the old
   * behaviour - a delay from the simulation stream and tier 1. The server
   * always supplies one for a real session, which is what makes the tier
   * unshoppable: it is drawn once, with cryptographic entropy, and written
   * down before it can affect anything.
   */
  /**
   * True when the server draws and persists this run's boss encounters.
   *
   * The flag rather than the presence of `bossEncounter`, because a managed
   * run is legitimately UNBOUND for a moment: a defeated boss leaves it with no
   * encounter until the next settlement boundary draws one, and that state must
   * not be mistaken for "this is an old run, re-arm from your own seed".
   */
  readonly serverManagedBoss?: boolean;
  readonly bossEncounter?: {
    readonly encounterId: string;
    readonly ordinal: number;
    readonly tier: BossTier;
    /** Whole milliseconds of ACTIVE hunting before the boss arrives. */
    readonly delayMs: number;
    /** True when adopting an encounter whose boss is already on the field. */
    readonly activated: boolean;
    /** True when the same boss is waiting out a recovery window. */
    readonly retrying?: boolean;
  };
  readonly debugBossDelaySeconds?: number;
  /**
   * DEV/DEBUG ONLY - multiplies attack speed, so a test can check that extreme
   * cadence still produces discrete projectiles. Logical hits keep being counted
   * at the boosted frequency, so DPS bookkeeping stays exact. It changes the
   * rate and nothing else - never how a shot is drawn. Never available to
   * players.
   */
  readonly devAttackSpeedBoost?: number;
  /**
   * Authoritative progression the run starts from.
   *
   * Supplied by the server at bootstrap so the HUD shows the real level, XP,
   * Gold and allocated points instead of restarting at zero on every reload.
   * Omitted only in tests and in the pure-domain unit suites.
   */
  readonly initialProgression?: HuntSnapshotProgression;
}

export interface DeathStats {
  readonly survivedMs: number;
  readonly kills: number;
  readonly damageDealt: number;
  readonly goldEarned: number;
}

export interface MetricWindow {
  /** Sum of amounts inside the rolling 60 s window. */
  readonly windowTotal: number;
  /** ms since the first event of the session, or null before any event. */
  readonly elapsedMs: number | null;
  /** True once a full window has elapsed since the first event. */
  readonly windowFull: boolean;
  readonly sessionTotal: number;
}

interface TimedAmount {
  t: number;
  amount: number;
}

function pruneWindow(events: TimedAmount[], now: number, windowMs: number): void {
  let firstKept = 0;
  while (firstKept < events.length && (events[firstKept]?.t ?? 0) <= now - windowMs) {
    firstKept += 1;
  }
  if (firstKept > 0) {
    events.splice(0, firstKept);
  }
}

/**
 * The events still inside the window, copied - never the live array.
 *
 * A snapshot has to be a function of the simulation's STATE and its tick, and
 * copying `this.damageEvents` straight out was not: pruning happens both when
 * an event is pushed AND when `damageMetric()` / `goldMetric()` are read, and
 * the two sides of a checkpoint read them very differently. The browser calls
 * both every frame to paint the metrics in the HUD, so its arrays are always
 * trimmed to the current tick. A server re-simulating the same window never
 * reads a metric at all, so its arrays still carried every event that had aged
 * out since the last kill.
 *
 * `metrics` is inside the checkpoint digest, so at any tick where an event had
 * fallen out of the window without a push to trim it, two runs that had played
 * IDENTICALLY hashed differently. The server then answered an honest
 * checkpoint with a correction: a full authoritative snapshot on the wire and a
 * restore of a simulation that was already right - per checkpoint, per player.
 *
 * Trimming the copy rather than the live array keeps `snapshot()` a pure read,
 * which matters because the client takes one far more often than it
 * checkpoints. Nothing is lost either way: an event outside the window
 * contributes zero to the metric that reads it.
 */
function eventsInWindow(
  events: readonly TimedAmount[],
  now: number,
  windowMs: number,
): TimedAmount[] {
  const oldest = now - windowMs;
  const kept: TimedAmount[] = [];
  for (const event of events) {
    if (event.t > oldest) {
      kept.push({ ...event });
    }
  }
  return kept;
}

/**
 * **O que as cartas desenham, num tipo com nome** (gate V28H.N).
 *
 * Nomeado e exportado porque a cena o consome inteiro: um tipo anónimo dentro
 * da assinatura obrigaria o renderer a redeclarar a mesma forma, e uma segunda
 * declaração é uma segunda oportunidade de divergir.
 */
export interface HuntCardVisuals {
  /**
   * **O círculo mágico do Turbilhão** - onde está, e até onde apanha.
   *
   * ## Porque ele existe mesmo com a carta desligada
   *
   * Decisão do dono (gate V28H.O): o círculo é **passivo**. Basta a carta estar
   * equipada para ele aparecer e ficar a girar; a activação acrescenta a serra
   * lá dentro. Um círculo que só aparecesse ao ligar não dizia a ninguém que
   * aquela nave tem a carta - e é ele que comunica a área que vai ferir.
   *
   * `active` é o que separa as duas coisas: falso é a moldura a girar sozinha,
   * verdadeiro é a serra a cortar. E o raio é o MESMO nos dois casos, porque a
   * promessa que a moldura faz tem de ser a que a serra cumpre.
   */
  readonly circle: {
    readonly x: number;
    readonly y: number;
    readonly radius: number;
    readonly active: boolean;
  } | null;
  /** O feixe da Lança Solar, a partir da BOCA da nave. */
  readonly beam: {
    readonly x: number;
    readonly y: number;
    readonly angle: number;
    readonly length: number;
    readonly width: number;
    /** A força com que ele está a arder, em bps. Graduou a espessura. */
    readonly strengthBps: number;
  } | null;
  /** A carga em curso: quanto já foi cumprido, de 0 a 1. */
  readonly charge: {
    readonly x: number;
    readonly y: number;
    readonly progress: number;
    readonly locksMovement: boolean;
    /**
     * **O que esta carga vai produzir** (gate V28H.V).
     *
     * `beam` é a Lança e `sphere` é a Esfera, e a distinção passou a ser
     * necessária no dia em que as DUAS passaram a prender o movimento: a cena
     * separava-as por `locksMovement`, e a partir daí desenhava a corona da
     * Lança por cima de uma esfera. A pergunta certa nunca foi "prende?", era
     * "o quê?".
     */
    readonly kind: 'beam' | 'sphere';
  } | null;
  /**
   * **A esfera a CRESCER à frente da boca** (gate V28H.V), ou `null`.
   *
   * Nove segundos de carga não podem ser nove segundos sem nada no ecrã, e a
   * bola que cresce é a própria mecânica a ser vista: ela é o que sai quando o
   * dedo sair.
   *
   * O raio é o da EXPLOSÃO que ela vai abrir - o mesmo número que decide quem
   * ela apanha - e quem desenha tira dele o corpo. Devolver aqui um tamanho de
   * arte seria a simulação a decidir desenho.
   */
  readonly sphere: {
    readonly x: number;
    readonly y: number;
    readonly radius: number;
  } | null;
  /**
   * **O Bastião a correr** (gate V28H.T), ou `null`.
   *
   * Um sinal que TEM de ser persistente: a carta dura quatro segundos e o que
   * ela faz - os tiros atravessarem - não se vê acontecer. Sem uma marca no
   * casco, um jogador não tem como saber que ela pegou nem quando acabou.
   *
   * `progress` vai de um a zero: é a fracção que RESTA, e é ela que faz o anel
   * poder mostrar quanto falta em vez de só piscar.
   */
  readonly bastion: {
    readonly x: number;
    readonly y: number;
    readonly radius: number;
    readonly progress: number;
  } | null;
}

/**
 * **Uma bomba de carta a caminho do alvo** (gate V28H.T).
 *
 * ## Porque ela passou a existir
 *
 * A Esfera Ígnea e o Catolé rebentavam no INSTANTE da ordem: nada viajava, e a
 * única coisa que a explosão produzia era um evento. Os eventos não entram na
 * instantânea - e na caça o cliente **adopta** a instantânea do servidor em vez
 * de re-executar a ordem. O evento nascia no servidor, era drenado lá, e o ecrã
 * do jogador nunca soube que alguma coisa tinha acontecido.
 *
 * Uma entidade resolve as duas metades de uma vez: ela é ESTADO, portanto viaja
 * na instantânea; e é o cliente que a faz chegar ao alvo e detonar, portanto o
 * evento nasce onde ele é desenhado. É exactamente como um tiro normal já
 * funciona nesta arena.
 *
 * E é o que as descrições aprovadas pedem: "uma grande esfera de fogo" e "uma
 * bomba colossal teleguiada" são coisas que se vêem A CAMINHO.
 */
export interface HuntCardBlast {
  readonly id: number;
  /** Qual das duas: muda a arte, o raio e o tamanho desenhado. */
  readonly kind: 'fireball' | 'catole';
  x: number;
  y: number;
  /** Quem ela persegue. Ela é TELEGUIADA: segue o alvo, e não o sítio dele. */
  readonly targetId: number;
  /** Unidades de mundo por segundo. */
  readonly speed: number;
  /** O raio da explosão, resolvido no lançamento e nunca depois. */
  readonly radius: number;
  /** O dano de cada apanhado, em milli, congelado no lançamento. */
  readonly damageMilli: bigint;
  /** Quanto tempo ela já voa, para a esfera CRESCER a caminho. */
  ageMs: number;
}

export class HuntSimulation {
  /**
   * The loadout and map this run fights with.
   *
   * Behind a getter because equipping replaces it in flight (`applyLoadout`)
   * while every entity, timer and RNG stream stays exactly where it is. It is
   * still read-only to callers: nothing outside this class may assign it.
   */
  private currentConfig: HuntConfig;

  get config(): HuntConfig {
    return this.currentConfig;
  }

  /** The frozen artifact totals this run fights with. */
  private get passives(): PassiveAttributes {
    return { effects: this.currentConfig.artifactEffects ?? NO_ARTIFACT_EFFECTS };
  }

  /** The frozen equipment totals this run fights with. */
  private get equipment(): EquipmentTotals {
    return this.currentConfig.equipmentTotals ?? NO_EQUIPMENT_TOTALS;
  }

  /**
   * **As quatro fontes da vida máxima desta conta**, reunidas num sítio só.
   *
   * As MESMAS que a autoridade reúne para emitir um bilhete de duelo, e a razão
   * de este acessor existir é essa: a caça e o PvP têm de produzir o mesmo
   * número, e a única maneira de o garantir é as duas pontas passarem as mesmas
   * parcelas à mesma função (gate V28G.S).
   *
   * Tudo aqui é o que a SESSÃO congelou - os pontos da progressão dela, os
   * artefactos dela, o equipamento dela, as joias dela - porque é isso que esta
   * corrida está a jogar. A fronteira temporal do V28G.A não se move.
   */
  private hullSources(): AccountMaxHealthSources {
    return {
      healthPoints: this.progression.healthPoints,
      passives: this.passives,
      equipment: this.equipment,
      jewels: this.config.jewels ?? EMPTY_JEWEL_SOCKET_PROFILE,
    };
  }

  // Not `readonly`: restoring a snapshot replaces the streams wholesale so the
  // continued run draws exactly the values the original would have drawn.
  private spawnRng: SimulationRng;
  private bossTimerRng: SimulationRng;
  /** Which enemies the pet's cannons pick. Its own stream, in the snapshot. */
  /**
   * **As oito ranhuras de efeito das cartas** (gate V28H.L).
   *
   * As mesmas da sala do duelo, do mesmo módulo neutro: uma Lentidão reduz
   * movimento nos dois sítios, e um Bastião torna inalvejável nos dois. Uma
   * segunda implementação seria uma segunda regra a divergir na primeira
   * correcção.
   */
  private cardEffects: CardEffectState;
  /**
   * As invocações a existir no campo (gate V28H.M).
   *
   * Ao lado dos mobs e do pet, e nunca dentro da nave: elas têm posição, vida e
   * relógio seus, e são desenhadas à parte.
   */
  private summons: HuntSummonEntity[] = [];
  /** As bombas de carta em voo (gate V28H.T). Estado, e não decoração. */
  private cardBlasts: HuntCardBlast[] = [];
  private petTargetRng: SimulationRng;
  /**
   * **A loteria de procs de saída desta corrida (gate V28G.G).**
   *
   * ## Porque um fluxo SEPARADO, e versionado
   *
   * Porque partilhá-lo com o spawn, o temporizador do boss ou a escolha do pet
   * faria cada disparo deslocar a sequência deles: encaixar o Ciclone mudaria
   * quando o próximo inimigo chega e que alvo o dragão escolhe, e a diferença
   * não seria da joia - seria de quantas balas foram disparadas antes.
   * `jewel-proc-hunt.test.ts` compara os três fluxos com e sem a joia.
   *
   * O rótulo é `hunt-jewel-outgoing-proc-v1` e não o do duelo. Um fluxo por
   * MODO, nunca um por joia: há uma loteria única por disparo, e vários
   * geradores atrás de uma decisão só não têm ordem correta de consulta.
   *
   * ## Porque não é `readonly`
   *
   * Pela mesma razão dos outros três: restaurar uma instantânea substitui-o por
   * inteiro, para que a corrida continuada sorteie exatamente o que a original
   * teria sorteado.
   */
  private jewelProcRng: SimulationRng;
  /**
   * O fluxo da rolagem POR SALVA do Núcleo Atômico (gate V28G.J).
   *
   * Um fluxo à parte, e a razão é a mesma que separou o dos procs do das runas:
   * uma salva consome UMA rolagem daqui e vinte e oito do outro, portanto
   * partilhá-los faria cada salva deslocar toda a sequência de procs. Uma caça
   * com a mesma semente deixaria de ser a mesma caça por causa de uma joia que a
   * nave nem tem.
   */
  private volleyBombRng: SimulationRng;
  /**
   * **O fluxo do Coração Vital** (gate V28G.U).
   *
   * Sétimo fluxo de joia, e separado dos outros seis pela mesma razão que eles
   * são separados uns dos outros: uma salva consome UMA rolagem daqui, e
   * partilhar o fluxo da Bomba faria cada salva deslocar a sequência dela e a do
   * Míssil. O contrato do gate diz-o por escrito - a cura *«não pode deslocar
   * RNG de runas, ofensivos, Bomba, Míssil, Veneno, Sangramento ou qualquer
   * outro sistema»* - e um rótulo próprio é a maneira de o cumprir por
   * construção em vez de por atenção.
   *
   * Consumido SEMPRE que uma salva comum sai, com joia ou sem ela: consumir
   * condicionalmente faria a sequência depender do equipamento.
   */
  private vitalHeartRng: SimulationRng;
  /**
   * DEV ONLY: a próxima salva cura. Ver {@link devForceNextVitalHeal}.
   *
   * **Fora da instantânea**, pela mesma razão exata que as bandeiras da bomba, do
   * míssil e do veneno estão.
   */
  private forceNextVitalHeal = false;
  /**
   * DEV ONLY: a próxima salva traz uma bomba. Ver {@link devForceNextVolleyBomb}.
   *
   * **Fora da instantânea, e deliberadamente.** Uma bandeira de ferramenta não
   * decide o futuro de uma corrida no sentido em que `HuntSnapshot` usa a
   * palavra: ela não muda um número, não muda o estado do gerador, e a próxima
   * salva de uma corrida restaurada é decidida pela rolagem real. Persistir uma
   * afordância de depuração seria pô-la a atravessar para o servidor.
   */
  private forceNextVolleyBomb = false;
  /**
   * O fluxo de SELEÇÃO DE ALVO do Arsenal Ígneo (gate V28G.L).
   *
   * Terceiro fluxo de joia, e separado dos outros dois pela mesma razão que eles
   * são separados um do outro: partilhá-lo faria uma escolha de alvo deslocar a
   * sequência de procs ou a da rolagem por salva - e, mais grave nesta caça,
   * deslocaria os spawns, o temporizador do boss e o alvo do pet se algum dia
   * fossem o mesmo gerador.
   *
   * É consumido CONDICIONALMENTE - só quando um míssil nasce ou perde o alvo - e
   * isso é seguro porque mais ninguém o lê: não há nada a jusante para deslocar,
   * e a instantânea guarda o estado do gerador.
   */
  private missileTargetRng: SimulationRng;
  /**
   * DEV ONLY: a próxima salva lança um míssil.
   * Ver {@link devForceNextVolleyMissile}.
   *
   * **Fora da instantânea**, pela mesma razão exata que a bandeira da bomba está.
   */
  private forceNextVolleyMissile = false;
  /**
   * **O fluxo do Orbe da Víbora** (gate V28G.M).
   *
   * Quarto fluxo de joia, separado dos outros três pela mesma razão que eles
   * são separados uns dos outros - e a UNIDADE aqui é outra: a rolagem do
   * veneno acontece uma vez por ACERTO DIRETO confirmado, e a loteria ofensiva
   * uma vez por projétil NASCIDO. Uma salva de vinte e oito tiros que acerta em
   * três consome vinte e oito de um e três do outro; partilhá-los faria cada
   * acerto deslocar a sequência de procs de todos os tiros seguintes.
   *
   * É consumido CONDICIONALMENTE - só num acerto direto num inimigo que lhe
   * sobreviveu - e isso é seguro porque mais ninguém o lê.
   */
  private poisonRng: SimulationRng;
  /**
   * DEV ONLY: o próximo acerto direto envenena.
   * Ver {@link devForceNextPoison}.
   *
   * **Fora da instantânea**, pela mesma razão exata que as bandeiras da bomba e
   * do míssil estão.
   */
  private forceNextPoison = false;
  /**
   * **Os stacks de veneno ativos, em ordem de criação** (gate V28G.M).
   *
   * Uma lista da CORRIDA e não um campo do mob, e a razão é a mesma pela qual
   * os mísseis são uma lista: um stack não é um inimigo, tem o seu próprio
   * relógio, e existem quantos forem. Pendurá-lo na entidade obrigaria cada mob
   * que a caçada cria - dezenas por minuto - a carregar um array vazio, e
   * mudaria o digest canónico de toda a corrida para descrever a coisa mais
   * rara que existe.
   *
   * A ORDEM é a de criação, e ela é o desempate: dois stacks que vencem no
   * mesmo tique cobram pela ordem em que nasceram, em qualquer máquina.
   */
  private poison: PoisonStack[] = [];
  /**
   * A identidade do próximo stack.
   *
   * Um contador próprio e não `nextEntityId`: um stack não é uma entidade da
   * arena - não tem posição, não colide e não é desenhado como um objeto do
   * mundo - e partilhar o contador faria cada aplicação deslocar os ids de todos
   * os mobs seguintes, o que muda o digest de uma corrida por uma razão que não
   * é a mecânica.
   */
  private nextPoisonId = 1;
  /**
   * O fluxo do SANGRAMENTO (gate V28G.N).
   *
   * Quinto fluxo de joia, separado dos outros quatro pela razão de sempre - e
   * separado do do veneno em particular porque as duas joias podem produzir no
   * MESMO acerto direto: uma marca e um stack não competem, e duas coisas que
   * podem acontecer ao mesmo tempo precisam de dois sorteios.
   *
   * É consumido CONDICIONALMENTE - só num acerto direto de CANHÃO num inimigo
   * que lhe sobreviveu - e isso é seguro porque mais ninguém o lê. A tabela
   * fechada é perguntada ANTES da rolagem, para que uma bala de pet não o
   * consuma: uma corrida com pet tem de ter a mesma sequência que uma sem.
   */
  private bleedingRng: SimulationRng;
  /**
   * DEV ONLY: o próximo acerto direto marca.
   * Ver {@link devForceNextBleeding}.
   *
   * **Fora da instantânea**, pela mesma razão exata que as bandeiras da bomba,
   * do míssil e do veneno estão.
   */
  private forceNextBleeding = false;
  /**
   * **As marcas de sangramento ativas, em ordem de criação** (gate V28G.N).
   *
   * Uma lista da CORRIDA e não um campo do mob, pela mesma razão que os stacks
   * de veneno são uma lista: pendurá-las na entidade obrigaria cada mob que a
   * caçada cria - dezenas por minuto - a carregar um array vazio, e mudaria o
   * digest canónico de toda a corrida para descrever a coisa mais rara que
   * existe.
   *
   * A ORDEM é a de criação, e ela é o desempate de uma detonação. Nada percorre
   * isto por tique: uma marca não expira e não cobra. O que a consome é a ordem
   * do jogador.
   */
  private bleeding: BleedingMark[] = [];
  /**
   * A identidade da próxima marca.
   *
   * Um contador próprio e não `nextEntityId` nem `nextPoisonId`: partilhá-lo com
   * o primeiro faria cada aplicação deslocar os ids de todos os mobs seguintes -
   * o que muda o digest de uma corrida por uma razão que não é a mecânica - e
   * partilhá-lo com o segundo faria duas coisas de vidas diferentes competirem
   * pelo mesmo espaço de nomes.
   */
  private nextBleedingId = 1;
  /**
   * O fluxo do DIAMANTE SERRILHADO (gate V28G.P).
   *
   * Sexto fluxo de joia, separado dos outros cinco pela razão de sempre e por
   * uma que só ele tem: a cadência dele é ditada pelo RELÓGIO. Ele avança
   * enquanto uma lâmina estiver a tocar um inimigo, tenha a nave disparado ou
   * não - portanto partilhá-lo faria a sequência de procs, veneno e sangramento
   * de uma corrida depender de quanto tempo as serras estiveram encostadas a um
   * mob.
   *
   * É consumido CONDICIONALMENTE - só numa instância de dano de serra
   * confirmada - e isso é seguro porque mais ninguém o lê. Uma corrida sem esta
   * joia não lhe toca, e consome exatamente a mesma entropia que consumia antes
   * deste gate.
   */
  private sawRng: SimulationRng;
  /**
   * **A FASE da órbita das serras desta nave** (gate V28G.P).
   *
   * Um inteiro em `[0, SAW_ORBIT_PHASE_UNITS)`, zero enquanto a joia não estiver
   * encaixada, e zero é também a fase inicial de quem a tem.
   *
   * Estado que decide o FUTURO - onde as lâminas estão agora decide quem elas
   * tocam a seguir - portanto atravessa a instantânea. Acumulado em vez de
   * derivado do tique porque o nível PODE mudar a meio: uma queda ou uma fusão
   * reconfiguram a sessão no lugar, e uma fase recalculada da velocidade nova
   * teletransportaria as lâminas para outro ponto da órbita.
   */
  private sawPhase = SAW_INITIAL_PHASE;
  /**
   * O resto INTEIRO do orçamento das serras (gate V28G.P).
   *
   * Sempre menor que `sawDamageDenominator(nível)`. Sem ele, a mesma fração
   * perdia-se em todos os contactos - a truncagem é a mesma sempre, portanto não
   * se cancela, acumula - e a taxa de longo prazo ficaria abaixo do que o
   * orçamento promete.
   */
  private sawCarryMilli = 0n;
  /**
   * **Os relógios de contacto das serras, um por par lâmina→alvo** (V28G.P).
   *
   * Uma lista da CORRIDA e não um campo do mob, pela mesma razão que os stacks e
   * as marcas são listas: pendurá-los na entidade obrigaria cada mob que a
   * caçada cria - dezenas por minuto - a carregar um array vazio.
   *
   * Um por PAR, e nunca um por lâmina nem um por alvo: um relógio por lâmina
   * faria a segunda a tocar o mesmo mob não doer, o que transformaria quatro
   * serras em uma.
   */
  private sawContacts: HuntSawContact[] = [];
  /**
   * **Os mísseis em voo, e porque são uma lista à parte dos projéteis.**
   *
   * Ver {@link MissileEntity}: um míssil troca de alvo, e um projétil desta caça
   * tem `targetId` readonly e a regra de nunca reaquirir. São entidades
   * diferentes, e mantê-las separadas é o que faz este gate não poder mudar um
   * tiro comum, uma bala de pet ou uma bomba por acidente.
   */
  private missiles: MissileEntity[] = [];

  private phase: HuntPhase = 'active';
  private pendingMs = 0;
  private simTimeMs = 0;
  /**
   * The phases of the ship's wander. Derived from the seed, never serialised.
   *
   * `readonly` on the array and assigned once in the constructor, so a restore
   * cannot end up with a path different from the one it is restoring.
   */
  private readonly driftPhases: readonly number[];

  /**
   * **Quanto tempo de deriva foi CONGELADO** (gate V28H.N).
   *
   * A Lança Solar prende o movimento enquanto carrega, e é contrato aprovado. Na
   * caça ninguém conduz a nave - ela deriva - portanto prender o movimento é
   * prender a deriva.
   *
   * Um acumulador e não uma bandeira, e a razão é a única que interessa: a
   * deriva é uma função do tempo, e multiplicá-la por zero durante a carga faria
   * a nave SALTAR de volta à fase zero e saltar outra vez ao retomar. Somando o
   * tempo congelado e subtraindo-o, a fase fica exactamente onde estava e
   * continua de lá - a nave pára, e não se teleporta.
   */
  private driftFrozenMs = 0;
  private runStartMs = 0;

  private nextEntityId = 1;
  private mobs: MobEntity[] = [];
  private boss: BossEntity | null = null;
  private projectiles: ProjectileEntity[] = [];
  private events: HuntEvent[] = [];
  /**
   * The active pet, or null when the account has none.
   *
   * Authoritative state, carried in the snapshot: its drift phase and firing
   * accumulator decide the FUTURE of the run, so the server's re-simulation
   * has to continue them rather than restart them - otherwise every checkpoint
   * would reset the pet's cadence and quietly change how much it contributed.
   */
  private pet: PetEntity | null = null;
  /**
   * **O estado das três cartas desta corrida** (gate V28H.H).
   *
   * Entre zero e três entradas, pela ordem canónica dos espaços. Cada uma tem
   * um cooldown, um tempo activo e um acumulador de disparo, e as três coisas
   * decidem o FUTURO - portanto atravessam a instantânea inteiras.
   *
   * A lista é construída UMA vez, do loadout congelado, e a sua composição não
   * muda durante a corrida: equipar uma carta a meio de uma caçada reconfigura
   * a conta, nunca esta.
   */
  private cards: CardAbilityState[] = [];

  /** Elapsed time inside the current spawn window, ms. */
  private spawnAccumulatorMs = 0;
  /**
   * Moments still to come in this window, in ms from its start.
   *
   * Authoritative state: it is drawn once per window from the deterministic
   * spawn stream, and the server's re-simulation must release the same
   * arrivals at the same instants the browser did. Losing it would either
   * drop enemies or bunch them together at the next window boundary.
   */
  private spawnSchedule: number[] = [];
  private attackAccumulatorMs = 0;
  private bossCountdownMs: number;
  /** Nose direction, radians. Starts vertical; only ever turns, never moves. */
  private facing = HUNT_BALANCE.ship.restingFacingRad;

  /**
   * Local prediction of the server's progression. Every value is a bigint:
   * KENOCYBER has no level cap, so nothing here may quietly stop being exact.
   */
  private progression: ProgressionState = INITIAL_PROGRESSION;
  private gold = 0n;
  private health: number;

  // Session metrics.
  private kills = 0;
  /**
   * Normal mobs killed this session - the counter the fractional Gold rides on.
   *
   * Authoritative state, carried in the snapshot: 3 Gold per 8 kills only adds
   * up if the count survives a checkpoint splitting the group, a reload, a
   * point being spent and a death. Bosses never touch it; their reward is a
   * flat number of its own.
   */
  private normalKills = 0;
  /**
   * Bosses killed this session. Authoritative for the same reason as
   * `normalKills`: a Gold percentage makes the boss reward a fraction too.
   */
  private bossKills = 0;
  /**
   * Gold the bosses of this run are owed BEFORE any bonus, cumulative.
   *
   * A count no longer determines this: a tier 6 is worth six times a tier 1.
   * Carrying the base as a running total is what lets the percentage apply
   * once to the whole, so the fractions a Gold bonus produces survive a
   * checkpoint, a death and a reload exactly as the mob total's do.
   */
  private bossGoldBase = 0n;
  /** The encounter the run is bound to, or null between encounters. */
  private bossEncounter: HuntSnapshotBossEncounter | null = null;
  /**
   * Milli-health The Cure has healed but not yet delivered as a whole point.
   *
   * Regeneration is a rate, and a 16 ms tick of 1 hp/s is 16 thousandths of a
   * point. Rounding that per tick would either heal nothing forever or heal a
   * whole point sixty times a second, so the remainder is carried here and in
   * the snapshot - the same trick the Gold fractions use.
   */
  private regenCarryMilli = 0;
  /**
   * O resto SUB-MILLI do Roubo de Vida, em `[0, 10 000)` (gate V28G.I).
   *
   * A percentagem de um golpe produz uma fração de um milésimo de ponto de
   * vida, e sem este resto ela desaparecia: mil golpes de um milli a 9 %
   * curariam ZERO em vez de 90 milli. O mesmo truque de `regenCarryMilli`, do
   * resto sub-milissegundo do `HuntClock` e das frações de Gold.
   *
   * Estado AUTORITATIVO: entra na instantânea, porque decide o futuro da
   * corrida. Largado na morte - um destroço não leva cura devida para a corrida
   * seguinte - e largado quando a vida cheia limita, pela mesma regra que
   * `tickRegen` já segue.
   */
  private lifestealCarryBps = 0;
  private runKills = 0;
  private runDamage = 0;
  private runGold = 0n;
  private readonly damageEvents: TimedAmount[] = [];
  private readonly goldEvents: TimedAmount[] = [];
  private firstDamageAtMs: number | null = null;
  private firstGoldAtMs: number | null = null;
  private sessionDamageTotal = 0;
  private sessionGoldTotal = 0n;
  /**
   * Cumulative XP granted this session, NEVER reduced by a level-up.
   *
   * The server diffs this across a checkpoint to learn what the window earned;
   * `xp` alone cannot answer that because it resets on every level.
   */
  private sessionXpTotal = 0n;

  private readonly mobMaxHealth: number;
  private readonly mobSize: number;
  private deathStats: DeathStats | null = null;
  private lastDeathCause: 'boss-breach' | 'mob-leaks' | null = null;

  constructor(config: HuntConfig) {
    this.currentConfig = config;
    // Independent, reproducible sub-streams: replay-friendly and stable even
    // if one system starts drawing more values than another.
    this.spawnRng = createSimulationRng(deriveSimulationSeed(config.seed, 'mob-spawn', 0));
    this.bossTimerRng = createSimulationRng(deriveSimulationSeed(config.seed, 'boss-timer', 0));
    /*
     * A stream of its OWN for pet target selection.
     *
     * Sharing the spawn stream would make the two interfere: a pet that fired
     * once more would shift every future spawn, and a map with a different
     * spawn rate would change which enemies the pet picks. Separate streams are
     * what let one system start drawing more values without moving another.
     *
     * It is SIMULATION randomness, seeded from the public simulation seed - the
     * choice of which mob to shoot is not an economic draw and must be
     * reproducible by the server. It never touches the economic contracts.
     */
    this.petTargetRng = createSimulationRng(deriveSimulationSeed(config.seed, 'pet-target', 0));
    /*
     * O fluxo da loteria, derivado da MESMA semente pública e de mais nada.
     *
     * Um proc não é um sorteio económico: não decide recompensa nenhuma, e o
     * servidor tem de chegar exatamente ao mesmo resultado quando re-simula uma
     * janela. Índice zero porque uma caça tem uma nave - o duelo domina pelo
     * índice canónico da cadeira, que aqui não existe.
     */
    this.jewelProcRng = createSimulationRng(
      deriveSimulationSeed(config.seed, HUNT_OUTGOING_PROC_RNG_LABEL, 0),
    );
    /* O fluxo da SALVA, da mesma semente pública e com o seu próprio rótulo. */
    this.volleyBombRng = createSimulationRng(
      deriveSimulationSeed(config.seed, HUNT_VOLLEY_BOMB_RNG_LABEL, 0),
    );
    /* E o fluxo de ALVO dos mísseis, pela mesma regra e com o seu rótulo. */
    this.missileTargetRng = createSimulationRng(
      deriveSimulationSeed(config.seed, HUNT_MISSILE_TARGET_RNG_LABEL, 0),
    );
    /* E o fluxo do VENENO, pela mesma regra e com o seu rótulo (V28G.M). */
    this.poisonRng = createSimulationRng(
      deriveSimulationSeed(config.seed, HUNT_POISON_RNG_LABEL, 0),
    );
    /* E o fluxo do SANGRAMENTO, pela mesma regra e com o seu rótulo (V28G.N). */
    this.bleedingRng = createSimulationRng(
      deriveSimulationSeed(config.seed, HUNT_BLEEDING_RNG_LABEL, 0),
    );
    /* E o fluxo das SERRAS, pela mesma regra e com o seu rótulo (V28G.P). */
    this.sawRng = createSimulationRng(deriveSimulationSeed(config.seed, HUNT_SAW_RNG_LABEL, 0));
    /* E o fluxo do CORAÇÃO VITAL, pela mesma regra e com o seu rótulo (V28G.U). */
    this.vitalHeartRng = createSimulationRng(
      deriveSimulationSeed(config.seed, HUNT_VITAL_HEART_RNG_LABEL, 0),
    );
    /*
     * The four phases of the ship's wander, drawn ONCE and never again.
     *
     * Drawn here rather than stored, and from `config.seed` rather than from
     * any live stream, because the config is what a restored run is rebuilt
     * with: the same seed gives the same phases on the browser, on the server,
     * after a reload and after a re-simulation. Drawing them from `spawnRng`
     * would have coupled the ship's path to how many enemies had spawned.
     */
    this.driftPhases = (() => {
      const rng = createSimulationRng(deriveSimulationSeed(config.seed, 'ship-drift', 0));
      return HUNT_BALANCE.ship.drift.periodsMs.map(() => rng.nextUnitInterval() * Math.PI * 2);
    })();
    this.mobMaxHealth = mobHealthForMap(config.mapIndex);
    this.mobSize = mobDisplaySizeForMap(config.mapIndex);
    // Server-authorised starting progression, when the caller has one. The
    // allocated points feed the derived stats, so this must land BEFORE health.
    const initial = config.initialProgression;
    if (initial) {
      this.progression = toProgressionState(initial);
      this.gold = parseDecimalString(initial.gold, 'progression.gold');
    }
    this.health = this.maxHealth();
    this.bossEncounter = config.bossEncounter
      ? {
          encounterId: config.bossEncounter.encounterId,
          ordinal: config.bossEncounter.ordinal,
          tier: config.bossEncounter.tier,
          activated: config.bossEncounter.activated,
          retrying: config.bossEncounter.retrying === true,
        }
      : null;
    this.bossCountdownMs = this.rollBossDelayMs();
    this.spawnSchedule = this.rollSpawnSchedule();
    if (config.bossEncounter?.activated) {
      // Its boss was already on the field when this simulation was built - a
      // restart after death, or a session rebuilt around the same encounter.
      // It returns immediately, at full health, rather than behind a timer
      // that has already run out.
      this.spawnBoss();
    }
    this.pet = this.buildPet(config.pet ?? null);
    /*
     * **As oito ranhuras de efeito das cartas** (gate V28H.L).
     *
     * As mesmas da sala do duelo, do mesmo módulo neutro: uma Lentidão reduz
     * movimento nos dois sítios, e um Bastião torna inalvejável nos dois. Uma
     * segunda implementação seria uma segunda regra a divergir na primeira
     * correcção.
     *
     * Na caça só uma delas é usada hoje - o Bastião - porque as outras sete são
     * cartas de duelo. As oito ficam na mesma: a estrutura é a mesma, e uma
     * carta futura de caça não precisa de a inventar.
     */
    this.cardEffects = initialCardEffects();
    this.cards = initialCardAbilities(config.cards);
  }

  /**
   * The pet entity for a configured pet, parked at the centre of its lane.
   *
   * Null in, null out: an account with no active pet has no entity, and every
   * pet code path below is a no-op rather than a branch on a dummy.
   */
  private buildPet(
    configured: {
      definitionId: string;
      evolution: number;
      damageFoodCount?: number;
      attackSpeedFoodCount?: number;
    } | null,
  ): PetEntity | null {
    if (!configured) {
      return null;
    }
    const home = this.petHome(0);
    return {
      definitionId: configured.definitionId,
      evolution: configured.evolution,
      // Absent means untrained, which is what every pet starts as and what a
      // session created before training existed correctly describes.
      damageFoodCount: configured.damageFoodCount ?? 0,
      attackSpeedFoodCount: configured.attackSpeedFoodCount ?? 0,
      x: home.x,
      y: home.y,
      facing: HUNT_BALANCE.ship.restingFacingRad,
      driftSeconds: 0,
      attackAccumulatorMs: 0,
      // A beam starts OFF and acquires on its first tick with a live enemy.
      beamTargetId: null,
      beamAccumulatorMs: 0,
      beamReacquireMs: 0,
      damageDealt: 0,
    };
  }

  /**
   * Where the pet is at a given phase of its drift.
   *
   * A figure-of-eight: `sin(t)` across, `sin(2t)` up and down. Deliberately
   * NOT anchored to the ship - the ship never moves, and a pet that simply
   * hovered beside it would read as a decoration bolted on rather than
   * something flying under its own power. The lane is bounded by construction,
   * so it can never leave the arena or reach the cannon formation.
   */
  private petHome(driftSeconds: number): { x: number; y: number } {
    const { centerXFraction, centerYFraction, amplitudeX, amplitudeY, periodSeconds } =
      PET_BALANCE.flight;
    const phase = (driftSeconds / periodSeconds) * Math.PI * 2;
    return {
      x: HUNT_BALANCE.arena.width * centerXFraction + Math.sin(phase) * amplitudeX,
      y: HUNT_BALANCE.arena.height * centerYFraction + Math.sin(phase * 2) * amplitudeY,
    };
  }

  /* ------------------------------------------------------------------ */
  /*  Snapshot / restore                                                */
  /* ------------------------------------------------------------------ */

  /**
   * Complete, serialisable state of the run.
   *
   * Presentation state is deliberately absent - see `snapshot.ts`. Restoring
   * this and continuing must produce exactly the run that never stopped.
   */
  exportSnapshot(): HuntSnapshot {
    return {
      version: HUNT_SNAPSHOT_VERSION,
      tickMs: this.simTimeMs,
      /* A deriva presa pela carga: sem ela, uma reconexão desprendia a nave. */
      driftFrozenMs: this.driftFrozenMs,
      pendingMs: this.pendingMs,
      runStartMs: this.runStartMs,
      phase: this.phase,
      nextEntityId: this.nextEntityId,
      facing: this.facing,
      health: this.health,
      spawnAccumulatorMs: this.spawnAccumulatorMs,
      regenCarryMilli: this.regenCarryMilli,
      lifestealCarryBps: this.lifestealCarryBps,
      spawnSchedule: [...this.spawnSchedule],
      attackAccumulatorMs: this.attackAccumulatorMs,
      bossCountdownMs: this.bossCountdownMs,
      rng: {
        spawn: this.spawnRng.snapshot(),
        bossTimer: this.bossTimerRng.snapshot(),
        petTarget: this.petTargetRng.snapshot(),
        jewelProc: this.jewelProcRng.snapshot(),
        /* O fluxo da salva retoma onde estava, como o dos procs (gate V28G.J). */
        volleyBomb: this.volleyBombRng.snapshot(),
        /* E o fluxo de alvo dos mísseis, pela mesma razão (gate V28G.L). */
        missileTarget: this.missileTargetRng.snapshot(),
        /* E o fluxo do veneno, pela mesma razão (gate V28G.M). */
        poison: this.poisonRng.snapshot(),
        /* E o do sangramento, pela mesma razão (gate V28G.N). */
        bleeding: this.bleedingRng.snapshot(),
        /* E o das serras, pela mesma razão (gate V28G.P). */
        saw: this.sawRng.snapshot(),
        /* E o do Coração Vital, pela mesma razão (gate V28G.U). */
        vitalHeart: this.vitalHeartRng.snapshot(),
      },
      bossEncounter: this.bossEncounter ? { ...this.bossEncounter } : null,
      mobs: this.mobs.map((mob) => ({ ...mob })),
      boss: this.boss ? { ...this.boss } : null,
      /*
       * O dano em milli sai como STRING decimal.
       *
       * Um `bigint` não sobrevive a `JSON.stringify` - atira - e um `number`
       * perderia dígitos numa contagem grande de corações. A string atravessa
       * o documento, o digest e a re-simulação do servidor sem perder nada.
       */
      projectiles: this.projectiles.map((projectile) => {
        const { damageMilli, splashDamageMilli, jewelProc, atomicBomb, ...rest } = projectile;
        return {
          ...rest,
          damageMilli: damageMilli.toString(10),
          // A magnitude congelada atravessa como decimal, pela mesma regra.
          jewelProc: jewelProcToWire(jewelProc),
          /*
           * A bomba atravessa inteira, e ausente quando não há bomba nenhuma.
           *
           * Omitida em vez de escrita como `null`: um tiro comum e uma bala de
           * pet não têm este campo, e escrevê-lo mudaria o digest canónico de
           * toda a corrida sem acrescentar informação.
           */
          ...(atomicBomb === undefined
            ? {}
            : { atomicBomb: atomicBombToWire(atomicBomb) as AtomicBombSelectionWire }),
          ...(splashDamageMilli === undefined
            ? {}
            : { splashDamageMilli: splashDamageMilli.toString(10) }),
        };
      }),
      /*
       * Os mísseis em voo, inteiros (gate V28G.L).
       *
       * Posição, direção, alvo, tempo restante, identidade e o dano congelado -
       * a lista exata que o contrato manda sobreviver a uma instantânea, a uma
       * reconexão e a uma retoma. O dano sai como string decimal pela mesma regra
       * que o dos projéteis segue.
       */
      missiles: this.missiles.map((missile) => {
        const { damageMilli, ...rest } = missile;
        return { ...rest, damageMilli: damageMilli.toString(10) };
      }),
      /*
       * Os stacks de veneno, inteiros (gate V28G.M).
       *
       * Identidade, alvo, total congelado, instante de aplicação e parcelas já
       * pagas - o que basta para as que faltam caírem exatamente nos instantes
       * em que iam cair. O total sai como string decimal pela mesma regra que o
       * dos projéteis e o dos mísseis seguem.
       */
      poison: this.poison.map((stack) => ({
        id: stack.id,
        targetId: stack.targetId,
        totalDamageMilli: stack.totalDamageMilli.toString(10),
        appliedAt: stack.appliedAt,
        ticksDone: stack.ticksDone,
      })),
      nextPoisonId: this.nextPoisonId,
      /*
       * As marcas de sangramento, inteiras (gate V28G.N).
       *
       * Identidade, alvo, dano congelado e instante de aplicação - o que basta
       * para uma detonação depois da retoma cobrar exatamente o que cobraria
       * antes dela. O dano sai como string decimal pela mesma regra que o dos
       * projéteis, o dos mísseis e o dos stacks seguem.
       */
      bleeding: this.bleeding.map((mark) => ({
        id: mark.id,
        targetId: mark.targetId,
        damageMilli: mark.damageMilli.toString(10),
        appliedAt: mark.appliedAt,
      })),
      nextBleedingId: this.nextBleedingId,
      /*
       * A órbita, o resto e os relógios das serras (gate V28G.P).
       *
       * Os relógios saem em ORDEM CANÓNICA - `(lâmina, alvo)` - e não pela ordem
       * em que os contactos aconteceram: a lista viva cresce por primeiro
       * contacto, que depende de onde os mobs desceram, e duas re-simulações da
       * mesma corrida podiam guardar a mesma informação por outra ordem. Uma
       * corrida tem uma representação só, ou o digest compara duas verdades
       * iguais e diz que discordam.
       */
      sawPhase: this.sawPhase,
      sawCarryMilli: this.sawCarryMilli.toString(10),
      saws: [...this.sawContacts]
        .sort((left, right) => left.bladeIndex - right.bladeIndex || left.targetId - right.targetId)
        .map((contact) => ({ ...contact })),
      pet: this.pet ? { ...this.pet } : null,
      /*
       * As invocações (versão 14, gate V28H.M).
       *
       * Copiadas e nunca partilhadas, como o pet: uma corrida a andar não pode
       * segurar a mesma referência que o documento que a descreve.
       */
      summons: this.summons.map((summon) => ({ ...summon })),
      /*
       * As três cartas, com os três relógios e o acumulador (gate V28H.H).
       *
       * Tudo aqui decide o FUTURO: o cooldown decide quando ela volta, o tempo
       * activo decide quantas salvas ainda saem, e o acumulador decide se a
       * próxima sai neste tique ou no seguinte. Uma retoma sem eles reiniciaria
       * a habilidade - e um F5 passaria a valer uma activação.
       *
       * Cópias, e nunca as referências vivas: uma instantânea é um documento.
       */
      cards: this.cards.map((card) => ({ ...card })),
      /*
       * **As oito ranhuras de efeito** (gate V28H.T), pela ordem canónica.
       *
       * Elas faltavam, e a falta era mecânica antes de ser visual: o Bastião
       * ligava-se e evaporava-se na restauração seguinte, portanto uma nave
       * protegida continuava a levar dano de uma fuga.
       */
      /* E as bombas em voo (gate V28H.T): elas são estado, e não decoração. */
      cardBlasts: this.cardBlasts.map((blast) => ({
        ...blast,
        damageMilli: blast.damageMilli.toString(10),
      })),
      cardEffects: CARD_EFFECT_SLOTS.map((slot) => ({
        ms: this.cardEffects[slot].remainingMs,
        bps: this.cardEffects[slot].magnitudeBps,
      })),
      progression: fromProgressionState(this.progression, this.gold),
      earned: {
        xp: this.sessionXpTotal.toString(10),
        gold: this.sessionGoldTotal.toString(10),
        damage: this.sessionDamageTotal,
        kills: this.kills,
        normalKills: this.normalKills,
        bossKills: this.bossKills,
        bossGoldBase: this.bossGoldBase.toString(10),
      },
      metrics: {
        damageEvents: eventsInWindow(
          this.damageEvents,
          this.simTimeMs,
          HUNT_BALANCE.metricsWindowMs,
        ),
        goldEvents: eventsInWindow(this.goldEvents, this.simTimeMs, HUNT_BALANCE.metricsWindowMs),
        firstDamageAtMs: this.firstDamageAtMs,
        firstGoldAtMs: this.firstGoldAtMs,
      },
      run: { kills: this.runKills, damage: this.runDamage, gold: this.runGold.toString(10) },
      deathCause: this.lastDeathCause,
      death: this.deathStats ? { ...this.deathStats } : null,
      events: this.events.map((event) => ({ ...event })),
    };
  }

  /**
   * Rebuilds a simulation that continues a snapshot exactly.
   *
   * `config` must be the one the run started with (same seed, map, ship and
   * cannon): the snapshot carries state, not identity.
   */
  static restore(config: HuntConfig, snapshot: HuntSnapshot): HuntSimulation {
    const simulation = new HuntSimulation(config);
    simulation.spawnRng = restoreSimulationRng(snapshot.rng.spawn);
    simulation.bossTimerRng = restoreSimulationRng(snapshot.rng.bossTimer);
    /*
     * Absent in every snapshot written before the Drone could pick its own
     * targets. Those runs are rebuilt with a FRESH stream from the session's
     * own seed rather than refused: the seed is in the config, so the stream is
     * still deterministic and the server's re-simulation derives the identical
     * one. The alternative - failing the parse - would strand live sessions.
     */
    simulation.petTargetRng = snapshot.rng.petTarget
      ? restoreSimulationRng(snapshot.rng.petTarget)
      : createSimulationRng(deriveSimulationSeed(config.seed, 'pet-target', 0));
    /*
     * RETOMA, e não recria. Recriar da semente repetiria a sequência desde o
     * princípio, e o disparo seguinte a uma reconexão devolveria um proc que a
     * corrida já tinha gasto.
     *
     * Sem alternativa para a ausência, ao contrário de `petTarget`: a versão 4
     * exige o campo, e um documento sem ele nunca chega até aqui.
     */
    simulation.jewelProcRng = restoreSimulationRng(snapshot.rng.jewelProc);
    /* E o da salva também retoma, pela mesma razão exata (gate V28G.J). */
    simulation.volleyBombRng = restoreSimulationRng(snapshot.rng.volleyBomb);
    /* E o de alvo dos mísseis, pela mesma razão (gate V28G.L). */
    simulation.missileTargetRng = restoreSimulationRng(snapshot.rng.missileTarget);
    /* E o do veneno, pela mesma razão (gate V28G.M). */
    simulation.poisonRng = restoreSimulationRng(snapshot.rng.poison);
    /* E o do sangramento, pela mesma razão (gate V28G.N). */
    simulation.bleedingRng = restoreSimulationRng(snapshot.rng.bleeding);
    /* E o das serras, pela mesma razão (gate V28G.P). */
    simulation.sawRng = restoreSimulationRng(snapshot.rng.saw);
    /* E o do Coração Vital, pela mesma razão (gate V28G.U). */
    simulation.vitalHeartRng = restoreSimulationRng(snapshot.rng.vitalHeart);
    /*
     * A órbita, o resto e os relógios de contacto VOLTAM inteiros (V28G.P).
     *
     * A fase vem do documento porque é a única fonte que ainda diz onde as
     * lâminas estavam; o resto porque é a única que diz o que ficou por pagar; e
     * os relógios porque sem eles uma retoma daria a todas as lâminas um
     * contacto imediato - um par que nunca se tocou está sempre pronto - e o
     * primeiro tique depois de um checkpoint pagaria um intervalo inteiro de
     * dano de uma vez.
     */
    simulation.sawPhase = snapshot.sawPhase;
    simulation.sawCarryMilli = BigInt(snapshot.sawCarryMilli);
    simulation.sawContacts = snapshot.saws.map((contact) => ({ ...contact }));
    simulation.simTimeMs = snapshot.tickMs;
    simulation.driftFrozenMs = snapshot.driftFrozenMs;
    /* E as bombas em voo voltam a voar de onde estavam (gate V28H.T). */
    simulation.cardBlasts = snapshot.cardBlasts.map((blast) => ({
      ...blast,
      damageMilli: BigInt(blast.damageMilli),
    }));
    /* E as oito ranhuras voltam onde estavam (gate V28H.T). */
    for (const [index, slot] of CARD_EFFECT_SLOTS.entries()) {
      const entry = snapshot.cardEffects[index];
      if (entry === undefined) {
        continue;
      }
      simulation.cardEffects[slot].remainingMs = entry.ms;
      simulation.cardEffects[slot].magnitudeBps = entry.bps;
    }
    simulation.pendingMs = snapshot.pendingMs;
    simulation.runStartMs = snapshot.runStartMs;
    simulation.phase = snapshot.phase;
    simulation.nextEntityId = snapshot.nextEntityId;
    simulation.facing = snapshot.facing;
    simulation.health = snapshot.health;
    simulation.spawnAccumulatorMs = snapshot.spawnAccumulatorMs;
    simulation.spawnSchedule = [...snapshot.spawnSchedule];
    simulation.attackAccumulatorMs = snapshot.attackAccumulatorMs;
    simulation.bossCountdownMs = snapshot.bossCountdownMs;
    /*
     * The snapshot's encounter wins over the config's: the config says which
     * encounter the session was BOUND to when it was built, and the snapshot
     * says what has happened to it since - including that its boss is already
     * on the field, or that it was defeated and the run is waiting to be
     * rebound. A config carrying one the snapshot does not is an adoption, and
     * the constructor has already applied it.
     */
    if (snapshot.bossEncounter !== null) {
      simulation.bossEncounter = { ...snapshot.bossEncounter };
    }
    simulation.mobs = snapshot.mobs.map((mob) => ({ ...mob }));
    simulation.boss = snapshot.boss ? { ...snapshot.boss } : null;
    simulation.projectiles = snapshot.projectiles.map((projectile) => {
      const { damageMilli, splashDamageMilli, jewelProc, atomicBomb, ...rest } = projectile;
      return {
        ...rest,
        damageMilli: BigInt(damageMilli),
        /*
         * A magnitude volta como o `bigint` que era, e NÃO é recalculada. O
         * documento é a única fonte: o perfil desta corrida pode já não ser o
         * que estava encaixado quando o tiro nasceu.
         */
        jewelProc: jewelProcFromWire(jewelProc),
        /* E a bomba volta com ela, pela mesma regra e sem reavaliar nada. */
        ...(atomicBomb === undefined
          ? {}
          : { atomicBomb: atomicBombFromWire(atomicBomb) as AtomicBombSelection }),
        ...(splashDamageMilli === undefined
          ? {}
          : { splashDamageMilli: BigInt(splashDamageMilli) }),
      };
    });
    /*
     * Os mísseis VOLTAM inteiros, e nada é reavaliado (gate V28G.L).
     *
     * A magnitude vem do documento porque é a única fonte que ainda diz quanto
     * ela valia quando o míssil saiu, e o alvo vem de lá porque a escolha foi
     * autoritativa e já foi feita.
     */
    simulation.missiles = snapshot.missiles.map((missile) => {
      const { damageMilli, ...rest } = missile;
      return { ...rest, damageMilli: BigInt(damageMilli) };
    });
    /*
     * Os stacks VOLTAM inteiros, e nada é reavaliado (gate V28G.M).
     *
     * O total vem do documento porque é a única fonte que ainda diz quanto ele
     * valia quando o stack nasceu, e o relógio continua de onde estava: uma
     * corrida retomada entre a terceira e a quarta parcela cobra a quarta no
     * instante em que ela ia cair, e não um segundo depois de voltar.
     */
    simulation.poison = snapshot.poison.map((stack) => ({
      id: stack.id,
      targetId: stack.targetId,
      totalDamageMilli: BigInt(stack.totalDamageMilli),
      appliedAt: stack.appliedAt,
      ticksDone: stack.ticksDone,
    }));
    simulation.nextPoisonId = snapshot.nextPoisonId;
    /*
     * As marcas VOLTAM inteiras, e nada é reavaliado (gate V28G.N).
     *
     * O dano vem do documento porque é a única fonte que ainda diz quanto ele
     * valia quando a marca nasceu. Não há relógio a retomar: elas não expiram, e
     * o que as espera é uma ordem do jogador.
     */
    simulation.bleeding = snapshot.bleeding.map((mark) => ({
      id: mark.id,
      targetId: mark.targetId,
      damageMilli: BigInt(mark.damageMilli),
      appliedAt: mark.appliedAt,
    }));
    simulation.nextBleedingId = snapshot.nextBleedingId;
    // The snapshot's pet wins over the config's, so the drift phase, the
    // firing accumulator and the damage tally continue rather than restart.
    // A config that carries a pet the snapshot does not (the run was started
    // before it was activated) still gets one, parked at its lane centre.
    simulation.pet = snapshot.pet ? { ...snapshot.pet } : simulation.buildPet(config.pet ?? null);
    /*
     * E as invocações voltam com ele (gate V28H.M). Reconstruídas, nunca
     * adoptadas - e um documento antigo devolve nenhuma, que é o estado exacto
     * da corrida que o escreveu.
     */
    simulation.summons = (snapshot.summons ?? []).map((summon) => ({ ...summon }));
    /*
     * As cartas vêm do DOCUMENTO, e a composição vem do loadout congelado.
     *
     * Ler os relógios da instantânea e a lista de `config.cards` seria duas
     * fontes; ler tudo da instantânea é uma. A versão 13 exige o campo, portanto
     * não há recuo a inventar aqui.
     */
    simulation.cards = snapshot.cards.map((card) => ({ ...card }));
    simulation.progression = toProgressionState(snapshot.progression);
    simulation.gold = parseDecimalString(snapshot.progression.gold, 'progression.gold');
    simulation.sessionXpTotal = parseDecimalString(snapshot.earned.xp, 'earned.xp');
    simulation.sessionGoldTotal = parseDecimalString(snapshot.earned.gold, 'earned.gold');
    simulation.sessionDamageTotal = snapshot.earned.damage;
    simulation.kills = snapshot.earned.kills;
    simulation.normalKills = snapshot.earned.normalKills;
    simulation.bossKills = snapshot.earned.bossKills;
    /*
     * Rebuilt from the count when absent, which is exactly right for a
     * snapshot written before tiers existed: every boss in it was worth the
     * flat 60, so the base IS the count times 60 and nothing is lost.
     */
    simulation.bossGoldBase =
      snapshot.earned.bossGoldBase === undefined
        ? BigInt(HUNT_BALANCE.boss.goldReward) * BigInt(snapshot.earned.bossKills)
        : parseDecimalString(snapshot.earned.bossGoldBase, 'earned.bossGoldBase');
    simulation.regenCarryMilli = snapshot.regenCarryMilli;
    simulation.lifestealCarryBps = snapshot.lifestealCarryBps;
    simulation.damageEvents.length = 0;
    simulation.damageEvents.push(...snapshot.metrics.damageEvents.map((event) => ({ ...event })));
    simulation.goldEvents.length = 0;
    simulation.goldEvents.push(...snapshot.metrics.goldEvents.map((event) => ({ ...event })));
    simulation.firstDamageAtMs = snapshot.metrics.firstDamageAtMs;
    simulation.firstGoldAtMs = snapshot.metrics.firstGoldAtMs;
    simulation.runKills = snapshot.run.kills;
    simulation.runDamage = snapshot.run.damage;
    simulation.runGold = parseDecimalString(snapshot.run.gold, 'run.gold');
    simulation.lastDeathCause = snapshot.deathCause;
    simulation.deathStats = snapshot.death ? { ...snapshot.death } : null;
    simulation.events = snapshot.events.map((event) => ({ ...event }));
    return simulation;
  }

  /**
   * Cumulative earnings of the session.
   *
   * The server takes the difference across a checkpoint window; nothing the
   * client reports takes part in that arithmetic.
   */
  earnedTotals(): { xp: bigint; gold: bigint; damage: number; kills: number } {
    return {
      xp: this.sessionXpTotal,
      gold: this.sessionGoldTotal,
      damage: this.sessionDamageTotal,
      kills: this.kills,
    };
  }

  /**
   * Overwrites the local prediction with the server's authoritative answer.
   *
   * Only the progression numbers move: entities, timers and the RNG are left
   * alone, so a correction never restarts the arena or skips a boss.
   */
  applyAuthoritativeProgression(progression: HuntSnapshotProgression): void {
    this.progression = toProgressionState(progression);
    this.gold = parseDecimalString(progression.gold, 'progression.gold');
    // Raising max health must never leave current health above it.
    this.health = Math.min(this.health, this.maxHealth());
  }

  /**
   * Applies newly spent points to the run in flight.
   *
   * Spending a point is not a new hunt: mobs, boss, projectiles, timers, RNG
   * streams, accumulators and earnings are untouched here. Only the numbers
   * the ship fights with change, and they change from now on - a shot already
   * in the air keeps the damage it was born with, and the attack accumulator
   * is not reset, so the new cadence simply applies to the next volley.
   *
   * Health follows the damage taken, not the fraction: a ship at 70/100 that
   * buys +5 maximum health continues at 75/105, because it had taken 30 points
   * of damage and still has. A full ship stays full. A dead one stays dead -
   * buying health is not a resurrection, and the restart is still the way back.
   */
  applyAllocation(progression: HuntSnapshotProgression): void {
    const previousMax = this.maxHealth();
    this.progression = toProgressionState(progression);
    this.gold = parseDecimalString(progression.gold, 'progression.gold');
    if (this.phase === 'dead') {
      return;
    }
    const gained = this.maxHealth() - previousMax;
    this.health = Math.max(0, Math.min(this.maxHealth(), this.health + gained));
  }

  /**
   * Swaps the equipped ship and cannon on the run in flight.
   *
   * The same promise `applyAllocation` makes, for equipment: this is not a new
   * hunt. Mobs keep their positions and remaining health, the boss keeps its
   * countdown or its presence, the spawn schedule and every RNG stream carry
   * on, accumulators are not reset, and earnings, metrics and the death count
   * are untouched. Equipping is therefore not a way to escape a boss or to
   * wipe a field that has gone badly.
   *
   * **Projectiles already in the air keep the damage they were born with.**
   * Each one carries its own `damage`, and nothing here rewrites it - a shot
   * fired under the old ship lands for what it was worth when it left the
   * muzzle. Only volleys created after this call use the new loadout, and the
   * attack accumulator is deliberately left alone so the swap neither grants
   * nor cancels a shot.
   *
   * Health follows the DAMAGE TAKEN, exactly like buying health does:
   *
   *     taken    = oldMaxHealth - oldHealth
   *     newHealth = clamp(newMaxHealth - taken, 0, newMaxHealth)
   *
   * Ships currently grant damage only, so today this is always a no-op - but
   * writing the general rule now means a future item that changes maximum
   * health cannot accidentally heal. Equipping never heals and never
   * resurrects; if a future swap ever left the ship at zero, the normal death
   * flow runs rather than the screen being cleared.
   */
  applyLoadout(equipment: {
    shipIndex?: number;
    shipLevel?: number;
    cannonRarity?: CannonRarity;
    cannonLevel?: number;
    /**
     * New artifact totals, when the change was a passive rather than a swap.
     *
     * Same promise as an equipment change and for the same reason: a copy
     * obtained, combined or sold applies FROM NOW ON. Shots already in the air
     * keep the damage they were born with, and nothing that already happened
     * is recomputed.
     */
    artifactEffects?: ScaledArtifactEffects;
    /**
     * New equipment totals, when a component was equipped, removed, upgraded,
     * traded away or destroyed.
     *
     * The identical promise, for the identical reason: the change applies FROM
     * NOW ON. Shots already in the air keep the damage they were born with, the
     * attack accumulator is not reset, the wave and the boss countdown continue,
     * and health follows the damage already taken rather than being refilled.
     */
    equipmentTotals?: EquipmentTotals;
    /**
     * A contagem de Corações Primordiais da nave EQUIPADA agora.
     *
     * A promessa idêntica, pela razão idêntica: aplica-se DAQUI PARA A FRENTE.
     * Trocar de nave troca o bónus a partir desta fronteira; os tiros que já
     * estão no ar mantêm o dano com que nasceram, o acumulador de ataque não é
     * reposto, e nada do que já aconteceu é recalculado.
     */
    primordialHeartCount?: bigint;
    /*
     * As joias, pela mesma promessa: aplicam-se DAQUI PARA A FRENTE.
     *
     * Trocar de skin troca o bonus a partir desta fronteira; os tiros que ja
     * estao no ar mantem o dano com que nasceram, e nada do que ja aconteceu e
     * recalculado.
     */
    jewels?: JewelSocketProfile;
  }): void {
    const previousMax = this.maxHealth();
    const taken = previousMax - this.health;
    this.currentConfig = { ...this.currentConfig, ...equipment };
    if (this.phase === 'dead') {
      // A dead ship stays dead: new equipment is not a resurrection.
      return;
    }
    const nextMax = this.maxHealth();

    /*
     * The ceiling moved, and the two directions are treated DIFFERENTLY on
     * purpose.
     *
     * Growing preserves the DAMAGE ALREADY TAKEN, so extra maximum health is
     * never free healing: 70/100 with +5 becomes 75/105. That is the rule
     * `CLAUDE.md` fixes for spending a point, and it is what stops a player
     * topping themselves up by buying health mid-fight.
     *
     * Shrinking preserves the PERCENTAGE instead: 70/100 becoming 63/90 is 70%
     * before and 70% after. Carrying the damage down would take 10 points of
     * current health for 10 points of ceiling - so selling an artifact while
     * hurt could kill outright, and a ship at 30/100 losing 75 of maximum would
     * simply die at the moment of a market sale. A player who chose to sell an
     * item did not choose that.
     *
     * The percentage is FLOORED, so the change can never round upwards into
     * free healing either.
     */
    if (nextMax >= previousMax) {
      this.health = Math.min(nextMax, nextMax - taken);
    } else if (previousMax > 0) {
      this.health = Math.floor((this.health * nextMax) / previousMax);
    }
    this.health = Math.max(0, Math.min(nextMax, this.health));

    if (this.health <= 0) {
      // Reachable only from a ship that was already at (or rounded down to)
      // nothing. It goes through the ordinary death path so the death screen,
      // the stats and the restart behave exactly as they do for a lethal leak.
      this.die('mob-leaks');
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Derived stats                                                     */
  /* ------------------------------------------------------------------ */

  /**
   * `SHIP_BASE_DAMAGE_MILLI` for this run: exact, integer, and the one value
   * every jewel coefficient will multiply.
   *
   * Nothing about it is a percentage, a rune, a heart, a critical or another
   * jewel, and it is emphatically not `projectile.damage` - that is the FINAL
   * damage of a shot, and an effect scaling off it would compound with
   * everything that was live when the shot was born.
   */
  baseDamageMilli(): number {
    return huntShipBaseDamageMilli(this.progression.damagePoints, this.equipment);
  }

  /**
   * Step 1, in the whole damage points the physics works in.
   *
   * A VIEW of {@link baseDamageMilli}, not a second calculation. It used to be
   * `effectiveBaseDamage(points) + equipmentBaseAttack(equipment)` - two
   * floating-point additions, and a second place that knew what "base damage"
   * meant. There is now one, it is an integer, and this divides it once.
   */
  baseDamage(): number {
    return this.baseDamageMilli() / DAMAGE_MILLI_PER_UNIT;
  }

  /**
   * Step 4: the flat damage added to EACH projectile after the main formula.
   *
   * Deliberately outside both `damageBonusPercent` and `baseDamage`. It is
   * never multiplied by the damage percentage and it is never part of the base
   * that percentage multiplies, which is what makes it the one attribute worth
   * the same to a beginner and to a player carrying +900%.
   */
  bonusAttackPerProjectile(): number {
    return equipmentBonusAttack(this.equipment);
  }

  /**
   * Every damage percentage, summed (step 2 of the authoritative order).
   *
   * The ship and the artifacts are ADDITIVE with each other - a 5% Guns next
   * to a 16% ship is 21%, not 21.8% - so the result cannot depend on the order
   * the sources happen to be listed in.
   */
  damageBonusPercent(): number {
    return (
      shipDamageBonusBpsAtLevel(this.config.shipIndex, this.config.shipLevel) / 100 +
      passiveDamagePercent(this.passives) +
      equipmentDamagePercent(this.equipment)
    );
  }

  /**
   * Damage of a SINGLE projectile. Multi-projectile volleys hit individually.
   *
   *     principal = (base damage + equipment base attack) x (1 + percentages)
   *     per shot  = principal + equipment bonus attack
   *
   * The bonus lands here rather than earlier, which is the whole of step 4.
   * Everything downstream reads THIS function - a pet's fraction of a ship
   * shot, the Overview's DPS, the number a hit prints - so the bonus is applied
   * exactly once wherever a ship projectile's damage is the input, and never
   * twice.
   */
  totalDamagePerProjectile(): number {
    const principal = this.baseDamage() * (1 + this.damageBonusPercent() / 100);
    return principal + this.bonusAttackPerProjectile();
  }

  /**
   * Corações Primordiais incorporados na nave desta corrida, em basis points.
   *
   * Zero para toda a sessão criada antes do gate V28F.B, e a ausência é exata:
   * essas corridas foram jogadas sem corações nenhuns.
   */
  private primordialBps(): bigint {
    return primordialHeartBps(this.config.primordialHeartCount ?? 0n);
  }

  /**
   * As percentagens SOMADAS que este gate acrescenta ao tiro normal.
   *
   * Os corações e as joias, numa parcela única. Uma soma e não um segundo
   * `shotDamageMilli` encadeado: `base x (1 + p + k)` e nunca
   * `base x (1 + p) x (1 + k)` - o que impede um coração de multiplicar uma
   * joia e uma joia de multiplicar um coração, que é a lei aditiva do contrato
   * V28D.2.
   *
   * `resolveJewelEffects` é quem sabe o que cada joia vale. Este método soma um
   * `bigint` e não conhece nenhuma delas pelo nome - a mesma disciplina que o
   * duelo segue, na mesma função partilhada.
   *
   * Vazio para toda a corrida sem joias, que é o estado de todas as contas de
   * hoje: `resolveJewelEffects` devolve zero e a soma fica exatamente onde
   * estava.
   */
  private summedDamageBps(): bigint {
    return (
      this.primordialBps() +
      resolveJewelEffects(this.config.jewels ?? EMPTY_JEWEL_SOCKET_PROFILE).damageBps
    );
  }

  /**
   * **O dano de UM projétil da nave, em MILLI e exato.**
   *
   * A mesma ordem canónica que {@link totalDamagePerProjectile} descreve, na
   * unidade em que a arena aplica dano desde o gate V28F.B:
   *
   *     (base + ataque-base do equipamento) x (10 000 + percentagens somadas)
   *       / 10 000  +  ataque-bónus do equipamento
   *
   * ## Porque teve de deixar de ser um `number`
   *
   * Porque uma das parcelas somadas deixou de ter tecto. Cada Coração
   * Primordial incorporado vale +100 bps e a contagem vive em `numeric(40, 0)`,
   * portanto o produto tem mais dígitos do que um `double` guarda - e o instante
   * em que ele fosse convertido seria o instante em que o jogo capou
   * silenciosamente o efeito de um item.
   *
   * ## As percentagens são SOMADAS, e os corações são uma delas
   *
   * A nave, os artefactos, o equipamento e os corações entram todos em
   * `shotDamageMilli` como uma parcela única, aplicada uma vez. `+900` ao lado
   * de `+2 500` é `+3 400`, nunca `1,09 x 1,25` - que é o que impede um coração
   * de multiplicar uma joia futura e uma joia futura de multiplicar um coração.
   *
   * ## O ataque-bónus fica FORA da percentagem
   *
   * Passo 4, como sempre foi: somado depois, nunca multiplicado por ela e nunca
   * parte da base que ela multiplica. É isso que o torna igual para um
   * principiante e para alguém a carregar +900 %.
   */
  totalDamagePerProjectileMilli(): bigint {
    /*
     * O que a corrida já valia, na sua própria aritmética, e SEM corações.
     *
     * Reutilizado em vez de re-derivado: as percentagens da nave, dos
     * artefactos e do equipamento continuam a ser somadas exatamente onde
     * sempre foram, por `damageBonusPercent`. Reescrevê-las aqui em basis
     * points seria mudar o dano de toda a gente numa alteração que só devia
     * acrescentar corações - e seria uma segunda definição de "quanto faz um
     * tiro", que é precisamente o que `hunt/passive-attributes.ts` proíbe.
     */
    const bonusMilli = BigInt(Math.round(this.bonusAttackPerProjectile() * DAMAGE_MILLI_PER_UNIT));
    const withoutHeartsMilli = BigInt(
      Math.round(this.totalDamagePerProjectile() * DAMAGE_MILLI_PER_UNIT),
    );
    /*
     * Os corações multiplicam o PRINCIPAL, e nunca o ataque-bónus.
     *
     * O ataque-bónus é o passo 4 da ordem canónica: somado depois, nunca dentro
     * da percentagem. Subtraí-lo antes e voltar a somá-lo depois é o que mantém
     * essa lei - e com zero corações a subtração e a soma cancelam-se
     * exatamente, o que faz uma corrida sem corações valer o que sempre valeu.
     */
    const principalMilli = withoutHeartsMilli - bonusMilli;
    return shotDamageMilli(principalMilli, this.summedDamageBps()) + bonusMilli;
  }

  /**
   * Base attack speed: points plus the artifacts' FLAT contribution.
   *
   * Step 1 of the authoritative order - a base increase, added before any
   * percentage. Uncapped by design: Speed Fast at level 10 alone is +30.72
   * attacks per second, and the game answers that with more discrete
   * projectiles, never with a beam.
   */
  baseAttacksPerSecond(): number {
    return (
      effectiveBaseAttackSpeed(this.progression.attackSpeedPoints) +
      passiveBaseAttackSpeed(this.passives) +
      equipmentBaseAttackSpeed(this.equipment)
    );
  }

  /**
   * Step 2 for attack speed: the summed percentage, applied once.
   *
   * Only equipment produces one today. It multiplies the base AFTER every flat
   * contribution has been added to it, which is the same rule damage and health
   * already follow - and the reason no percentage is ever applied twice.
   */
  attackSpeedBonusPercent(): number {
    return equipmentAttackSpeedPercent(this.equipment) + this.jewelAttackSpeedPercent();
  }

  /**
   * A percentagem de cadência que as joias somam a este passo (gate V28G.D).
   *
   * ## Somada, e nunca multiplicada em separado
   *
   * O valor entra em {@link attackSpeedBonusPercent}, que
   * {@link totalAttacksPerSecond} aplica UMA vez sobre a base já somada. Uma
   * joia de `+9 %` ao lado de um equipamento de `+25 %` dá `x1,34`, e nunca
   * `x1,09 x 1,25 = x1,3625` - a diferença é de 1,68 % ao nível 30 e de 7,54 %
   * com seis cópias, e é a mesma lei aditiva do contrato V28D.2 que já governa
   * o dano.
   *
   * ## Porque divide por 100
   *
   * Porque este passo do cálculo é em PERCENTAGEM e o resolvedor devolve basis
   * points. `900` bps são `9` por cento, e a divisão é exata para qualquer bps
   * inteiro - não há resíduo a perder aqui, ao contrário do intervalo de
   * milli-tiques do duelo.
   *
   * Zero para toda a caçada sem esta joia, e nesse caso a soma acima devolve
   * exatamente o que devolvia: a mesma sequência temporal, tique a tique.
   */
  private jewelAttackSpeedPercent(): number {
    const { attackSpeedBps } = resolveJewelEffects(
      this.config.jewels ?? EMPTY_JEWEL_SOCKET_PROFILE,
    );
    return Number(attackSpeedBps) / 100;
  }

  /**
   * O coeficiente de cura desta corrida, do perfil CONGELADO (gate V28G.I).
   *
   * Zero para toda a caçada sem esta joia, e nesse caso `applyHit` sai pelo
   * mesmo ramo que sempre teve: nem o resto se mexe, nem a vida.
   *
   * Lido do mesmo `config.jewels` que o dano e a cadência já leem, portanto
   * congela quando a sessão congela e reconfigura quando uma queda ou uma fusão
   * reconfigura a MESMA sessão - a fronteira temporal do V28G.A, sem exceção
   * para esta joia.
   */
  private jewelLifestealBps(): bigint {
    return resolveJewelEffects(this.config.jewels ?? EMPTY_JEWEL_SOCKET_PROFILE).lifestealBps;
  }

  /**
   * O multiplicador do dano hostil que esta corrida recebe (gate V28G.Q).
   *
   * `10 000` para toda a caçada sem esta joia, e nesse caso
   * {@link reduceHostileDamage} devolve o `number` de entrada intacto - a mesma
   * fuga, o mesmo valor, o mesmo evento.
   *
   * Lido do mesmo `config.jewels` que o dano, a cadência e a cura já leem,
   * portanto congela quando a sessão congela e reconfigura quando uma queda ou
   * uma fusão reconfigura a MESMA sessão. A fronteira temporal do V28G.A, sem
   * exceção para esta joia: uma fuga que aconteceu antes da reconfiguração foi
   * cobrada ao perfil que estava em vigor.
   */
  /**
   * **A velocidade de movimento desta corrida, em basis points** (gate V28G.T).
   *
   * ## O agrupamento percentual, e é este
   *
   * {@link MOVE_SPEED_BPS_ONE} é a nave da caça como ela sempre andou, e as
   * percentagens de movimento são PARCELAS somadas a esse número antes de uma
   * única aplicação. Hoje há uma parcela e amanhã pode haver três; a lei que as
   * soma é a mesma que o duelo atravessa - `hull/move-speed.ts` - e não uma
   * segunda expressão dela escrita aqui.
   *
   * `10 000` para toda a caçada sem esta joia, e nesse caso
   * {@link HuntSimulation.driftTimeMs} devolve o relógio da simulação intacto:
   * a nave vagueia exatamente pelo caminho, à velocidade e nos instantes em que
   * sempre vagueou.
   *
   * Lido do mesmo `config.jewels` que o dano, a cadência, a cura e a redução já
   * leem, portanto congela quando a sessão congela e reconfigura quando uma queda
   * ou uma fusão reconfigura a MESMA sessão. A fronteira temporal do V28G.A, sem
   * exceção para esta joia.
   */
  moveSpeedBps(): number {
    return composeMoveSpeedBps(
      MOVE_SPEED_BPS_ONE,
      resolveJewelEffects(this.config.jewels ?? EMPTY_JEWEL_SOCKET_PROFILE).moveSpeedBps,
    );
  }

  private hostileDamageMultiplierBps(): bigint {
    return hostileDamageMultiplierBps(
      resolveJewelEffects(this.config.jewels ?? EMPTY_JEWEL_SOCKET_PROFILE).damageReductionBps,
    );
  }

  /**
   * **A cadência que esta conta PEDE**, inteira e sem teto nenhum.
   *
   * Continua a ser o que o equipamento, os pontos, os artefactos e as joias
   * compõem: o atributo não é dividido nem escondido, e é este o número que a
   * interface mostra ao lado do efectivo.
   */
  totalAttacksPerSecond(): number {
    const boost = this.config.devAttackSpeedBoost ?? 1;
    return this.baseAttacksPerSecond() * (1 + this.attackSpeedBonusPercent() / 100) * boost;
  }

  /**
   * **A cadência que a CAÇA deixa emitir** (gate V28I.CF.3): `min(pedida, 20)`.
   *
   * A mesma lei do mundo aberto, do mesmo sítio neutro, porque é a mesma decisão
   * do dono para os dois modos do campo. Uma nave abaixo do teto não muda nada.
   */
  emittedAttacksPerSecond(): number {
    return fieldEmittedVolleysPerSecond(this.totalAttacksPerSecond());
  }

  /** Se o teto da caça está a morder a cadência desta conta. Só para a leitura. */
  cadenceIsLimited(): boolean {
    return fieldCadenceIsLimited(this.totalAttacksPerSecond());
  }

  /**
   * Passo 1: toda a vida PLANA - pontos, artefactos e equipamento.
   *
   * Delegada a `accountFlatMaxHealth`, que é a mesma parcela que a autoridade
   * soma ao emitir um bilhete de duelo (gate V28G.S). Não há aqui uma soma
   * escrita a repetir a de lá.
   */
  baseHealthValue(): number {
    return accountFlatMaxHealth(this.hullSources());
  }

  /**
   * **A soma de TODAS as percentagens de vida máxima, exata, em basis points.**
   *
   * Os artefactos, o equipamento e o Coração do Titã, somados - e aplicados uma
   * única vez por {@link maxHealth}. Nunca multiplicados em sequência: `+60 %` de
   * uma joia ao lado de `+20 %` de um artefacto são `+80 %`, e nunca
   * `1,60 x 1,20`.
   *
   * Em `bigint` porque é a unidade em que as três fontes já vivem, e porque um
   * `number` no meio de uma soma de percentagens sem tecto seria um sítio onde
   * uma delas perderia dígitos em silêncio.
   */
  maxHealthBonusBps(): bigint {
    return accountMaxHealthBps(this.hullSources());
  }

  /**
   * Summed maximum-health percentage from artifacts, equipment and jewels
   * (step 2), as a percentage.
   *
   * Derivada de {@link maxHealthBonusBps} e não somada outra vez: a Visão Geral
   * imprime este número, e um casco que fosse formado por uma soma e mostrado
   * por outra teria duas verdades.
   */
  healthBonusPercent(): number {
    return Number(this.maxHealthBonusBps()) / 100;
  }

  /**
   * O teto deste casco, pela composição CANÓNICA (gate V28G.R).
   *
   * Toda a vida plana primeiro - a dos pontos, a dos artefactos, a do
   * equipamento - e só depois a soma das percentagens, aplicada uma vez. A
   * mesma função que o duelo usa, na unidade fracionária da caça.
   *
   * Substituiu `base * (1 + percent / 100)`. As duas dão o mesmo número, e só
   * esta o dá pela mesma aritmética que o duelo usa - que é a condição do gate
   * para PvP e caça terem uma composição só.
   */
  maxHealth(): number {
    return accountMaxHealth(this.hullSources());
  }

  /**
   * **O raio do casco desta caça, em unidades da arena.**
   *
   * {@link HUNT_SHIP_BASE_RADIUS}, sempre, e é uma decisão de produto e não uma
   * omissão (gate V28G.R).
   *
   * ## A geometria da caça NÃO depende da vida máxima
   *
   * O duelo dimensiona um casco pela vida máxima final, porque lá dois cascos
   * partilham uma arena e o tamanho é a contrapartida visível da resistência.
   * A caça não: a nave é uma só, a arena é fixa em 1280x720, e a progressão de
   * vida é ilimitada. Medido antes de ser decidido: com a curva do duelo, uma
   * conta com 1 000 pontos de vida punha as bocas dos canhões em (1324, -207) -
   * fora da arena - e a nave deixava de matar. Não era um problema de tamanho;
   * era a caça a parar de funcionar.
   *
   * Então o Coração do Titã aumenta a vida máxima da caça em até 60 % e **não
   * toca em nada geométrico**: o sprite, a hitbox, os hardpoints, a posição dos
   * canhões, a órbita e o raio das serras e as colisões ficam exatamente onde
   * estavam. `hunt-hull-geometry.test.ts` afirma-o medindo, e não em prosa.
   *
   * É contra ele que a órbita e o tamanho de uma lâmina são medidos, exatamente
   * como o duelo mede os seus contra o raio do casco de lá - é isso que faz as
   * serras terem a mesma PROPORÇÃO em relação à nave nos dois modos, apesar de
   * as duas arenas terem escalas completamente diferentes.
   */
  hullRadius(): number {
    return HUNT_SHIP_BASE_RADIUS;
  }

  /** Health restored per second by The Cure, in whole health units. */
  regenPerSecond(): number {
    return passiveRegenPerSecond(this.passives);
  }

  projectilesPerVolley(): number {
    return (
      cannonCountForLevel(this.config.cannonLevel) *
      CANNON_CATALOG[this.config.cannonRarity].projectilesPerCannon
    );
  }

  /**
   * Where the ship is, this tick.
   *
   * ## Derived, not stored
   *
   * A function of `simTimeMs` and the run's seed, and of nothing else. That is
   * what lets the ship move without a single field entering the snapshot: the
   * server re-simulating a window arrives at the same tick with the same seed
   * and therefore at the same position, so the two sides cannot disagree about
   * something neither of them recorded. A stored position would have to be
   * versioned, restored and defaulted for every run written before today.
   *
   * ## Rounded, because `Math.sin` is implementation-defined
   *
   * Node and Chromium may differ in the last bit of a sine, exactly as this
   * file already accepts for `Math.atan2`. The position feeds the facing and
   * the muzzles, so a last-bit difference could in principle decide a
   * knife-edge arrival one tick either way. Quantising to four decimals - the
   * same precision the digest compares floats at - erases the difference
   * before it can travel anywhere.
   */
  shipPosition(): { x: number; y: number } {
    const { width, height, shipCenterYFraction } = HUNT_BALANCE.arena;
    const drift = HUNT_BALANCE.ship.drift;
    const [acrossSlow, acrossFast, riseSlow, riseFast] = drift.periodsMs;
    const [phase0 = 0, phase1 = 0, phase2 = 0, phase3 = 0] = this.driftPhases;
    const driftMs = this.driftTimeMs();

    const wave = (periodMs: number, phase: number): number =>
      Math.sin((driftMs / periodMs) * Math.PI * 2 + phase);

    const across =
      wave(acrossSlow ?? 1, phase0) * drift.blend +
      wave(acrossFast ?? 1, phase1) * (1 - drift.blend);
    // 0..1 rather than -1..1: the ship rises from its resting line and never
    // sinks below it. See the note on `drift` in the balance table.
    const rise =
      (wave(riseSlow ?? 1, phase2) * drift.blend +
        wave(riseFast ?? 1, phase3) * (1 - drift.blend) +
        1) /
      2;

    const quantise = (value: number): number => Math.round(value * 10_000) / 10_000;
    return {
      x: quantise(width / 2 + across * width * drift.spanXFraction),
      y: quantise(height * shipCenterYFraction - rise * height * drift.riseYFraction),
    };
  }

  /**
   * **O relógio por que a nave vagueia**, que é onde a velocidade entra
   * (gate V28G.T).
   *
   * ## Porque o tempo, e não uma velocidade somada a uma posição
   *
   * Porque a nave da caça não tem velocidade: {@link HuntSimulation.shipPosition}
   * é uma FUNÇÃO do tique e da semente, e é isso que permite ao servidor
   * re-simular uma janela e chegar à mesma posição sem que nada disso entre na
   * instantânea. Uma velocidade integrada teria de ser estado - uma posição, um
   * resto - versionada, restaurada e errada para toda a corrida escrita antes de
   * existir.
   *
   * Acelerar o RELÓGIO da função dá exatamente o que a joia promete e não
   * acrescenta campo nenhum. Para `x(t) = A sin(2 pi t / T)`, a posição em
   * `k t` tem velocidade `k x'(k t)`: a nave percorre o MESMO caminho, ponto a
   * ponto, e cobre-o `k` vezes mais depressa. É a definição de velocidade de
   * movimento, e não uma aproximação dela.
   *
   * ## Porque não acumula erro
   *
   * Porque não há acumulação nenhuma. Isto é uma multiplicação sobre
   * `simTimeMs`, avaliada de novo a cada leitura a partir do relógio
   * autoritativo, e não um incremento somado tique a tique. Ao fim de seis horas
   * o valor é o mesmo que uma única multiplicação daria - porque é uma única
   * multiplicação.
   *
   * ## A identidade, e porque ela é estrutural
   *
   * Sem a joia, {@link HuntSimulation.moveSpeedBps} devolve
   * {@link MOVE_SPEED_BPS_ONE} e esta função devolve `this.simTimeMs` pelo mesmo
   * `number`, sem multiplicar e sem dividir. Uma corrida sem esta passiva vagueia
   * pelos mesmos instantes que vagueava, ao bit - o que também mantém o digest de
   * cada checkpoint exatamente onde estava.
   *
   * ## O que ele NÃO acelera
   *
   * Nada além do vaguear. Não é o relógio da simulação: `simTimeMs` continua a
   * crescer `HUNT_TICK_MS` por tique e é ele que decide cadência, projéteis,
   * spawns, boss, DoT, serras, cura e recompensa. Este número é lido por
   * {@link HuntSimulation.shipPosition} e por mais nada.
   */
  private driftTimeMs(): number {
    const bps = this.moveSpeedBps();
    /* O tempo que a carga prendeu não conta para a deriva (gate V28H.N). */
    const moving = this.simTimeMs - this.driftFrozenMs;
    if (bps === MOVE_SPEED_BPS_ONE) {
      return moving;
    }
    return (moving * bps) / MOVE_SPEED_BPS_ONE;
  }

  /* ------------------------------------------------------------------ */
  /*  Aim                                                               */
  /* ------------------------------------------------------------------ */

  /**
   * Direction the nose is pointing, in radians (`-PI/2` is straight up).
   *
   * Living in the simulation rather than the renderer means the drawn nose and
   * the position a shot is actually born at can never disagree, and the turn
   * stays deterministic and frame-rate independent. That mattered when the
   * ship was fixed and matters more now that it wanders: the muzzles move both
   * because the hull moved and because the nose turned, and one source for
   * both is what keeps them together.
   *
   * It may point sideways or downwards when the target has already slipped
   * past the ship - the nose follows where the shot really goes.
   */
  shipFacing(): number {
    return this.facing;
  }

  /** Angle the nose is turning toward: the target, or straight up if none. */
  desiredFacing(): number {
    const target = this.findEntity(this.currentTargetId() ?? -1);
    if (!target) {
      return HUNT_BALANCE.ship.restingFacingRad;
    }
    const ship = this.shipPosition();
    return Math.atan2(target.y - ship.y, target.x - ship.x);
  }

  /** Ship-local slots of the equipped cannon level. */
  cannonSlots(): readonly CannonSlot[] {
    return cannonFormation(this.config.cannonLevel);
  }

  /**
   * World position of every cannon body, in formation order.
   *
   * The renderer draws one sprite per entry; the simulation fires one volley
   * per entry. Same source, so they cannot drift apart.
   */
  cannonPositions(): readonly { x: number; y: number }[] {
    const ship = this.shipPosition();
    return this.cannonSlots().map((slot) => slotToWorld(slot, ship.x, ship.y, this.facing));
  }

  /** World position of every muzzle, aligned one-to-one with the cannons. */
  muzzlePositions(): readonly { x: number; y: number }[] {
    const ship = this.shipPosition();
    return this.cannonSlots().map((slot) =>
      slotToWorld(slotMuzzle(slot), ship.x, ship.y, this.facing),
    );
  }

  /**
   * Alguma carta está a prender o movimento neste instante?
   *
   * A pergunta é da TABELA e não do id: `chargeLocksMovement` é onde a
   * decisão aprovada está escrita, e só a Lança Solar a tem. Uma segunda carta
   * que a ganhe não precisa de tocar aqui.
   */
  private chargeLocksMovement(): boolean {
    return this.cards.some(
      (card) =>
        card.chargeRemainingMs > 0 &&
        (cardBalance(card.definitionId)?.chargeLocksMovement ?? false),
    );
  }

  private tickFacing(dt: number): void {
    this.facing = stepFacing(this.facing, this.desiredFacing(), dt);
  }

  /* ------------------------------------------------------------------ */
  /*  Time                                                              */
  /* ------------------------------------------------------------------ */

  /**
   * Feeds wall-clock time into the fixed-step loop.
   *
   * The delta is clamped so a backgrounded tab cannot fast-forward the hunt
   * and mint rewards for time that was never simulated on screen.
   */
  advance(deltaMs: number): void {
    if (!Number.isFinite(deltaMs) || deltaMs < 0) {
      return;
    }
    this.pendingMs += Math.min(Math.round(deltaMs), HUNT_BALANCE.simulation.maxFrameDeltaMs);
    while (this.pendingMs >= HUNT_TICK_MS) {
      this.pendingMs -= HUNT_TICK_MS;
      if (this.phase === 'active') {
        this.tick();
      }
    }
  }

  now(): number {
    return this.simTimeMs;
  }

  /**
   * **O tique da simulação, como índice inteiro** (gate V28G.I).
   *
   * `simTimeMs` cresce exatamente `HUNT_TICK_MS` por tique, de zero, e a
   * instantânea guarda-o tal como está - portanto o quociente é um índice único
   * e monotónico por corrida, que sobrevive a um F5 pelo mesmo caminho que o
   * relógio.
   *
   * Existe para os eventos de apresentação dizerem QUANDO aconteceram. Não é
   * usado por nada mecânico: o relógio da simulação continua a ser `simTimeMs`,
   * e derivar um índice dele não cria uma segunda fonte de verdade sobre o
   * tempo - cria uma leitura dela.
   *
   * `Math.round` e não uma divisão crua: `simTimeMs` é sempre um múltiplo do
   * tique por construção, e arredondar é o que impede um float que passasse por
   * aqui de produzir um índice fracionário que o decodificador recusaria.
   */
  private currentTick(): number {
    return Math.round(this.simTimeMs / HUNT_TICK_MS);
  }

  private tick(): void {
    this.simTimeMs += HUNT_TICK_MS;
    const dt = HUNT_TICK_MS / 1000;

    /*
     * A deriva congela ANTES de tudo o que lê a posição da nave neste tique: a
     * nave tem de estar parada para os tiros, o feixe e as colisões deste tique,
     * e não a partir do tique seguinte.
     */
    if (this.chargeLocksMovement()) {
      this.driftFrozenMs += HUNT_TICK_MS;
    }

    this.tickBossTimer();
    this.tickSpawning();
    this.tickMovement(dt);
    if ((this.phase as HuntPhase) === 'dead') {
      return; // a breach this tick ended the hunt
    }
    // Healing AFTER the leaks of this tick: a mob that just breached did its
    // damage, and The Cure works against it rather than pre-empting it.
    this.tickRegen();
    // Aim before firing so a volley always leaves the muzzle as drawn.
    this.tickFacing(dt);
    this.tickFiring();
    /*
     * **As cartas, logo a seguir ao casco** (gate V28H.H).
     *
     * Aqui e não antes: os Super Canhões são armamento da nave, e a salva deles
     * sai do mesmo tique e contra o mesmo alvo que a salva do casco. Antes de
     * `tickFiring` a habilidade dispararia contra um alvo que a mira ainda não
     * tinha escolhido neste tique.
     *
     * E antes do pet, porque o pet é a entidade mais externa: a ordem tem de
     * ser fixa para a re-simulação do servidor chegar ao mesmo estado, e esta é
     * casco -> cartas -> pet, de dentro para fora.
     */
    this.tickCards();
    // The pet flies and fires AFTER the ship, on the same tick and against the
    // same target rule. Its bullets join the same projectile list, so one
    // `tickProjectiles` moves both and one `applyHit` resolves both - which is
    // what makes a kill impossible to reward twice, whoever landed it.
    this.tickPet(dt);
    /*
     * **As invocações, depois do pet e antes dos projéteis** (gate V28H.M).
     *
     * Depois do pet porque as duas coisas são escoltas e a ordem entre elas tem
     * de ser fixa; antes dos projéteis porque uma salva nascida neste tique tem
     * de andar neste tique, como a da nave anda.
     */
    this.tickSummons();
    /*
     * **E as bombas de carta** (gate V28H.T), ao lado dos projéteis.
     *
     * Antes deles pela mesma razão que as invocações: uma bomba lançada neste
     * tique tem de andar neste tique. E antes dos mísseis, para um mob que ela
     * mate já estar morto quando eles procuram alvo - a ordem que a linha
     * abaixo já explica.
     */
    this.tickCardBlasts(dt);
    this.tickProjectiles(dt);
    /*
     * Os mísseis DEPOIS dos projéteis, e a ordem é uma regra.
     *
     * Um mob que morre de um tiro deste tique já está morto quando os mísseis o
     * procuram: o retarget encontra a morte no tique em que ela aconteceu, e não
     * um tique atrasado. E um míssil lançado nesta salva já viaja neste tique,
     * exatamente como o projétil que saiu ao lado dele.
     */
    this.tickMissiles(dt);
    /*
     * **AS SERRAS, depois de tudo o que dispara e antes do veneno** (V28G.P).
     *
     * A mesma linha que o duelo escolheu, e pelas mesmas três razões: a órbita é
     * desenhada à volta da posição final da nave neste tique, um contacto cobra
     * sobre a vida que os tiros e as explosões já reduziram, e um stack aplicado
     * por uma lâmina não deve cobrar a primeira parcela no instante em que
     * nasceu.
     */
    this.tickSaws();
    /*
     * **O veneno DEPOIS de tudo o que dispara, e a ordem é uma regra.**
     *
     * As parcelas deste tique cobram sobre a vida que os tiros, o pet, as
     * explosões e os mísseis deste tique já reduziram - portanto um mob que
     * morre de um tiro não paga a parcela que lhe caía no mesmo instante, e um
     * que sobrevive paga-a. Pô-las antes faria o veneno decidir a morte com uma
     * vida que a salva ainda ia tirar.
     */
    this.tickPoison();
  }

  /* ------------------------------------------------------------------ */
  /*  Pet                                                               */
  /* ------------------------------------------------------------------ */

  /**
   * Flies the pet and fires its machine guns.
   *
   * Deliberately parallel to the ship's own targeting and firing, and
   * deliberately separate from it: the pet has its own accumulator, so the
   * ship's cadence is untouched, and its own muzzles, so its bullets are born
   * where the model's guns actually are.
   *
   * The pet is not a combatant in the defensive sense - it has no health,
   * cannot be hit and blocks nothing - so there is no damage path to it here.
   */
  private tickPet(dt: number): void {
    const pet = this.pet;
    if (!pet) {
      return;
    }
    pet.driftSeconds += dt;
    const home = this.petHome(pet.driftSeconds);
    pet.x = home.x;
    pet.y = home.y;

    const targetId = this.currentTargetId();
    const target = targetId === null ? null : this.findEntity(targetId);
    // Turns quickly toward the target, and rests pointing up when there is
    // none - the same rule the ship's nose follows.
    const profile = petCombatProfile(pet.definitionId);
    if (profile.beam) {
      // A beam pet aims at its LOCK, not at the field's current best enemy:
      // the body has to keep pointing where the laser is actually going.
      this.tickPetBeam(pet, dt, profile.beam);
      return;
    }

    const desired = target
      ? Math.atan2(target.y - pet.y, target.x - pet.x)
      : HUNT_BALANCE.ship.restingFacingRad;
    pet.facing = stepFacing(pet.facing, desired, PET_BALANCE.flight.turnRadPerSecond * dt);

    /*
     * Cadence is the PET's own, and the training APPLIES.
     *
     * For a long time this tick used the flat base rate while the Overview
     * reported `petBulletsPerSecond`, which includes the food bonus - so the
     * game showed a cadence it did not deliver. Both now read the same
     * function, which is the only arrangement in which the two cannot drift.
     *
     * The counter is the one FROZEN on the entity, so a checkpoint
     * re-simulating a window uses the training the pet had during that window
     * rather than whatever it has been fed since. Applied once, here: nothing
     * downstream multiplies it again.
     */
    const periodMs = 1000 / petVolleysPerSecond(pet.definitionId, pet.attackSpeedFoodCount);
    pet.attackAccumulatorMs += HUNT_TICK_MS;
    if (target === null) {
      // Bank at most one ready volley, never a burst - identical to the ship.
      pet.attackAccumulatorMs = Math.min(pet.attackAccumulatorMs, periodMs);
      return;
    }
    while (pet.attackAccumulatorMs >= periodMs) {
      pet.attackAccumulatorMs -= periodMs;
      this.firePetVolley(pet, target.id);
    }
  }

  /**
   * The CONTINUOUS BEAM, one tick of it.
   *
   * The Void Butterfly's weapon, and the only beam in the game. It is
   * authoritative simulation state from end to end: the lock, the instance
   * accumulator and the re-acquisition timer all live on the entity and all go
   * into the snapshot, so the server's re-simulation, a reload and a checkpoint
   * produce exactly the same damage at exactly the same moments.
   *
   * ## What the beam does, stated as the rules it must obey
   *
   * - ONE enemy. It never pierces, never chains, never splits and never damages
   *   a bystander. There is no blast and no second beam in any form.
   * - It STAYS on that enemy while the enemy lives, rather than re-picking the
   *   best target every tick. That is what makes it a lock instead of a
   *   flickering pointer.
   * - The boss overrides the lock the moment it becomes attackable. The prompt
   *   states it absolutely - "durante o boss, o laser sempre ataca o boss" - and
   *   that is a superset of the ship's lowest-enemy rule rather than a
   *   contradiction of it: the boss is usually the lowest thing on the field
   *   anyway, and when it is not, the player wants the beam on the boss.
   * - When the target dies or leaves, the beam goes OFF immediately, the banked
   *   fraction of an instance is discarded, and nothing may be acquired for
   *   `reacquireDelayMs`. A beam with no target applies no damage and produces
   *   no events - there are no invisible ticks.
   */
  private tickPetBeam(pet: PetEntity, dt: number, beam: PetBeamProfile): void {
    pet.beamReacquireMs = Math.max(0, pet.beamReacquireMs - HUNT_TICK_MS);

    /*
     * Is the lock still worth having?
     *
     * `findEntity` returns null for anything that has died or left the field,
     * which covers both "the target is gone" cases in one check. A revealing
     * boss is not attackable yet, so it is refused here exactly as the ship's
     * own targeting refuses it.
     */
    let target = pet.beamTargetId === null ? null : this.findEntity(pet.beamTargetId);
    if (target && target.kind === 'boss' && target.phase !== 'descending') {
      target = null;
    }
    if (target === null && pet.beamTargetId !== null) {
      this.dropBeamLock(pet, beam);
    }

    /*
     * The boss takes precedence over a living lock.
     *
     * No re-acquisition delay: the old target did not die, so there is no
     * corpse-strobe to suppress and no reason to give the player a tenth of a
     * second of darkness for switching to the thing they care about.
     */
    if (this.boss && this.boss.phase === 'descending' && pet.beamTargetId !== this.boss.id) {
      pet.beamTargetId = this.boss.id;
      target = this.boss;
    } else if (target === null && pet.beamReacquireMs <= 0) {
      const acquired = this.currentTargetId();
      if (acquired !== null) {
        pet.beamTargetId = acquired;
        target = this.findEntity(acquired);
      }
    }

    if (target === null) {
      // Off. Facing drifts back to rest, and nothing accumulates: a beam that
      // is not firing must not bank instances to release in a burst later.
      pet.facing = stepFacing(
        pet.facing,
        HUNT_BALANCE.ship.restingFacingRad,
        PET_BALANCE.flight.turnRadPerSecond * dt,
      );
      return;
    }

    pet.facing = stepFacing(
      pet.facing,
      Math.atan2(target.y - pet.y, target.x - pet.x),
      PET_BALANCE.flight.turnRadPerSecond * dt,
    );

    /*
     * Instances, at the form's own frequency.
     *
     * Same shape as the ship's firing loop and for the same reason: a tick is
     * 16 ms and a frequency of ten is a period of 100 ms, so most ticks apply
     * nothing and every sixth or seventh applies one. Chunking wall-clock time
     * differently cannot change how many land.
     */
    const periodMs =
      1000 / petBeamInstancesPerSecond(pet.definitionId, pet.evolution, pet.attackSpeedFoodCount);
    pet.beamAccumulatorMs += HUNT_TICK_MS;
    const damageMilli = petBulletDamageMilli(
      pet.definitionId,
      this.totalDamagePerProjectileMilli(),
      pet.damageFoodCount,
    );
    while (pet.beamAccumulatorMs >= periodMs) {
      pet.beamAccumulatorMs -= periodMs;
      /*
       * Re-read the target before EVERY instance.
       *
       * Two instances can fall in one tick at a high frequency, and the first
       * of them may be the one that kills. Without this check the second would
       * hit a corpse - damage credited to an enemy that is already dead, which
       * is exactly the kind of thing that survives a hundred ticks and then
       * diverges from the server's re-simulation.
       */
      const live = pet.beamTargetId === null ? null : this.findEntity(pet.beamTargetId);
      if (live === null) {
        this.dropBeamLock(pet, beam);
        return;
      }
      this.applyHit(live, damageMilli, 'pet');
    }
  }

  /** Turns the beam off and starts the darkness before it may lock again. */
  private dropBeamLock(pet: PetEntity, beam: PetBeamProfile): void {
    pet.beamTargetId = null;
    // Discarded, not banked. A beam that spent 90 ms of a 100 ms period on a
    // mob that died does not get to spend it on the next one.
    pet.beamAccumulatorMs = 0;
    pet.beamReacquireMs = beam.reacquireDelayMs;
  }

  /**
   * Where the beam is being drawn, for the renderer. Null when it is off.
   *
   * Derived from authoritative state rather than decided here: the lock is the
   * simulation's, and this only reports where that enemy currently stands so
   * the beam can end exactly on it. A pet with no beam always answers null.
   */
  getPetBeam(): { readonly targetId: number; readonly x: number; readonly y: number } | null {
    const pet = this.pet;
    if (!pet || pet.beamTargetId === null) {
      return null;
    }
    const target = this.findEntity(pet.beamTargetId);
    return target === null ? null : { targetId: target.id, x: target.x, y: target.y };
  }

  /**
   * Which enemies this volley's cannons shoot at.
   *
   * One target per cannon, DIFFERENT while there are enough enemies to go
   * round. That is the Drone's whole tactical identity: three cannons that all
   * shoot the same mob are three explosions on one corpse, and three that
   * spread out are three blasts in three places.
   *
   * ## Deterministic, and therefore reproducible
   *
   * The draw comes from `petTargetRng`, a simulation stream whose state is in
   * the snapshot. So the server's re-simulation, a reload, a second tab and a
   * checkpoint all pick the SAME enemies - and reloading the page cannot
   * re-roll a better spread. `Math.random()` appears nowhere.
   *
   * The candidate list is sorted by id before drawing. Mobs live in spawn
   * order and that order is already deterministic, but sorting makes the input
   * to the shuffle independent of how the array happens to have been filtered
   * by earlier kills - which is the kind of thing that survives a hundred
   * ticks and then diverges once.
   *
   * ## The boss is not a lottery
   *
   * When the boss is the target every cannon fires at the boss. There is
   * nothing to spread across, and a volley that split between the boss and a
   * stray mob would be strictly worse for the player.
   */
  private petVolleyTargets(
    count: number,
    primaryTargetId: number,
    mode: PetTargetingMode = 'spread',
  ): number[] {
    if (count <= 1 || mode === 'single') {
      return Array.from({ length: count }, () => primaryTargetId);
    }
    // Against the boss - or anything that is not a mob - every launcher agrees.
    if (this.boss && this.boss.id === primaryTargetId) {
      return Array.from({ length: count }, () => primaryTargetId);
    }

    if (mode === 'ranked') {
      /*
       * Gun N takes the Nth enemy, by the SHIP's own priority.
       *
       * The Apache Omega, and no randomness anywhere in it: enemies are ordered
       * by how close they are to the bottom line - highest y first, ties broken
       * by the lowest id, exactly as `currentTargetId` picks the ship's - and
       * the guns take the first, the second and the third. Fewer enemies than
       * guns and the spare ones double up on the FIRST, which is the one about
       * to reach the ship.
       *
       * The order is total and depends on nothing but the field, so two clients
       * and the server's re-simulation assign the same gun to the same enemy.
       */
      const ranked = this.mobs
        .map((mob) => ({ id: mob.id, y: mob.y }))
        .sort((left, right) => (right.y === left.y ? left.id - right.id : right.y - left.y));
      if (ranked.length === 0) {
        return Array.from({ length: count }, () => primaryTargetId);
      }
      return Array.from(
        { length: count },
        (_, index) => (ranked[index] ?? ranked[0])?.id ?? primaryTargetId,
      );
    }

    const candidates = this.mobs.map((mob) => mob.id).sort((left, right) => left - right);
    if (candidates.length <= 1) {
      return Array.from({ length: count }, () => primaryTargetId);
    }

    if (mode === 'independent') {
      /*
       * One draw per projectile, WITH replacement.
       *
       * Every missile chooses for itself, so three of them may land on one mob
       * or on three - A/A/A is as legitimate an outcome as A/B/C, and forcing a
       * spread would make this pet a copy of the Abyssal Drone with different
       * art. Each draw consumes exactly one value from the stream, in order, so
       * the sequence is reproducible whatever the wave looks like.
       */
      return Array.from(
        { length: count },
        () => candidates[this.petTargetRng.nextIntInclusive(0, candidates.length - 1)] as number,
      );
    }

    /*
     * Partial Fisher-Yates, drawing WITHOUT replacement.
     *
     * Only the first `count` positions are shuffled, so the number of draws
     * depends on how many cannons fire and not on how many mobs are alive -
     * which keeps the stream's consumption stable as a wave thins out.
     */
    const pool = candidates.slice();
    const picked: number[] = [];
    const draws = Math.min(count, pool.length);
    for (let slot = 0; slot < draws; slot += 1) {
      const index = slot + this.petTargetRng.nextIntInclusive(0, pool.length - 1 - slot);
      const chosen = pool[index] as number;
      pool[index] = pool[slot] as number;
      pool[slot] = chosen;
      picked.push(chosen);
    }
    /*
     * More cannons than enemies: the extra shots repeat, deterministically.
     *
     * Three cannons against two mobs is two distinct targets and one repeat -
     * and the repeat is the first pick rather than a fresh draw, so the same
     * situation always resolves the same way.
     */
    while (picked.length < count) {
      picked.push(picked[picked.length % draws] as number);
    }
    return picked;
  }

  /**
   * Where each gun of the current form sits, in arena coordinates.
   *
   * Read from `PET_MUZZLE_LAYOUT`, which states each pet's real barrel
   * positions per form, and placed in the pet's OWN frame: `lateral` across the
   * firing line, `forward` along it. So a pet that has turned to face a mob
   * still fires from its own barrels rather than from a fixed screen offset.
   *
   * This used to be a formula - an evenly spaced row 22 units apart on the
   * centre line - and it was wrong for every pet at once. The Ballistic Drone's
   * EV. III launchers sit 94 units out on the flanks and 14 forward; its
   * missiles were being born at 11 and 33 units with no forward offset at all,
   * so they left the middle of a body whose pods are at the wingtips. Nobody
   * noticed while the pets were small, and correcting the drone's declared
   * height doubled its drawn size and doubled the gap with it.
   *
   * Still entirely deterministic: a pure function of the definition, the form
   * and the pet's own position and facing, all of which are in the snapshot.
   */
  petMuzzlePositions(): readonly { x: number; y: number }[] {
    const pet = this.pet;
    if (!pet) {
      return [];
    }
    const dirX = Math.cos(pet.facing);
    const dirY = Math.sin(pet.facing);
    const perpX = -Math.sin(pet.facing);
    const perpY = Math.cos(pet.facing);
    return petMuzzleOffsets(pet.definitionId, pet.evolution).map((offset) => ({
      x: pet.x + perpX * offset.lateral + dirX * offset.forward,
      y: pet.y + perpY * offset.lateral + dirY * offset.forward,
    }));
  }

  private firePetVolley(pet: PetEntity, targetId: number): void {
    const dirX = Math.cos(pet.facing);
    const dirY = Math.sin(pet.facing);
    // The pet's damage is a fraction of the SHIP's per-projectile damage, so
    // it scales with allocated points, the equipped ship and every artifact
    // percentage automatically - and can never outgrow the ship it escorts.
    const damageMilli = petBulletDamageMilli(
      pet.definitionId,
      this.totalDamagePerProjectileMilli(),
      pet.damageFoodCount,
    );
    /*
     * The blast, decided at FIRE TIME and carried on the projectile.
     *
     * Not looked up at impact: a ball fired by an MK II that lands after the
     * pet evolved must still make an MK II blast, and the server's
     * re-simulation reads the same number off the same snapshot rather than
     * off whatever the pet is now.
     */
    const blastRadius = petBlastRadius(pet.definitionId, pet.evolution);
    const splashDamageMilli = petSplashDamageMilli(pet.definitionId, damageMilli);
    const profile = petCombatProfile(pet.definitionId);
    const missile = profile.missile;
    /*
     * A shot detonates when it has a blast OR when it is a missile.
     *
     * The two are independent: a ball explodes and hurts everything near it, a
     * missile explodes and hurts only what it hit. Both fly on to the last
     * place a dead target stood; only one of them does damage when it arrives.
     */
    const detonates = blastRadius > 0 || missile !== null;
    const origins = this.petMuzzlePositions();
    /*
     * One target per launcher, drawn the way this pet draws.
     *
     * Frozen into each projectile at birth: after this line the shot never
     * chooses again, and if its enemy dies it flies on to the last place that
     * enemy was and detonates there.
     */
    const targets = this.petVolleyTargets(origins.length, targetId, profile.targeting);
    /*
     * How many parallel rounds ONE gun releases.
     *
     * One for every pet but the Apache Omega, whose guns are the cannon 3
     * concept: three rounds side by side on the enemy that gun chose. They are
     * three SEPARATE projectiles with three separate damage instances and three
     * separate numbers - never one shot worth triple.
     */
    const perGun = petProjectilesPerGun(pet.definitionId);
    const burstSpacing = PET_BALANCE.bullet.spacing;
    const ids: number[] = [];
    for (const [index, origin] of origins.entries()) {
      const ownTargetId = targets[index] ?? targetId;
      const own = this.findEntity(ownTargetId);
      /*
       * Aimed at ITS OWN enemy, not at the pet's heading.
       *
       * With one shared target the body's facing was the launch direction for
       * every cannon; with three different ones it would send two of them the
       * wrong way and let the homing drag them back in an arc. Each ball now
       * leaves pointing at the enemy it was given.
       */
      let launchX = dirX;
      let launchY = dirY;
      if (own) {
        const toX = own.x - origin.x;
        const toY = own.y - origin.y;
        const length = Math.hypot(toX, toY);
        if (length > 0) {
          launchX = toX / length;
          launchY = toY / length;
        }
      }
      /*
       * The gun's BURST, spread across its own line of fire.
       *
       * Perpendicular to where this gun is shooting, so the rounds leave side
       * by side and PARALLEL rather than converging - the same arrangement the
       * ship's cannon 3 uses, and the homing bends each of them in afterwards.
       * `perGun` is one for every pet but the Apache, so this loop runs once
       * and the other four behave exactly as they did.
       */
      const burstX = -launchY;
      const burstY = launchX;
      for (let round = 0; round < perGun; round += 1) {
        const lane = (round - (perGun - 1) / 2) * burstSpacing;
        const bullet: ProjectileEntity = {
          id: this.nextEntityId++,
          x: origin.x + burstX * lane,
          y: origin.y + burstY * lane,
          dirX: launchX,
          dirY: launchY,
          damageMilli,
          /*
           * O pet NÃO seleciona, e é dito aqui em vez de herdado.
           *
           * A tabela fechada já recusaria a origem `pet` no seletor; escrever
           * `null` explicitamente é a segunda garantia, e a que impede a bala
           * do dragão de consumir a sequência da nave.
           */
          jewelProc: null,
          targetId: ownTargetId,
          orphaned: false,
          fromPet: true,
          ...(detonates
            ? {
                detonates: true,
                // Seeded with where the target is now, so a shot orphaned on
                // its very first tick still has somewhere to go.
                fuseX: own?.x ?? origin.x,
                fuseY: own?.y ?? origin.y,
              }
            : {}),
          ...(blastRadius > 0 ? { blastRadius, splashDamageMilli } : {}),
          ...(missile
            ? {
                /*
                 * The missile's own flight, frozen at launch.
                 *
                 * Carried on the projectile rather than looked up at each tick
                 * for the same reason its damage is: a missile fired by an
                 * EV. II that lands after the pet evolved must still fly like
                 * an EV. II, and the server reads the same numbers off the same
                 * snapshot.
                 */
                speed: missile.launchSpeed,
                maxSpeed: missile.maxSpeed,
                acceleration:
                  (missile.maxSpeed - missile.launchSpeed) /
                  Math.max(0.001, missile.accelerationSeconds),
                maxTurn: missile.maxTurnRadPerSecond,
                remainingMs: missile.maxLifeSeconds * 1000,
              }
            : {}),
        };
        this.projectiles.push(bullet);
        ids.push(bullet.id);
      }
    }
    this.events.push({ type: 'pet-volley', projectileIds: ids, origins, heading: pet.facing });
  }

  /** The active pet's live state, for the renderer. Null when none. */
  getPet(): PetEntity | null {
    return this.pet;
  }

  /**
   * Swaps the active pet on the run in flight.
   *
   * The same promise `applyLoadout` makes: this is not a new hunt. Mobs keep
   * their positions and health, the boss keeps its countdown, every RNG stream
   * carries on and bullets already in the air keep the damage they were born
   * with. Activating parks the new pet at its lane centre; deactivating simply
   * removes it, and its bullets finish their flight rather than vanishing.
   *
   * Evolving the ACTIVE pet keeps it active and keeps its drift and cadence -
   * only the form changes, which is what stops an evolution from reading as a
   * restart.
   */
  applyPet(
    pet: {
      definitionId: string;
      evolution: number;
      /** Permanent training. Absent means untrained, which is the default. */
      damageFoodCount?: number;
      attackSpeedFoodCount?: number;
    } | null,
  ): void {
    if (pet) {
      this.currentConfig = { ...this.currentConfig, pet };
    } else {
      // Deleted rather than set to `undefined`: the config type is exact, and
      // "no pet" is the absence of the key, not a key holding nothing.
      const { pet: _removed, ...rest } = this.currentConfig;
      this.currentConfig = rest;
      this.pet = null;
      return;
    }
    if (this.pet && this.pet.definitionId === pet.definitionId) {
      /*
       * Same pet, new form or new training.
       *
       * Everything that decides the FUTURE is kept - the drift phase, the
       * firing accumulator, the cumulative damage - so a pet fed mid-run does
       * not restart its cycle or lose what it has already dealt. Only the
       * numbers that apply from the next volley on are replaced.
       */
      this.pet = {
        ...this.pet,
        evolution: pet.evolution,
        damageFoodCount: pet.damageFoodCount ?? this.pet.damageFoodCount,
        attackSpeedFoodCount: pet.attackSpeedFoodCount ?? this.pet.attackSpeedFoodCount,
      };
      return;
    }
    this.pet = this.buildPet(pet);
  }

  /**
   * The Cure, one tick at a time.
   *
   * Only during an ACTIVE hunt, which is exactly the time the server
   * re-simulates - so it works identically during a tail settlement and a
   * resume, and contributes nothing to offline farm, which simulates nothing.
   *
   * It never exceeds maximum health, never revives a dead ship, and never
   * produces an item. The remainder below a whole health point is carried in
   * authoritative state rather than rounded away.
   */
  private tickRegen(): void {
    const perSecondMilli = Number(this.passives.effects.regenMilliPerSecond);
    if (perSecondMilli <= 0) {
      return;
    }
    const max = this.maxHealth();
    if (this.health >= max) {
      // Full: the carry is dropped rather than banked, so a long stretch at
      // full health cannot become a burst of healing later.
      this.regenCarryMilli = 0;
      return;
    }
    this.regenCarryMilli += (perSecondMilli * HUNT_TICK_MS) / 1000;
    const whole = Math.floor(this.regenCarryMilli / HEALTH_MILLI_PER_UNIT);
    if (whole <= 0) {
      return;
    }
    this.regenCarryMilli -= whole * HEALTH_MILLI_PER_UNIT;
    this.health = Math.min(max, this.health + whole);
  }

  /* ------------------------------------------------------------------ */
  /*  Boss                                                              */
  /* ------------------------------------------------------------------ */

  /**
   * How long until the boss arrives.
   *
   * The server's persisted delay wins over everything but the dev shortcut.
   * The simulation stream below is the LEGACY path: it is reached only by a
   * run with no encounter - a pure-domain test, or a session written before
   * encounters existed and not yet adopted. It survives because refusing those
   * would strand them, not because a public seed is an acceptable source for
   * something that decides Gold.
   */
  private rollBossDelayMs(): number {
    if (this.config.debugBossDelaySeconds !== undefined) {
      // DEV ONLY shortcut - the production rule below stays untouched.
      return Math.max(1, Math.round(this.config.debugBossDelaySeconds)) * 1000;
    }
    if (this.config.bossEncounter) {
      return this.config.bossEncounter.delayMs;
    }
    const seconds = this.bossTimerRng.nextIntInclusive(
      HUNT_BALANCE.boss.minDelaySeconds,
      HUNT_BALANCE.boss.maxDelaySeconds,
    );
    return seconds * 1000;
  }

  /**
   * The tier the bound encounter carries, or `null` when it is not revealed.
   *
   * Null only ever happens in a BROWSER: the server reads the tier from the
   * row before it builds anything. A run with no encounter at all - a
   * pure-domain test, or a session not yet adopted - answers with the shipped
   * default instead, because that is a run the server is not managing.
   */
  private encounterTier(): BossTier | null {
    if (this.bossEncounter === null) {
      return this.config.serverManagedBoss === true ? null : DEFAULT_BOSS_TIER;
    }
    return (this.bossEncounter.tier as BossTier | null) ?? null;
  }

  /**
   * Binds this run to a NEW encounter, from the settlement boundary onwards.
   *
   * The same shape as a drop reconfiguring a session in place: the wave, the
   * shots in flight and the accumulators are untouched, and only what governs
   * the next boss changes. Called by the server after it has drawn and written
   * the encounter - never by the simulation, which has no entropy it is
   * allowed to use for this.
   */
  applyBossEncounter(encounter: {
    encounterId: string;
    ordinal: number;
    tier: BossTier;
    delayMs: number;
  }): void {
    this.bossEncounter = {
      encounterId: encounter.encounterId,
      ordinal: encounter.ordinal,
      tier: encounter.tier,
      activated: false,
      // A brand new encounter is not a retry of anything.
      retrying: false,
    };
    this.bossCountdownMs = encounter.delayMs;
  }

  /** What the server needs to know about the bound encounter. */
  boundEncounter(): HuntSnapshotBossEncounter | null {
    return this.bossEncounter ? { ...this.bossEncounter } : null;
  }

  private tickBossTimer(): void {
    if (this.boss) {
      return;
    }
    if (this.bossEncounter === null && this.config.serverManagedBoss === true) {
      /*
       * Between encounters. A defeated boss leaves the run unbound until the
       * server draws the next one at the following settlement boundary, and
       * nothing may spawn in the meantime - the alternative would be a boss
       * with no persisted tier, which is to say a boss worth guessing at.
       *
       * Only for runs the server actually manages: a pure-domain simulation
       * has no server to draw for it and keeps re-arming from its own stream.
       */
      return;
    }
    this.bossCountdownMs -= HUNT_TICK_MS;
    if (this.bossCountdownMs <= 0) {
      this.spawnBoss();
    }
  }

  /**
   * The width the arena allows a boss, in arena units.
   *
   * A CONSTANT, computed from the canonical arena the simulation is defined in
   * - never a viewport, a browser width, a monitor or a zoom level. Two players
   * meeting the same encounter must see the same boss, and the same account
   * must see the same one on a phone and a monitor; the canvas scales this
   * arena to fit and changes nothing here.
   */
  private bossFitLimit(): number {
    return (
      Math.min(HUNT_BALANCE.arena.width, HUNT_BALANCE.arena.height) -
      2 * HUNT_BALANCE.boss.fitMargin
    );
  }

  /**
   * True when the countdown has run out and the boss cannot be drawn yet.
   *
   * Derived, never stored: it is exactly "no boss, nothing left to wait for,
   * and no tier to draw one with". A second piece of state would be a second
   * thing that could disagree with the first.
   *
   * Only a browser is ever in it, and only until the next checkpoint answers.
   */
  awaitingBossActivation(): boolean {
    return (
      this.boss === null &&
      this.bossEncounter !== null &&
      this.bossEncounter.tier === null &&
      this.bossCountdownMs <= 0
    );
  }

  /** Milliseconds left before the boss arrives, recovery included. */
  bossCountdownRemainingMs(): number {
    return Math.max(0, this.bossCountdownMs);
  }

  private spawnBoss(): void {
    const { fitMargin } = HUNT_BALANCE.boss;
    const tier = this.encounterTier();
    if (tier === null) {
      /*
       * The browser reaching the end of a countdown for a boss whose tier it
       * has not been told. It must NOT guess one: a guessed tier would draw
       * the wrong size, and the client deciding a boss had arrived is exactly
       * the authority this whole design moved to the server.
       *
       * So nothing spawns. The countdown is pinned at zero, the run keeps
       * playing, and the next checkpoint's answer brings the real boss.
       */
      this.bossCountdownMs = 0;
      return;
    }
    /*
     * Size from the TIER, through the one shared function. The old random
     * 2..13 is gone: it came from a stream whose seed is public and which a
     * death or an F5 re-mints, so a player could reload until the boss they
     * were about to fight looked good - harmless while size was cosmetic, and
     * not harmless now that it decides Gold and drop odds.
     */
    const size = cappedBossWidth(
      this.mobSize,
      mobScaleBpsForMap(this.config.mapIndex),
      tier,
      this.bossFitLimit(),
    );
    const scale = size / this.mobSize;
    /*
     * Health from a FIXED reference scale, so all six tiers fight identically.
     * A tier is size and reward; making the big one tough as well would turn a
     * lucky encounter into a punishing one, which is the opposite of the
     * design.
     */
    const maxHealth = bossHealth(this.mobMaxHealth, HUNT_BALANCE.boss.healthScale);
    const boss: BossEntity = {
      id: this.nextEntityId++,
      kind: 'boss',
      // Centred at the top, fully inside the arena: never laterally cut.
      x: HUNT_BALANCE.arena.width / 2,
      y: size / 2 + fitMargin,
      health: maxHealth,
      maxHealth,
      scale,
      size,
      phase: 'revealing',
      revealRemainingMs: HUNT_BALANCE.boss.revealDurationMs,
    };
    this.boss = boss;
    if (this.bossEncounter) {
      // Back on the field: it is fighting again, not recovering.
      // It has been on the field. A death clears the entity, not this - which
      // is what brings the SAME boss back on the next attempt.
      this.bossEncounter = { ...this.bossEncounter, activated: true, retrying: false };
    }
    this.events.push({ type: 'boss-spawned', bossId: boss.id, scale });
  }

  /* ------------------------------------------------------------------ */
  /*  Spawning                                                          */
  /* ------------------------------------------------------------------ */

  /**
   * Advances the spawn window and releases whatever is due.
   *
   * Enemies arrive one at a time, at three moments drawn when the window
   * opened. The clock only runs while there is no boss, so a boss fight pauses
   * the window where it stands instead of banking arrivals - when it ends, the
   * remaining moments happen at their own pace rather than all at once.
   */
  private tickSpawning(): void {
    // A live (or revealing) boss pauses NEW normal spawns only.
    if (this.boss) {
      return;
    }
    this.spawnAccumulatorMs += HUNT_TICK_MS;
    // Everything whose moment has arrived. They are 400 ms apart, so in
    // practice this releases at most one per tick.
    while (
      this.spawnSchedule.length > 0 &&
      this.spawnAccumulatorMs >= (this.spawnSchedule[0] ?? Number.POSITIVE_INFINITY)
    ) {
      this.spawnSchedule.shift();
      // One opportunity, one mob - and a full field simply loses it. Banking
      // it would turn a busy moment into a burst later.
      if (this.mobs.length < HUNT_BALANCE.mobs.maxAlive) {
        this.spawnMob();
      }
    }
    if (this.spawnAccumulatorMs >= HUNT_BALANCE.mobs.spawnWindowMs) {
      this.spawnAccumulatorMs -= HUNT_BALANCE.mobs.spawnWindowMs;
      this.spawnSchedule = this.rollSpawnSchedule();
    }
  }

  /**
   * Draws the three moments of the next window, one per region.
   *
   * Stratified: each moment comes from its own slice of the window, so the
   * timing stays unpredictable while two arrivals can never land within 400 ms
   * of each other - not inside a window, and not across the seam between two.
   * The draws come from the deterministic spawn stream, so the browser and the
   * server schedule the same arrivals from the same seed.
   */
  private rollSpawnSchedule(): number[] {
    return HUNT_BALANCE.mobs.spawnSlots.map((slot) => {
      const at = this.spawnRng.nextIntInclusive(slot.fromMs, slot.toMs);
      // Moments land on tick boundaries; the simulation only ever looks at
      // spawns on a tick, and quantising here keeps the stored schedule equal
      // to the one that will actually be honoured.
      return Math.round(at / HUNT_TICK_MS) * HUNT_TICK_MS;
    });
  }

  /**
   * Creates ONE normal mob: its own id, its own column, its own health.
   *
   * Called once per spawn opportunity. Each call draws from the same
   * deterministic spawn stream, so the server's re-simulation places the same
   * mobs in the same columns - and each new mob is part of the crowd the
   * next one tries to avoid.
   */
  private spawnMob(): void {
    const half = this.mobSize / 2;
    // Never spawn laterally cut: the whole sprite stays inside [0, width].
    const minX = Math.ceil(half);
    const maxX = Math.floor(HUNT_BALANCE.arena.width - half);
    let x = this.spawnRng.nextIntInclusive(minX, maxX);
    for (let attempt = 0; attempt < HUNT_BALANCE.mobs.spawnPlacementAttempts; attempt += 1) {
      const nearTop = this.mobs.filter((mob) => mob.y < this.mobSize);
      const crowded = nearTop.some(
        (mob) => Math.abs(mob.x - x) < HUNT_BALANCE.mobs.minSpawnSpacing,
      );
      if (!crowded) {
        break;
      }
      x = this.spawnRng.nextIntInclusive(minX, maxX);
    }
    this.mobs.push({
      id: this.nextEntityId++,
      kind: 'mob',
      x,
      y: -half, // fully above the top edge, descending in
      health: this.mobMaxHealth,
      maxHealth: this.mobMaxHealth,
      size: this.mobSize,
      leaked: false,
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Movement, leaks, breaches                                          */
  /* ------------------------------------------------------------------ */

  private tickMovement(dt: number): void {
    const bottom = HUNT_BALANCE.arena.bottomBoundary;
    const survivors: MobEntity[] = [];
    for (const mob of this.mobs) {
      mob.y += HUNT_BALANCE.mobs.descentSpeed * dt;
      // "Fully crossed": the sprite's top edge is beyond the boundary.
      if (!mob.leaked && mob.y - mob.size / 2 > bottom) {
        mob.leaked = true;
        /*
         * **A única fonte de dano hostil da caça, e a sua redução** (gate V28G.Q).
         *
         * A ordem é a mesma do duelo, com os mesmos passos e a mesma função: o
         * payload bruto sai de `mobLeakDamage`, a redução do destinatário
         * encolhe-o, e só depois a vida é descontada com o seu clamp em zero.
         *
         * O evento publica o valor JÁ reduzido, porque é esse o dano que
         * aconteceu. Publicar o bruto faria a cena imprimir um golpe que a nave
         * não levou, e faria um cliente a re-simular ler dois números diferentes
         * para a mesma fuga.
         *
         * A passagem do boss não passa por aqui: ela é morte incondicional e não
         * é dano, portanto não há magnitude para reduzir.
         */
        /*
         * **O Bastião: a fuga não fere** (gate V28H.L).
         *
         * Aqui, porque este é o ÚNICO sítio por onde a caça tira vida à nave -
         * a mesma disciplina que pôs a redução da Égide nesta linha. Um mob que
         * passa continua a passar e continua a sair do campo: o que a carta
         * impede é o golpe, e não o acontecimento.
         *
         * `cardIsHittable` e não uma segunda condição: é a mesma pergunta que o
         * duelo faz a um projétil, do mesmo módulo, e a resposta tem de ser a
         * mesma nos dois modos.
         *
         * **E uma nave dentro de um Leviatã também não é ferida** (gate
         * V28H.M): ela está lá dentro, e a estrutura leva por ela. É a mesma
         * regra do duelo, onde um piloto não pode ser atingido.
         */
        const damage =
          cardIsHittable(this.cardEffects) && !this.shipIsPiloting()
            ? reduceHostileDamage(
                mobLeakDamage(this.maxHealth(), mob.health, mob.maxHealth),
                this.hostileDamageMultiplierBps(),
              )
            : 0;
        this.health = Math.max(0, this.health - damage);
        this.events.push({ type: 'leak', mobId: mob.id, damage });
        if (this.health <= 0) {
          this.die('mob-leaks');
          return;
        }
        continue; // removed after damaging once
      }
      survivors.push(mob);
    }
    this.mobs = survivors;

    const boss = this.boss;
    if (boss) {
      if (boss.phase === 'revealing') {
        boss.revealRemainingMs -= HUNT_TICK_MS;
        if (boss.revealRemainingMs <= 0) {
          boss.phase = 'descending';
        }
      } else {
        boss.y += HUNT_BALANCE.mobs.descentSpeed * HUNT_BALANCE.boss.descentSpeedFraction * dt;
        if (boss.y - boss.size / 2 > bottom) {
          // A boss breach is unconditional death, whatever the ship's health.
          this.die('boss-breach');
        }
      }
    }
  }

  private die(cause: 'boss-breach' | 'mob-leaks'): void {
    this.phase = 'dead';
    this.lastDeathCause = cause;
    this.deathStats = {
      survivedMs: this.simTimeMs - this.runStartMs,
      kills: this.runKills,
      damageDealt: this.runDamage,
      goldEarned: Number(this.runGold),
    };
    const bossWasFighting = this.boss !== null;
    // Everything on the field is removed on death.
    this.mobs = [];
    this.boss = null;
    this.projectiles = [];
    /*
     * E os stacks de veneno com ele (gate V28G.M).
     *
     * Não é uma regra nova: o §"se o alvo morrer, todos os stacks daquele alvo
     * são removidos" já os condena um a um, porque o campo acabou de ser
     * esvaziado e nenhum deles tem alvo. Escrito aqui porque uma corrida morta
     * não corre mais tiques - a fase é `dead` e `tick` não volta a passar - e
     * sem esta linha eles ficavam na instantânea a apontar para inimigos que já
     * não existem.
     */
    this.poison = [];
    /*
     * E as marcas de sangramento com ela (gate V28G.N).
     *
     * Aqui a razão é DUPLA. O §"desaparece quando o autor morre" condena-as
     * todas de uma vez - nesta simulação há um autor só, e é ele que caiu - e o
     * §"desaparece quando o alvo morre" já teria condenado as que estavam em
     * inimigos que o campo acabou de esvaziar. Uma linha cobre as duas, e uma
     * corrida morta não corre mais tiques para as apanhar de outra maneira.
     */
    this.bleeding = [];
    /*
     * E o resto do Roubo de Vida vai com ela (gate V28G.I).
     *
     * Um destroço não leva cura devida para a corrida seguinte, pela mesma razão
     * que o casco do duelo larga `damageCarryMilli` ao cair. É também o que
     * torna a morte o fim de um episódio de cura em vez de uma pausa nele.
     *
     * A limpeza do campo acima já torna o caso "atacante morto com tiros no ar"
     * inalcançável nesta simulação - não sobra projétil nenhum para chegar.
     */
    this.lifestealCarryBps = 0;
    /*
     * E as serras param com ela (gate V28G.P).
     *
     * O §"ao morrer a nave proprietária, todas as serras deixam de causar dano
     * imediatamente" é sobre o DONO, e nesta simulação há um só. A órbita volta
     * ao princípio, o resto é largado e os relógios saem - uma corrida morta não
     * corre mais tiques, e sem esta linha eles ficavam na instantânea a apontar
     * para inimigos que o campo acabou de esvaziar.
     */
    this.sawPhase = SAW_INITIAL_PHASE;
    this.sawCarryMilli = 0n;
    this.sawContacts = [];
    if (bossWasFighting && this.bossEncounter !== null) {
      /*
       * The boss killed the player. It is the SAME encounter - same id, same
       * ordinal, same tier, no draw - and it goes away for a fixed recovery
       * window rather than being waiting on the next tick.
       *
       * That window is the whole point: with no escape rule, a boss that comes
       * straight back kills a weak ship again immediately, and the account can
       * never earn the levels that would let it win. Three minutes of ordinary
       * mobs makes the next attempt stronger than the last.
       */
      this.bossEncounter = { ...this.bossEncounter, activated: false, retrying: true };
      this.bossCountdownMs = HUNT_BALANCE.boss.retryWaitSeconds * 1000;
    }
    this.events.push({ type: 'ship-death', cause });
  }

  /**
   * DEV ONLY - ends the current run immediately, for tooling and E2E tests of
   * the death flow. Guarded by the caller (debug mode); grants nothing.
   */
  devForceDeath(): void {
    if (this.phase === 'active') {
      this.die('mob-leaks');
    }
  }

  /**
   * DEV ONLY - a PRÓXIMA salva traz uma bomba, se a nave tiver a joia.
   *
   * ## O que ele muda, e o que não pode mudar
   *
   * Muda **uma comparação**, e nada mais. A rolagem continua a ser feita e os
   * dois valores continuam a sair do fluxo, portanto a sequência de números
   * desta corrida é idêntica à de uma corrida sem a ferramenta - ligar isto não
   * desloca um spawn, um temporizador de boss nem um proc. O que é substituído é
   * o valor com que a rolagem é COMPARADA, por zero, que cai sempre abaixo do
   * numerador.
   *
   * Não toca em dano, magnitude, raio, velocidade, colisão, empurrão ou
   * resultado: a bomba que sai daqui é construída por `selectVolleyBomb` com o
   * dano-base autoritativo da nave, como qualquer outra.
   *
   * **Não acrescenta a joia.** Uma nave sem o Núcleo Atômico encaixado continua
   * a não produzir bomba nenhuma, porque `selectVolleyBomb` devolve `null`
   * quando o perfil não a tem - forçar a rolagem não é forçar a posse.
   *
   * Uma salva só. Consome-se ao ser usado, para que uma tecla carregada por
   * engano não transforme a corrida inteira.
   *
   * Guardado pelo chamador (modo de depuração), como `devForceDeath` é, e não
   * concede coisa nenhuma: uma sessão de depuração já não é económica.
   */
  devForceNextVolleyBomb(): void {
    this.forceNextVolleyBomb = true;
  }

  /** Returns to the hunt after death. Progression survives; the arena resets. */
  restart(): void {
    if (this.phase !== 'dead') {
      return;
    }
    this.phase = 'active';
    this.health = this.maxHealth();
    this.mobs = [];
    this.boss = null;
    this.projectiles = [];
    this.spawnAccumulatorMs = 0;
    // A fresh run opens a fresh window; the arrivals of the old one died with
    // it rather than landing all at once on the first tick of the new.
    this.spawnSchedule = this.rollSpawnSchedule();
    this.attackAccumulatorMs = 0;
    this.facing = HUNT_BALANCE.ship.restingFacingRad;
    if (this.pet) {
      // The pet SURVIVES the death - it was never on the field to be killed,
      // and a player who activated one should not have to activate it again
      // every time the ship dies. Its cadence restarts with the run; its
      // cumulative damage does not, because that tally is the session's.
      this.pet = {
        ...this.pet,
        attackAccumulatorMs: 0,
        facing: HUNT_BALANCE.ship.restingFacingRad,
      };
    }
    /*
     * The encounter SURVIVES the death, and so does whatever is left of its
     * countdown.
     *
     * A free-to-play ship will die to the same boss several times before it
     * wins, and each attempt must be against the same boss rather than a fresh
     * roll - otherwise dying is how a player re-rolls a tier they did not
     * like. So: no new delay, no new tier, and no reward for coming back.
     *
     * It does NOT come straight back. `die` has already put the encounter
     * into its recovery window, and the countdown below is what brings it out.
     */
    if (this.config.serverManagedBoss !== true) {
      // Legacy run with no server encounter: the old behaviour, a fresh roll.
      this.bossCountdownMs = this.rollBossDelayMs();
    }
    this.runStartMs = this.simTimeMs;
    this.runKills = 0;
    this.runDamage = 0;
    this.runGold = 0n;
    this.deathStats = null;
    this.lastDeathCause = null;
  }

  /* ------------------------------------------------------------------ */
  /*  Targeting and firing                                              */
  /* ------------------------------------------------------------------ */

  /**
   * The enemy closest to the BOTTOM boundary: highest y wins; ties break by
   * lowest entity id (stable, deterministic). Never euclidean distance.
   * A revealing boss is not yet targetable.
   */
  currentTargetId(): number | null {
    let best: { id: number; y: number } | null = null;
    const consider = (id: number, y: number) => {
      if (best === null || y > best.y || (y === best.y && id < best.id)) {
        best = { id, y };
      }
    };
    for (const mob of this.mobs) {
      consider(mob.id, mob.y);
    }
    if (this.boss && this.boss.phase === 'descending') {
      consider(this.boss.id, this.boss.y);
    }
    return best === null ? null : (best as { id: number; y: number }).id;
  }

  private tickFiring(): void {
    /* A EMISSÃO passa pelo teto do campo; o atributo pedido fica intacto. */
    const periodMs = 1000 / this.emittedAttacksPerSecond();
    this.attackAccumulatorMs += HUNT_TICK_MS;
    const targetId = this.currentTargetId();
    if (targetId === null) {
      // No target: bank at most one ready shot, never a burst.
      this.attackAccumulatorMs = Math.min(this.attackAccumulatorMs, periodMs);
      return;
    }
    // High attack speeds fire several logical volleys per tick. Each one is a
    // separate volley of separate projectiles, counted at exactly this
    // frequency - never merged, and never drawn as a beam.
    while (this.attackAccumulatorMs >= periodMs) {
      this.attackAccumulatorMs -= periodMs;
      this.fireVolley(targetId);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Cartas (gate V28H.H)                                              */
  /* ------------------------------------------------------------------ */

  /**
   * **Um tique das três cartas**: envelhecer, activar no máximo uma, disparar.
   *
   * ## A ordem das três coisas é a regra
   *
   * Envelhecer primeiro, para uma habilidade que acaba neste tique não disparar
   * mais uma vez. Activar a seguir, no máximo UMA por tique e pela ordem dos
   * espaços. Disparar por último, para uma que acabou de activar não sair já a
   * atirar - ela nasce montada, e o primeiro tiro é do tique seguinte.
   *
   * ## No máximo uma activação por tique
   *
   * Do contrato, e não uma optimização: com três cartas prontas e um alvo a
   * aparecer, activar as três no mesmo instante daria ao jogador três
   * habilidades sincronizadas para sempre - os cooldowns nunca mais se
   * separariam. Uma de cada vez, pela ordem 1, 2, 3.
   */
  private tickCards(): void {
    if (this.cards.length === 0) {
      return;
    }
    const targetId = this.currentTargetId();

    /*
     * As cargas que TERMINARAM neste tique (gate V28H.L): a Esfera Ígnea sai,
     * e o feixe da Lança Solar acende. Um instante e não um estado.
     */
    const charged: CardAbilityState[] = [];
    for (const card of this.cards) {
      const before = card.chargeRemainingMs;
      ageCardAbility(card, HUNT_TICK_MS);
      if (cardAbilityChargeEnded(before, card.chargeRemainingMs)) {
        charged.push(card);
      }
    }
    ageCardEffects(this.cardEffects, HUNT_TICK_MS);
    for (const card of charged) {
      this.releaseChargedCard(card, targetId);
    }

    /*
     * **Nada activa sozinho, e isso é a decisão.**
     *
     * Uma carta é uma habilidade ACTIVA: quem a dispara é o jogador, e um tique
     * que a disparasse pelo jogador tirar-lhe-ia a única escolha que ela tem.
     * Este ciclo faz correr os relógios e alimenta o que já está activo; quem
     * liga é {@link activateCardSlot}, e mais ninguém.
     */
    for (const card of this.cards) {
      if (!cardAbilityIsActive(card)) {
        continue;
      }
      if (card.definitionId === 'card_super_cannons') {
        this.tickSuperCannons(card, targetId);
        continue;
      }
      if (card.definitionId === 'card_blade_storm') {
        this.tickBladeStorm(card);
        continue;
      }
      if (card.definitionId === 'card_solar_lance' && cardAbilityIsFiring(card)) {
        this.tickSolarLance(card);
      }
    }
  }

  /**
   * **O que uma carta faz no instante em que liga** (gate V28H.L).
   *
   * Duas cartas têm efeito imediato na caça: o **Bastião**, que liga o seu
   * temporizador, e o **Catolé**, que rebenta - ele não carrega e não voa nesta
   * arena, portanto o instante da ordem é o instante da explosão.
   *
   * As outras não fazem nada aqui: elas são alimentadas pelo tique.
   */
  private applyCardEffect(card: CardAbilityState): void {
    const balance = cardBalance(card.definitionId);
    if (balance === null) {
      return;
    }
    if (balance.effect === 'self_buff' && balance.buff === 'untargetable') {
      applyCardTimer(this.cardEffects.untargetable, balance.durationMs, balance.magnitudeBps);
      return;
    }
    if (balance.effect === 'guided_blast') {
      /* LANÇADA, e já não detonada no instante da ordem (gate V28H.T). */
      this.launchCardBlast('catole', balance, this.currentTargetId());
      return;
    }
    if (balance.effect === 'summon') {
      this.spawnSummon(card.definitionId, balance);
    }
  }

  /**
   * **Uma invocação nasce** (gate V28H.M).
   *
   * À FRENTE da nave, e não em cima dela: a entrada do Leviatã tem de ser uma
   * coisa que se vê acontecer, e um que nascesse no centro do casco embarcava-o
   * no primeiro tique sem ninguém perceber.
   *
   * A vida sai da vida MÁXIMA da nave, congelada aqui - a mesma regra do duelo.
   */
  private spawnSummon(definitionId: string, balance: CardBalance): void {
    if (this.summons.length >= CARD_MAX_SUMMONS) {
      return;
    }
    const kind: CardSummonKind = definitionId === 'card_leviathan' ? 'leviathan' : 'drone';
    const here = this.shipPosition();
    const ahead = this.shipSize();
    const maxHealthMilli =
      kind === 'drone'
        ? Math.trunc((this.maxHealth() * 1000 * CARD_DRONE_HEALTH_BPS) / 10_000)
        : this.maxHealth() * 1000;
    this.summons.push({
      id: this.nextEntityId++,
      kind,
      x: here.x,
      y: here.y - ahead,
      /* Apontado para cima, que é de onde os inimigos vêm. */
      facing: HUNT_BALANCE.ship.restingFacingRad,
      healthMilli: maxHealthMilli,
      maxHealthMilli,
      lifeMs: kind === 'leviathan' ? CARD_LEVIATHAN_IDLE_MS : balance.durationMs,
      attackAccumulatorMs: 0,
      boarded: false,
      boardedRemainingMs: 0,
    });
  }

  /** As invocações a existir, para o desenho e para as provas. */
  summonList(): readonly HuntSummonEntity[] {
    return this.summons;
  }

  /**
   * **A nave está DENTRO de uma invocação?** (gate V28H.M).
   *
   * Um piloto de Leviatã está lá dentro, e a estrutura leva por ele: uma fuga
   * não o fere. É a mesma pergunta que o Bastião responde, por outra razão e no
   * mesmo sítio.
   */
  private shipIsPiloting(): boolean {
    return this.summons.some((summon) => summon.boarded);
  }

  /**
   * **Um tique de todas as invocações** (gate V28H.M).
   *
   * A ordem é: envelhecer, largar as que acabaram, e só depois agir. Uma
   * entidade que agisse no tique em que morre disparava do além.
   *
   * **Uma invocação morre com quem a invocou**: uma nave abatida leva-as todas.
   */
  private tickSummons(): void {
    if (this.summons.length === 0) {
      return;
    }
    const survivors: HuntSummonEntity[] = [];
    for (const summon of this.summons) {
      if (this.health <= 0) {
        this.endSummon(summon);
        continue;
      }
      if (summon.healthMilli <= 0 || ageCardSummon(summon, HUNT_TICK_MS)) {
        this.endSummon(summon);
        continue;
      }
      this.actSummon(summon);
      survivors.push(summon);
    }
    this.summons = survivors;
  }

  /**
   * O que uma invocação faz quando sai do campo.
   *
   * O **Drone explode**: o kamikaze é o fim dele, aconteça ele por tempo, por
   * vida ou pela morte da nave. É dano de CARTA - origem `card` - e por isso
   * não activa joia nenhuma: "a colisão kamikaze não ativa joias" é literal no
   * contrato do catálogo.
   *
   * O **Leviatã larga a nave**, e mais nada.
   */
  private endSummon(summon: HuntSummonEntity): void {
    if (summon.kind !== 'drone') {
      summon.boarded = false;
      return;
    }
    const damageMilli = BigInt(
      Math.trunc((this.baseDamageMilli() * CARD_DRONE_KAMIKAZE_BPS) / 10_000),
    );
    if (damageMilli <= 0n) {
      return;
    }
    const radius = (this.shipSize() / 2) * (CARD_DRONE_KAMIKAZE_REACH_BPS / 10_000);
    let hits = 0;
    for (const enemy of this.enemyList()) {
      const dx = enemy.x - summon.x;
      const dy = enemy.y - summon.y;
      const limit = radius + enemy.size / 2;
      if (dx * dx + dy * dy > limit * limit) {
        continue;
      }
      this.applyHit(enemy, damageMilli, 'card');
      hits += 1;
    }
    /* E o kamikaze anuncia-se, pela mesma razão que a explosão de carta. */
    this.events.push({
      type: 'explosion',
      x: summon.x,
      y: summon.y,
      radius,
      hits,
      projectileId: -summon.id,
      effectId: ATOMIC_BLAST_EFFECT,
    });
  }

  /**
   * O que uma invocação faz enquanto existe.
   *
   * O **Drone** caça: anda para o inimigo mais próximo e dispara ao seu ritmo.
   * Os disparos PRIMÁRIOS dele nascem com a rolagem de joia do dono - é a
   * metade positiva da mesma frase do contrato cuja metade negativa proíbe o
   * kamikaze de activar seja o que for.
   *
   * O **Leviatã** aproxima-se da nave até ela entrar, e depois dispara os seis
   * canhões ao alvo, com o dano e as joias de quem o pilota.
   */
  private actSummon(summon: HuntSummonEntity): void {
    const balance = cardBalance(
      summon.kind === 'leviathan' ? 'card_leviathan' : 'card_attack_drone',
    );
    if (balance === null) {
      return;
    }
    if (summon.kind === 'leviathan') {
      this.tickLeviathan(summon, balance);
      return;
    }
    const target = this.nearestSummonTarget(summon);
    if (target !== null) {
      summon.facing = stepFacing(
        summon.facing,
        Math.atan2(target.y - summon.y, target.x - summon.x),
        PET_BALANCE.flight.turnRadPerSecond * (HUNT_TICK_MS / 1000),
      );
      const step = HUNT_BALANCE.ship.displayHeight * (HUNT_TICK_MS / 1000) * 3;
      summon.x += Math.cos(summon.facing) * step;
      summon.y += Math.sin(summon.facing) * step;
    }
    summon.attackAccumulatorMs += HUNT_TICK_MS;
    if (target === null) {
      /* Guarda no máximo uma salva pronta, como a nave e o pet fazem. */
      summon.attackAccumulatorMs = Math.min(summon.attackAccumulatorMs, balance.hitIntervalMs);
      return;
    }
    while (summon.attackAccumulatorMs >= balance.hitIntervalMs) {
      summon.attackAccumulatorMs -= balance.hitIntervalMs;
      this.fireSummonVolley(summon, target.id, balance.damageBps, 1);
    }
  }

  /**
   * **O Leviatã, por um tique** (gate V28H.M).
   *
   * ## Sem piloto: ele VAI ter com a nave
   *
   * "Entrada física da nave" é uma decisão aprovada, e na caça a nave não é
   * conduzida - ela deriva num caminho que a simulação decide. Um Leviatã
   * parado à espera de uma nave que não obedece a ninguém seria um Leviatã que
   * às vezes nunca embarca. Ele aproxima-se, e a entrada é uma coisa que se vê
   * acontecer: a estrutura vem ter com a nave e engole-a.
   *
   * ## Com piloto: treze segundos, e seis canhões
   *
   * **APROVADO: destruição automática 13 s após a entrada.** A nave viaja com
   * ele e fica protegida - ela está lá dentro - e os seis canhões disparam ao
   * ritmo da carta com o dano e as joias dela.
   */
  private tickLeviathan(summon: HuntSummonEntity, balance: CardBalance): void {
    const here = this.shipPosition();
    if (!summon.boarded) {
      const entry = (this.shipSize() / 2) * (CARD_LEVIATHAN_ENTRY_BPS / 10_000);
      const dx = here.x - summon.x;
      const dy = here.y - summon.y;
      const distance = Math.hypot(dx, dy);
      if (distance <= entry) {
        summon.boarded = true;
        /* **APROVADO: treze segundos a contar da entrada.** */
        summon.boardedRemainingMs = balance.durationMs;
        return;
      }
      const step = HUNT_BALANCE.ship.displayHeight * (HUNT_TICK_MS / 1000) * 2;
      summon.x += (dx / (distance || 1)) * step;
      summon.y += (dy / (distance || 1)) * step;
      return;
    }
    /* Embarcado: ele leva a nave, e por isso está onde ela está. */
    summon.x = here.x;
    summon.y = here.y;
    const targetId = this.currentTargetId();
    const target = targetId === null ? null : this.findEntity(targetId);
    if (target !== null) {
      summon.facing = Math.atan2(target.y - summon.y, target.x - summon.x);
    }
    summon.attackAccumulatorMs += HUNT_TICK_MS;
    if (target === null) {
      summon.attackAccumulatorMs = Math.min(summon.attackAccumulatorMs, balance.hitIntervalMs);
      return;
    }
    while (summon.attackAccumulatorMs >= balance.hitIntervalMs) {
      summon.attackAccumulatorMs -= balance.hitIntervalMs;
      /* Os SEIS canhões, com o dano e as joias de quem pilota. */
      this.fireSummonVolley(summon, target.id, balance.damageBps, balance.count);
    }
  }

  /** O inimigo vivo mais próximo desta invocação, ou `null`. */
  private nearestSummonTarget(summon: HuntSummonEntity): MobEntity | BossEntity | null {
    let best: MobEntity | BossEntity | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const enemy of this.enemyList()) {
      const distance = (enemy.x - summon.x) ** 2 + (enemy.y - summon.y) ** 2;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = enemy;
      }
    }
    return best;
  }

  /**
   * Uma invocação dispara, com o dano e as JOIAS da nave que a criou.
   *
   * A mesma rolagem que os Super Canhões usam - `rollTemporaryCannonProc` - e
   * pela mesma razão: "só os ataques primários dessas entidades podem iniciar a
   * loteria de joias" é literal no contrato do catálogo. O que estes tiros
   * gerarem nasce `jewel`, que não origina nada.
   */
  private fireSummonVolley(
    summon: HuntSummonEntity,
    targetId: number,
    damageBps: number,
    barrels: number,
  ): void {
    const target = this.findEntity(targetId);
    if (!target) {
      return;
    }
    const damageMilli = BigInt(Math.trunc((this.baseDamageMilli() * damageBps) / 10_000));
    if (damageMilli <= 0n) {
      return;
    }
    const spacing = HUNT_BALANCE.ship.projectileSpacing;
    for (let barrel = 0; barrel < barrels; barrel += 1) {
      /*
       * As bocas espalham-se PERPENDICULARMENTE ao rumo, centradas: seis
       * canhões de um Leviatã saem de seis sítios, e não todos do mesmo ponto.
       */
      const lane = barrel - (barrels - 1) / 2;
      const originX = summon.x + Math.cos(summon.facing + Math.PI / 2) * lane * spacing;
      const originY = summon.y + Math.sin(summon.facing + Math.PI / 2) * lane * spacing;
      const dx = target.x - originX;
      const dy = target.y - originY;
      const length = Math.hypot(dx, dy) || 1;
      this.projectiles.push({
        id: this.nextEntityId++,
        x: originX,
        y: originY,
        dirX: dx / length,
        dirY: dy / length,
        damageMilli,
        /* Uma rolagem POR PROJÉTIL, com a origem do armamento temporário. */
        jewelProc: this.rollTemporaryCannonProc(),
        targetId,
        orphaned: false,
      });
    }
  }

  /**
   * **O Turbilhão de Lâminas, na caça** (gate V28H.L).
   *
   * A mesma carta do duelo, e a mesma forma: uma lâmina que orbita o casco e
   * apanha TODOS os inimigos ao alcance de `hitIntervalMs` em
   * `hitIntervalMs`. O que muda é o passo do relógio - 16 ms em vez de 50 - e
   * quem está do outro lado: mobs e o chefe em vez de cascos.
   *
   * A origem é `card`, portanto não activa joia nenhuma - a mesma tabela
   * fechada, a mesma frase do contrato do catálogo.
   */
  private tickBladeStorm(card: CardAbilityState): void {
    const balance = cardBalance('card_blade_storm');
    if (balance === null) {
      return;
    }
    card.attackAccumulatorMs += HUNT_TICK_MS;
    if (card.attackAccumulatorMs < balance.hitIntervalMs) {
      return;
    }
    card.attackAccumulatorMs -= balance.hitIntervalMs;
    const reach = (this.shipSize() / 2) * (balance.reachBps / 10_000);
    const damageMilli = BigInt(Math.trunc((this.baseDamageMilli() * balance.damageBps) / 10_000));
    if (damageMilli <= 0n) {
      return;
    }
    for (const enemy of this.enemyList()) {
      const dx = enemy.x - this.shipPosition().x;
      const dy = enemy.y - this.shipPosition().y;
      const limit = reach + enemy.size / 2;
      if (dx * dx + dy * dy > limit * limit) {
        continue;
      }
      this.applyHit(enemy, damageMilli, 'card');
    }
  }

  /**
   * **A Lança Solar, na caça** (ADR 0065, gate V28H.L).
   *
   * O feixe cobra em PARCELAS, e cada parcela imprime o seu número - a terceira
   * restrição da ADR, e a que mantém o §"cada golpe lógico imprime o seu
   * número" verdadeiro sobre um feixe.
   *
   * A geometria é a mesma do duelo: um segmento a partir do casco, ao longo da
   * mira, e um inimigo é atingido quando o centro dele cai dentro de meia
   * espessura mais o seu próprio raio.
   */
  private tickSolarLance(card: CardAbilityState): void {
    const balance = cardBalance('card_solar_lance');
    if (balance === null) {
      return;
    }
    card.attackAccumulatorMs += HUNT_TICK_MS;
    if (card.attackAccumulatorMs < balance.hitIntervalMs) {
      return;
    }
    card.attackAccumulatorMs -= balance.hitIntervalMs;
    const radius = this.shipSize() / 2;
    const length = radius * (HUNT_SOLAR_LANCE_REACH_BPS / 10_000);
    /*
     * **A espessura e o dano saem da CARGA** (gate V28H.N).
     *
     * Contrato aprovado: "espessura, intensidade e dano aumentam
     * progressivamente". A mesma função que a cena usa para desenhar decide
     * aqui quem é apanhado - duas expressões seriam um feixe pintado mais gordo
     * do que aquilo por que ele cobra.
     */
    const halfWidth = cardBeamWidthFor(
      radius * (HUNT_SOLAR_LANCE_WIDTH_BPS / 10_000),
      card.chargeStrengthBps,
    );
    const damageMilli = BigInt(
      Math.trunc(
        (this.baseDamageMilli() * cardScaleByCharge(balance.damageBps, card.chargeStrengthBps)) /
          10_000,
      ),
    );
    if (damageMilli <= 0n) {
      return;
    }
    const aim = this.shipAim();
    /*
     * **A boca, e não o centro do casco** (gate V28H.N).
     *
     * Contrato aprovado: "o feixe nasce na frente da nave, nunca atrás ou
     * atravessando o casco". Medido do centro, o segmento começava DENTRO da
     * nave e apanhava quem estivesse encostado atrás dela - com `along` preso a
     * zero, um inimigo às costas caía no princípio do feixe.
     */
    const muzzle = this.solarLanceMuzzle();
    for (const enemy of this.enemyList()) {
      const dx = enemy.x - muzzle.x;
      const dy = enemy.y - muzzle.y;
      const along = Math.max(0, Math.min(length, dx * aim.x + dy * aim.y));
      const nearestX = muzzle.x + aim.x * along;
      const nearestY = muzzle.y + aim.y * along;
      const limit = halfWidth + enemy.size / 2;
      const gapX = enemy.x - nearestX;
      const gapY = enemy.y - nearestY;
      if (gapX * gapX + gapY * gapY > limit * limit) {
        continue;
      }
      this.applyHit(enemy, damageMilli, 'card');
    }
  }

  /**
   * **A carga acabou: a Esfera Ígnea rebenta** (gate V28H.L).
   *
   * Na caça ela não voa: o alvo já está escolhido - é o mesmo que os canhões
   * seguem - e a explosão acontece nele. Fazê-la viajar exigiria um sistema de
   * projéteis de carta que a caça não tem, e o que a descrição promete é uma
   * esfera explosiva, que é o que acontece.
   *
   * A Lança Solar não faz nada aqui: o feixe É ela, e o que este instante faz é
   * deixá-la passar de "a carregar" para "a arder".
   */
  private releaseChargedCard(card: CardAbilityState, targetId: number | null): void {
    const balance = cardBalance(card.definitionId);
    if (balance === null || balance.effect !== 'charged_projectile') {
      return;
    }
    /*
     * A esfera SAI, e viaja (gate V28H.T). Ela não rebenta onde nasceu.
     *
     * **Com a força da carga** (gate V28H.V), como no duelo - e aqui a carga é
     * automática e completa-se sempre, portanto a força é cheia salvo se alguma
     * coisa a interromper. A regra é a mesma nos dois modos porque é a mesma
     * carta; o que difere é o tempo, e esse é da arena.
     */
    this.launchCardBlast('fireball', balance, targetId, card.chargeStrengthBps);
    /* E o arrefecimento começa no DISPARO, como no duelo (gate V28I.C). */
    cardBeginCooldown(card);
  }

  /**
   * **Uma bomba de carta sai da nave** (gate V28H.T).
   *
   * O raio e o dano são congelados AQUI, no lançamento, e não recalculados na
   * chegada: o dano-base pode mudar a meio do voo - um ponto gasto, uma joia, um
   * artefacto - e uma bomba que cobrasse pelo que existe quando chega seria uma
   * bomba cujo valor depende de quanto tempo ela demorou.
   *
   * Sem alvo não há lançamento: é a mesma regra que já recusava a ordem.
   */
  private launchCardBlast(
    kind: HuntCardBlast['kind'],
    balance: CardBalance,
    targetId: number | null,
    /**
     * A força da carga, em bps (gate V28H.V).
     *
     * O Catolé não carrega e passa cheio - é o que o valor por omissão diz. A
     * Esfera passa o que a carga alcançou, e é isso que faz nove segundos de
     * espera valerem alguma coisa.
     */
    strengthBps: number = CARD_CHARGE_FULL_BPS,
  ): void {
    if (targetId === null) {
      return;
    }
    const damageMilli = BigInt(
      Math.trunc(
        (this.baseDamageMilli() * cardScaleByCharge(balance.damageBps, strengthBps)) / 10_000,
      ),
    );
    if (damageMilli <= 0n) {
      return;
    }
    /*
     * O raio do Catolé é **6x o da Bomba Atômica**, e é uma decisão aprovada. A
     * palavra "6x" continua verdadeira se a Bomba mudar, porque ela é lida e
     * nunca copiada.
     */
    const radius =
      kind === 'catole'
        ? HUNT_BOMB_BLAST_RADIUS * 6
        : /*
           * E a esfera vale o que a carga cumpriu, como no duelo (V28H.V), sobre
           * o alcance DESTA arena (gate V28H.Z) - o do duelo é dez vezes maior,
           * e aqui daria uma bola maior do que o mapa.
           */
          cardSphereRadiusFor(
            (this.shipSize() / 2) * (HUNT_FIREBALL_REACH_BPS / 10_000),
            strengthBps,
          );
    /*
     * **Nasce na BOCA da nave** (gate V28I.C), e já não no centro dela.
     *
     * "Sai da boca da nave e viaja visivelmente até o alvo." Nascer no centro
     * punha metade da bola por baixo do casco no instante em que ela aparecia -
     * e com um corpo do tamanho da nave, isso é a bola inteira.
     */
    const here = kind === 'fireball' ? this.solarLanceMuzzle() : this.shipPosition();
    this.cardBlasts.push({
      id: this.nextEntityId++,
      kind,
      x: here.x,
      y: here.y,
      targetId,
      speed: kind === 'catole' ? HUNT_CATOLE_SPEED : HUNT_FIREBALL_SPEED,
      radius,
      damageMilli,
      ageMs: 0,
    });
  }

  /** As bombas em voo, para o desenho e para as provas. */
  cardBlastList(): readonly HuntCardBlast[] {
    return this.cardBlasts;
  }

  /**
   * **Um tique das bombas em voo** (gate V28H.T).
   *
   * Elas perseguem o alvo - teleguiadas, como a descrição diz - e detonam
   * quando lhe tocam. Um alvo que morre a meio deixa a bomba sem destino: ela
   * detona ONDE ESTÁ, porque uma bomba que desaparecesse por o alvo ter morrido
   * seria uma carta gasta em troca de nada.
   */
  private tickCardBlasts(dt: number): void {
    if (this.cardBlasts.length === 0) {
      return;
    }
    const survivors: HuntCardBlast[] = [];
    for (const blast of this.cardBlasts) {
      blast.ageMs += HUNT_TICK_MS;
      const target = this.enemyList().find((enemy) => enemy.id === blast.targetId);
      if (target === undefined) {
        this.detonateCardBlastAt(blast, blast.x, blast.y);
        continue;
      }
      const dx = target.x - blast.x;
      const dy = target.y - blast.y;
      const distance = Math.hypot(dx, dy);
      const step = blast.speed * dt;
      /*
       * **E agora o corpo conta também aqui** (gate V28I.C): "corpo, brilho e
       * colisão usam a mesma escala".
       *
       * Isto já tinha sido tentado no gate anterior e teve de ser desfeito, por
       * uma razão que já não existe: nessa altura o corpo da esfera da caça era
       * uma fracção do raio da EXPLOSÃO - cento e oitenta e sete unidades - e uma
       * bola desse tamanho rebentava no tique em que nascia. Com o corpo a valer
       * trinta por cento da nave, são vinte e nove unidades, e ela viaja.
       *
       * O Catolé não tem corpo aqui: ele persegue e detona ao chegar.
       */
      const body = blast.kind === 'fireball' ? this.fireballBodyRadius() : 0;
      /*
       * **E NUNCA no tique em que nasce** (gate V28I.C), que é uma exigência
       * escrita. A bola nasce na boca, e um inimigo colado ao casco fá-la-ia
       * rebentar antes de existir um único quadro em que ela se vê. Um tique de
       * idade é o mínimo para "viaja visivelmente até o alvo" ser verdade.
       */
      if (blast.ageMs > HUNT_TICK_MS && distance <= step + target.size / 2 + body) {
        this.detonateCardBlastAt(blast, target.x, target.y);
        continue;
      }
      blast.x += (dx / distance) * step;
      blast.y += (dy / distance) * step;
      survivors.push(blast);
    }
    this.cardBlasts = survivors;
  }

  /**
   * A explosão de uma bomba que chegou, no sítio onde ela chegou.
   *
   * O evento leva o raio MECÂNICO - o mesmo que decidiu quem levou dano -
   * portanto a onda desenhada é a verdade e não uma aproximação dela. E leva o
   * `kind`, porque as duas explosões não são a mesma: o Catolé tem seis vezes o
   * raio da Bomba, e adivinhar isso pelo número seria a cena a decidir a arte a
   * partir de um valor de equilíbrio.
   */
  private detonateCardBlastAt(blast: HuntCardBlast, x: number, y: number): void {
    let hits = 0;
    for (const enemy of this.enemyList()) {
      const dx = enemy.x - x;
      const dy = enemy.y - y;
      const limit = blast.radius + enemy.size / 2;
      if (dx * dx + dy * dy > limit * limit) {
        continue;
      }
      this.applyHit(enemy, blast.damageMilli, 'card');
      hits += 1;
    }
    this.events.push({
      type: 'explosion',
      x,
      y,
      radius: blast.radius,
      hits,
      projectileId: -blast.id,
      effectId: blast.kind === 'catole' ? CATOLE_BLAST_EFFECT : FIREBALL_BLAST_EFFECT,
    });
  }

  /**
   * **A ordem do jogador: liga a carta deste espaço** (gate V28H.H).
   *
   * ## Porque é um comando e não um automatismo
   *
   * Porque uma carta é uma habilidade activa. O tique faz correr o
   * arrefecimento e alimenta o que está ligado; ligar é uma decisão, e é a
   * única que o jogador tem numa caçada em que a nave nem sequer é conduzida.
   *
   * ## Determinística, e por isso a instantânea chega
   *
   * Ela não sorteia nada e não lê relógio de parede: escreve dois números que a
   * instantânea já guarda. O que ela NÃO tem é um canal - o servidor re-simula
   * a janela a partir da instantânea e não sabe que a ordem existiu, portanto
   * uma activação numa sessão económica divergiria no checkpoint seguinte.
   * Enquanto o protocolo do comando não existir, quem chama isto é uma
   * afordância de desenvolvimento, numa sessão de depuração - que é
   * não-económica e recusada no checkpoint por construção.
   *
   * @returns `true` se a carta ligou mesmo. `false` - e sem gastar nada - se o
   * espaço está vazio, se a carta já está activa, se ainda arrefece, se não há
   * alvo, ou se a habilidade dela ainda não existe.
   */
  /**
   * **Solta a carga de um espaço** (gate V28H.N).
   *
   * ## Porque isto é uma ordem, e não um temporizador
   *
   * "A habilidade é carregada enquanto o botão ou tecla permanece pressionado."
   * O instante em que a tecla se solta é uma decisão do jogador, e sob autoridade
   * do servidor uma decisão só existe se chegar cá - por isso ela entra pela
   * mesma porta que {@link activateCardSlot}, e não por um relógio.
   *
   * Quem NÃO solta continua a ter a carga a correr, e ela completa-se sozinha ao
   * fim do tempo máximo, valendo cheia. Segurar para sempre não é uma opção.
   *
   * Devolve `false` quando não havia carga nenhuma para soltar - uma ordem
   * repetida, uma carta que não carrega, um espaço vazio.
   */
  releaseCardSlot(slot: CardSlot): boolean {
    const card = this.cards.find((entry) => entry.slot === slot);
    if (card === undefined || !cardAbilityCanRelease(card)) {
      return false;
    }
    /*
     * **A Esfera Ígnea NÃO se solta à mão nesta arena** (gate V28I.C).
     *
     * "Um toque activa; carga automática de 400 ms; não exige segurar nem
     * soltar." A barra manda uma ordem de soltar quando a tecla sobe, e num
     * toque isso acontece uns cinquenta milésimos depois - a carga de 400 ms
     * seria interrompida a um oitavo, e o toque daria a bola mínima em vez da
     * bola cheia. Ignorá-la aqui é o que faz o toque valer o que ele promete.
     *
     * A Lança continua a soltar-se: ela é uma carta de segurar nos dois modos.
     */
    if (cardBalance(card.definitionId)?.effect === 'charged_projectile') {
      return false;
    }
    if (!releaseCardCharge(card, this.chargeMsOf(card))) {
      return false;
    }
    /*
     * O que a carga produzia acontece AGORA, pelo mesmo caminho que o fim
     * natural da carga usa: a Esfera rebenta, e a Lança passa a arder porque
     * `chargeRemainingMs` é zero e o tique seguinte já a vê a disparar.
     */
    this.releaseChargedCard(card, this.currentTargetId());
    return true;
  }

  /**
   * **Quanto tempo esta carta carrega NESTA arena** (gate V28I.C).
   *
   * A Esfera Ígnea é a única que difere: 400 ms aqui, quinze segundos no duelo.
   * O catálogo guarda zero de propósito, para ninguém o ler por engano - quem
   * decide é o modo, e este método é o sítio onde a caça o diz.
   */
  private chargeMsOf(card: CardAbilityState): number {
    if (card.definitionId === 'card_charged_fireball') {
      return HUNT_FIREBALL_CHARGE_MS;
    }
    return cardBalance(card.definitionId)?.chargeMs ?? 0;
  }

  activateCardSlot(slot: CardSlot): boolean {
    const card = this.cards.find((entry) => entry.slot === slot);
    if (!card) {
      return false;
    }
    if (!cardAbilityCanActivate(card, { hasTarget: this.currentTargetId() !== null })) {
      return false;
    }
    /*
     * **E a habilidade tem de existir NESTE modo** (gate V28H.L).
     *
     * Hoje ela existe para as dezasseis - o Drone e o Leviatã passaram a ser
     * entidades também aqui (gate V28H.M) - e a guarda fica porque é o sítio
     * onde uma carta futura escrita só no duelo seria recusada nesta arena.
     */
    if (!cardAbilityIsImplementedIn(card.definitionId, 'hunt')) {
      return false;
    }
    /*
     * **E a carta tem de VALER neste modo** (gate V28I.C).
     *
     * O Bastião passou a ser somente do duelo, e a recusa acontece AQUI - antes
     * de {@link activateCardAbility} - precisamente para ele não gastar
     * arrefecimento: uma carta recusada por modo não foi usada, e cobrar-lhe o
     * tempo seria puni-la por existir na barra.
     *
     * A mesma guarda que o duelo já fazia, e a mesma que o serviço da caça faz
     * do lado do servidor com `wrong_mode`. São três, e é deliberado: cada uma
     * está numa fronteira diferente.
     */
    if (!cardWorksIn(card.definitionId, 'hunt')) {
      return false;
    }
    activateCardAbility(card, this.chargeMsOf(card));
    /*
     * **O efeito imediato, se a carta tiver um** (gate V28H.L).
     *
     * O Bastião liga o temporizador dele, e o Catolé rebenta - ele não carrega
     * e não voa nesta arena, portanto o instante da ordem É o instante da
     * explosão. Tudo o resto acontece no tique, alimentado por `tickCards`.
     */
    this.applyCardEffect(card);
    this.events.push({
      type: 'card-activated',
      slot: card.slot,
      definitionId: card.definitionId,
      durationMs: card.activeRemainingMs,
      tick: this.currentTick(),
    });
    return true;
  }

  /**
   * **Os dois canhões temporários, a disparar** (gate V28H.H).
   *
   * ## Um acumulador PRÓPRIO
   *
   * O da habilidade, e nunca o do casco: partilhá-lo faria a cadência da nave
   * depender de a carta estar activa, e faria uma salva da habilidade consumir
   * a salva seguinte do casco. Os dois correm em paralelo, cada um com o seu
   * resto.
   *
   * ## À velocidade EFECTIVA da nave
   *
   * `totalAttacksPerSecond()` - a mesma que o casco usa, com joias,
   * equipamento, artefactos e pontos já dentro. Uma nave que dispara nove vezes
   * por segundo tem dois canhões que disparam nove vezes por segundo.
   *
   * ## Sem alvo, o acumulador não estoura
   *
   * Guarda no máximo uma salva pronta, como `tickFiring` faz: um campo vazio
   * durante três segundos não pode devolver trinta salvas quando o mob seguinte
   * aparece.
   */
  private tickSuperCannons(card: CardAbilityState, targetId: number | null): void {
    /* Os Super Canhões leem a cadência primária, portanto herdam o mesmo teto. */
    const periodMs = 1000 / this.emittedAttacksPerSecond();
    card.attackAccumulatorMs += HUNT_TICK_MS;
    if (targetId === null) {
      card.attackAccumulatorMs = Math.min(card.attackAccumulatorMs, periodMs);
      return;
    }
    while (card.attackAccumulatorMs >= periodMs) {
      card.attackAccumulatorMs -= periodMs;
      this.fireSuperCannonVolley(targetId);
    }
  }

  /**
   * Onde os dois canhões temporários estão, em coordenadas de mundo.
   *
   * Derivada da posição e da orientação da nave a cada leitura, exactamente
   * como `cannonPositions()` é: os dois acompanham o casco sem guardar posição
   * nenhuma, e por isso não há estado de desenho na instantânea.
   *
   * Vazia quando a habilidade não está activa - a cena lê isto e desenha
   * exactamente o que ele devolve.
   */
  superCannonPositions(): readonly { x: number; y: number }[] {
    if (
      !this.cards.some(
        (card) => card.definitionId === 'card_super_cannons' && cardAbilityIsActive(card),
      )
    ) {
      return [];
    }
    const { lateral, forward } = HUNT_BALANCE.cards.superCannons;
    const ship = this.shipPosition();
    return [
      slotToWorld({ forward, lateral: -lateral }, ship.x, ship.y, this.facing),
      slotToWorld({ forward, lateral }, ship.x, ship.y, this.facing),
    ];
  }

  /** As bocas dos dois, alinhadas uma a uma com {@link superCannonPositions}. */
  superCannonMuzzles(): readonly { x: number; y: number }[] {
    if (
      !this.cards.some(
        (card) => card.definitionId === 'card_super_cannons' && cardAbilityIsActive(card),
      )
    ) {
      return [];
    }
    const { lateral, forward } = HUNT_BALANCE.cards.superCannons;
    const ship = this.shipPosition();
    return [
      slotToWorld(slotMuzzle({ forward, lateral: -lateral }), ship.x, ship.y, this.facing),
      slotToWorld(slotMuzzle({ forward, lateral }), ship.x, ship.y, this.facing),
    ];
  }

  /** Quanto tempo a habilidade dos Super Canhões ainda tem, em ms. */
  /**
   * **A geometria do que as cartas desenham** (gate V28H.M).
   *
   * ## Porque isto vive na simulação e não na cena
   *
   * Pela mesma razão exacta que `superCannonPositions()` vive: a lâmina e o
   * feixe são desenhados EXACTAMENTE onde eles ferem, e a única maneira de
   * garantir isso é a cena ler a mesma medida que o dano usa. Uma segunda
   * expressão no renderer é como um feixe passa a ser pintado ao lado do sítio
   * onde ele cobra.
   *
   * Devolve `null` quando a carta não está a fazer nada - a cena desenha
   * exactamente o que ele devolve, e não guarda estado nenhum.
   */
  /**
   * **De onde o feixe da Lança Solar nasce** (gate V28H.N).
   *
   * A boca da nave: o centro do casco mais meio casco na direcção da mira. Uma
   * função e não uma expressão repetida, porque ela é lida em dois sítios - quem
   * cobra e quem desenha - e a única maneira de um feixe ser pintado onde ele
   * fere é os dois lerem o mesmo.
   */
  private solarLanceMuzzle(): { x: number; y: number } {
    const here = this.shipPosition();
    const aim = this.shipAim();
    const radius = this.shipSize() / 2;
    return { x: here.x + aim.x * radius, y: here.y + aim.y * radius };
  }

  /**
   * **A geometria do que as cartas desenham** (gates V28H.M e V28H.N).
   *
   * ## Porque isto vive na simulação e não na cena
   *
   * Pela mesma razão exacta que `superCannonPositions()` vive: a lâmina e o
   * feixe são desenhados EXACTAMENTE onde eles ferem, e a única maneira de
   * garantir isso é a cena ler a mesma medida que o dano usa. Uma segunda
   * expressão no renderer é como um feixe passa a ser pintado ao lado do sítio
   * onde ele cobra.
   *
   * Devolve `null` em cada campo que não tem nada a acontecer - a cena desenha
   * exactamente o que ele devolve, e não guarda estado nenhum.
   */
  cardVisuals(): HuntCardVisuals {
    const here = this.shipPosition();
    const radius = this.shipSize() / 2;
    let circle: HuntCardVisuals['circle'] = null;
    let beam: HuntCardVisuals['beam'] = null;
    let charge: HuntCardVisuals['charge'] = null;
    let sphere: HuntCardVisuals['sphere'] = null;
    /*
     * O Bastião lê-se da RANHURA e não da carta.
     *
     * O temporizador vive em `cardEffects.untargetable`, que é o mesmo sítio de
     * onde a fuga pergunta se a nave pode ser ferida - portanto o que se desenha
     * é literalmente o que decide o dano. Lê-lo da carta seria uma segunda fonte
     * de verdade sobre a mesma coisa.
     */
    const untargetable = this.cardEffects.untargetable;
    const bastion: HuntCardVisuals['bastion'] =
      untargetable.remainingMs <= 0
        ? null
        : {
            x: here.x,
            y: here.y,
            radius: radius * 1.9,
            progress: Math.max(
              0,
              Math.min(
                1,
                untargetable.remainingMs / (cardBalance('card_bastion')?.durationMs ?? 1),
              ),
            ),
          };

    for (const card of this.cards) {
      const balance = cardBalance(card.definitionId);
      if (balance === null) {
        continue;
      }
      /*
       * **O círculo é PASSIVO**, e por isso vem antes da pergunta sobre o tempo
       * activo: ele pertence à carta estar equipada, e não a ela estar ligada.
       */
      if (card.definitionId === 'card_blade_storm') {
        circle = {
          x: here.x,
          y: here.y,
          radius: radius * (balance.reachBps / 10_000),
          /* A serra só existe depois da carga - que esta carta não tem - e enquanto dura. */
          active: card.activeRemainingMs > 0 && card.chargeRemainingMs === 0,
        };
      }
      if (card.activeRemainingMs <= 0) {
        continue;
      }
      if (card.chargeRemainingMs > 0) {
        /*
         * A fracção JÁ CUMPRIDA, e a crescer de zero até um.
         *
         * É a mesma leitura que a força vai ter quando a tecla se soltar, o que
         * torna o anel uma promessa honesta: o que ele mostra é o que o feixe
         * vai valer se o dedo sair agora.
         */
        /* O tempo total é o DESTA arena (gate V28I.C), e o catálogo tem zero. */
        const total = this.chargeMsOf(card);
        const progress =
          total === 0 ? 1 : Math.max(0, Math.min(1, (total - card.chargeRemainingMs) / total));
        const kind = balance.effect === 'charged_projectile' ? 'sphere' : 'beam';
        charge = {
          x: here.x,
          y: here.y,
          progress,
          locksMovement: balance.chargeLocksMovement,
          kind,
        };
        if (kind === 'sphere') {
          /*
           * A bola nasce na BOCA e cresce com a carga, exactamente como no
           * duelo - e o raio sai da MESMA função que a vai lançar. Uma segunda
           * expressão aqui seria uma bola a mudar de tamanho no instante em que
           * a tecla se solta.
           */
          const muzzle = this.solarLanceMuzzle();
          sphere = {
            x: muzzle.x,
            y: muzzle.y,
            /*
             * O raio do CORPO, e já não o da explosão (gate V28I.C): nesta arena
             * é ele que se desenha, e a mesma medida serve o brilho e a colisão.
             * Quem desenha recebe o número pronto e não o multiplica por nada.
             */
            radius: cardSphereRadiusFor(
              this.fireballBodyRadius(),
              /*
               * A fracção CRUA, sem o chão da carga mínima (gate V28H.W): com
               * ele, a bola ficava parada no tamanho mínimo durante o primeiro
               * quarto da carga e só depois começava a crescer.
               */
              cardChargeProgressBps(HUNT_FIREBALL_CHARGE_MS, card.chargeRemainingMs),
            ),
          };
        }
        continue;
      }
      if (card.definitionId === 'card_solar_lance') {
        const aim = this.shipAim();
        const muzzle = this.solarLanceMuzzle();
        beam = {
          x: muzzle.x,
          y: muzzle.y,
          angle: Math.atan2(aim.y, aim.x),
          length: radius * (HUNT_SOLAR_LANCE_REACH_BPS / 10_000),
          /* A espessura TOTAL, graduada pela carga: a simulação compara metade. */
          width:
            cardBeamWidthFor(
              radius * (HUNT_SOLAR_LANCE_WIDTH_BPS / 10_000),
              card.chargeStrengthBps,
            ) * 2,
          strengthBps: card.chargeStrengthBps,
        };
      }
    }
    return { circle, beam, charge, sphere, bastion };
  }

  superCannonRemainingMs(): number {
    const card = this.cards.find((entry) => entry.definitionId === 'card_super_cannons');
    return card ? card.activeRemainingMs : 0;
  }

  /** O estado dos três espaços, para a cena e para as provas. */
  getCardAbilities(): readonly CardAbilityState[] {
    return this.cards;
  }

  /**
   * **Uma salva dos Super Canhões: exactamente dois projéteis.**
   *
   * ## Um por canhão, e não `projectilesPerCannon`
   *
   * O contrato é explícito: "cada ataque dispara exactamente um projétil por
   * canhão". A raridade do canhão equipado decide quantos projéteis o CASCO
   * emite por boca; estes dois são outra arma, e emitem um cada.
   *
   * ## Dano-base autoritativo, e nada mais
   *
   * `baseDamageMilli()`: sem a Chama, sem os Corações, sem o ataque-bónus do
   * equipamento e sem a percentagem somada que o tiro do casco leva. O contrato
   * diz "exactamente o dano-base autoritativo da nave", e é isso.
   *
   * ## As joias, com a semântica de cada família preservada
   *
   * A rolagem POR SALVA acontece uma vez, antes do laço - é a unidade da Bomba
   * e a do Coração Vital. A rolagem POR PROJÉTIL acontece dentro, uma por cada
   * um dos dois - é a unidade do Ciclone. E o veneno e o sangramento continuam
   * a exigir um acerto directo, que só acontece em `applyHit`.
   *
   * A origem é `temporary_cannon`, e é ela que abre a porta: tudo o que estas
   * duas salvas gerarem nasce com origem `jewel`, que não origina nada. A
   * recursão fecha na tabela, e não numa condição.
   */
  private fireSuperCannonVolley(targetId: number): void {
    const target = this.findEntity(targetId);
    if (!target) {
      return;
    }
    const muzzles = this.superCannonMuzzles();
    if (muzzles.length === 0) {
      return;
    }
    const damageMilli = BigInt(this.baseDamageMilli());
    const ids: number[] = [];
    const origins: { x: number; y: number }[] = [];

    /* A rolagem da SALVA, uma vez - a unidade da Bomba e do Míssil. */
    const { bomb, missile } = this.rollVolleyProcs();
    /* E a do Coração Vital, do seu fluxo, uma vez por salva. */
    this.procVitalHeal();

    const born: ProjectileEntity[] = [];
    for (const muzzle of muzzles) {
      const dx = target.x - muzzle.x;
      const dy = target.y - muzzle.y;
      const length = Math.hypot(dx, dy) || 1;
      const projectile: ProjectileEntity = {
        id: this.nextEntityId++,
        x: muzzle.x,
        y: muzzle.y,
        dirX: dx / length,
        dirY: dy / length,
        damageMilli,
        /* Uma rolagem POR PROJÉTIL: a unidade do Ciclone, e a origem é a nova. */
        jewelProc: this.rollTemporaryCannonProc(),
        targetId,
        orphaned: false,
      };
      this.projectiles.push(projectile);
      born.push(projectile);
      ids.push(projectile.id);
      origins.push({ x: muzzle.x, y: muzzle.y });
    }

    /*
     * A bomba e o míssil da salva ocupam o PRIMEIRO projétil.
     *
     * "O mais central" não tem significado com dois projéteis simétricos - os
     * dois estão à mesma distância do eixo - portanto o desempate é o índice,
     * que é exactamente o desempate que `centralVolleyIndex` já usaria.
     */
    const chosen = born[0];
    if (bomb !== null && chosen) {
      const mutable = chosen as {
        jewelProc: JewelProcSelection | null;
        atomicBomb?: AtomicBombSelection;
        speed?: number;
      };
      mutable.jewelProc = null;
      mutable.atomicBomb = bomb;
      mutable.speed = HUNT_BOMB_SPEED;
    }
    if (missile !== null) {
      this.launchMissile(missile);
    }

    this.events.push({
      type: 'card-volley',
      definitionId: 'card_super_cannons',
      projectileIds: ids,
      origins,
      heading: this.facing,
    });
  }

  /**
   * A rolagem de proc de UM projétil de armamento temporário.
   *
   * Idêntica à do canhão em tudo menos na ORIGEM, e a origem é o assunto: ela
   * atravessa a tabela fechada de `damage/origin.ts`, que é o que decide se
   * este tiro pode originar alguma coisa e o quê.
   *
   * Consome SEMPRE dois valores do mesmo fluxo, com joia ou sem ela, pela mesma
   * razão que `rollOutgoingProc` consome: consumir condicionalmente faria a
   * sequência depender do equipamento.
   */
  private rollTemporaryCannonProc(): JewelProcSelection | null {
    const roll = this.jewelProcRng.nextIntInclusive(0, PROC_CHANCE_DENOMINATOR - 1);
    const entropy = this.jewelProcRng.nextIntInclusive(0, 0xffff_ffff);
    return selectOutgoingProc(
      this.config.jewels ?? EMPTY_JEWEL_SOCKET_PROFILE,
      'temporary_cannon',
      roll,
      entropy,
      BigInt(this.baseDamageMilli()),
    );
  }

  /** Projectiles emitted by ONE cannon per shot, from the equipped rarity. */
  projectilesPerCannon(): number {
    return CANNON_CATALOG[this.config.cannonRarity].projectilesPerCannon;
  }

  /**
   * A rolagem de proc de UM projétil de canhão, do fluxo desta corrida.
   *
   * Consome SEMPRE dois valores - a rolagem e a entropia - mesmo sem joia
   * nenhuma. Consumir condicionalmente faria a sequência depender do
   * equipamento, e duas caças com a mesma semente deixariam de ser a mesma caça
   * assim que alguém encaixasse uma joia.
   *
   * Uma rolagem por PROJÉTIL e não por salva: em KENOCYBER cada projétil é um
   * disparo discreto com o seu próprio acerto, o seu próprio número de dano e a
   * sua própria oportunidade. Uma rolagem por salva daria a um canhão de sete
   * bocas a mesma chance que a um de uma.
   */
  private rollOutgoingProc(): JewelProcSelection | null {
    const roll = this.jewelProcRng.nextIntInclusive(0, PROC_CHANCE_DENOMINATOR - 1);
    const entropy = this.jewelProcRng.nextIntInclusive(0, 0xffff_ffff);
    return selectOutgoingProc(
      this.config.jewels ?? EMPTY_JEWEL_SOCKET_PROFILE,
      'cannon',
      roll,
      entropy,
      /*
       * O dano-base AUTORITATIVO desta nave, NESTE instante.
       *
       * `baseDamageMilli()` e nada mais: sem a Chama, sem os Corações, sem o
       * ataque-bónus do equipamento, sem crítico e sem outro proc. Lido aqui,
       * no nascimento, para que a magnitude fique congelada com a seleção -
       * comprar um ponto com o tiro no ar deixa de mudar o corte.
       */
      BigInt(this.baseDamageMilli()),
    );
  }

  /**
   * **A rolagem da SALVA**, do fluxo por salva desta corrida.
   *
   * Uma por salva e **não** uma por projétil, e esta é a única diferença de
   * unidade entre a Bomba e o Ciclone. A razão está escrita em
   * `damage/atomic-bomb.ts`, e a consequência é a que o §2 exige: um canhão de
   * vinte e oito bocas tem exatamente a mesma chance de bomba que um de uma, e o
   * Pulso do Dragão aumenta o número de salvas ao longo do tempo sem mudar a
   * chance de cada uma.
   *
   * Consome SEMPRE dois valores, com joia ou sem ela.
   */
  private rollVolleyProcs(): {
    readonly bomb: AtomicBombSelection | null;
    readonly missile: MissileSelection | null;
  } {
    const roll = this.volleyBombRng.nextIntInclusive(0, PROC_CHANCE_DENOMINATOR - 1);
    const entropy = this.volleyBombRng.nextIntInclusive(0, 0xffff_ffff);
    const jewels = this.config.jewels ?? EMPTY_JEWEL_SOCKET_PROFILE;
    /* O dano-base AUTORITATIVO desta nave, e mais nada. A mesma leitura. */
    const baseMilli = BigInt(this.baseDamageMilli());

    /*
     * FERRAMENTA DE DESENVOLVIMENTO, e ela não mexe em número nenhum.
     *
     * A rolagem foi feita e os dois valores foram consumidos acima, portanto o
     * fluxo avança exatamente como avançaria sem isto - a sequência de uma
     * corrida com a ferramenta ligada é a mesma de uma sem. O que muda é a
     * COMPARAÇÃO: em vez da rolagem real usa-se o INÍCIO do intervalo do proc
     * pedido, que por construção cai dentro dele e fora de todos os outros.
     *
     * Dano, colisão, raio, alvo, giro e resultado saem todos do caminho normal.
     * Uma nave sem a joia pedida continua a não produzir coisa nenhuma - forçar
     * não acrescenta a joia, e não é razão para acionar a joia do lado.
     */
    const forced = this.spendForcedVolleyProc();
    if (forced !== null) {
      const interval = volleyProcInterval(jewels, forced);
      if (interval === null) {
        return { bomb: null, missile: null };
      }
      return forced === 'atomic_blast'
        ? {
            bomb: selectVolleyBomb(jewels, 'cannon', interval.from, entropy, baseMilli),
            missile: null,
          }
        : {
            bomb: null,
            missile: selectVolleyMissile(jewels, 'cannon', interval.from, entropy, baseMilli),
          };
    }

    /*
     * **Uma rolagem, dois intervalos DISJUNTOS, no máximo um proc.**
     *
     * A mesma rolagem é comparada contra os dois intervalos, e eles não se
     * sobrepõem - a Bomba ocupa `[0, w)` e o míssil `[w, w + m)`, na ordem
     * canónica do registo. Não são dois sorteios independentes, e a exclusão
     * mútua é uma propriedade da aritmética em vez de uma verificação que alguém
     * tem de se lembrar de escrever.
     *
     * `selectVolleyBomb` recebe exatamente os argumentos que sempre recebeu, e é
     * por isso que uma caçada sem o Arsenal Ígneo corre tique a tique como corria
     * antes deste gate.
     */
    return {
      bomb: selectVolleyBomb(jewels, 'cannon', roll, entropy, baseMilli),
      missile: selectVolleyMissile(jewels, 'cannon', roll, entropy, baseMilli),
    };
  }

  /**
   * **A rolagem do Coração Vital, e ela é INDEPENDENTE da loteria ofensiva.**
   *
   * Um fluxo próprio, um intervalo próprio e uma rolagem própria, na unidade da
   * SALVA. O §"é um proc de suporte independente: não consome nem bloqueia a
   * loteria ofensiva" é cumprido por construção - esta rolagem não é comparada
   * contra nenhum intervalo da Bomba nem do Míssil, e nenhuma delas é comparada
   * contra o desta - portanto uma salva pode legitimamente lançar um míssil E
   * curar.
   *
   * Consome UM valor por salva, sempre. Sem entropia: a variação de apresentação
   * deriva da identidade autoritativa do evento - o tique - e um segundo valor
   * consumido seria estado que a instantânea teria de justificar sem ninguém o
   * ler.
   *
   * Na caça há uma nave só, portanto a lei de seleção de alvo escolhe sempre a
   * própria. Não há aqui um ramo de autocura: há a MESMA função de `vital-heart.ts`
   * a responder com um único candidato, que é o que faz a caça e o duelo terem a
   * mesma regra em vez de duas parecidas.
   */
  private procVitalHeal(): void {
    const roll = this.vitalHeartRng.nextIntInclusive(0, PROC_CHANCE_DENOMINATOR - 1);
    const jewels = this.config.jewels ?? EMPTY_JEWEL_SOCKET_PROFILE;
    /*
     * FERRAMENTA DE DESENVOLVIMENTO, e ela não mexe em número nenhum.
     *
     * A rolagem foi feita e o valor foi consumido acima, portanto a sequência de
     * uma corrida com a ferramenta ligada é idêntica à de uma sem - ligar isto
     * não desloca um spawn, um temporizador de boss, um proc nem uma bomba. O que
     * é substituído é o valor com que a rolagem é COMPARADA, por zero, que cai
     * sempre abaixo do numerador.
     *
     * **Não acrescenta a joia.** Uma nave sem o Coração Vital continua a não
     * curar coisa nenhuma, porque `selectVitalHeal` devolve `null` quando o
     * perfil não a tem - forçar a rolagem não é forçar a posse. E não muda a
     * magnitude: ela sai do dano-base autoritativo, como qualquer outra.
     */
    const forced = this.forceNextVitalHeal;
    this.forceNextVitalHeal = false;
    const selection: VitalHealSelection | null = selectVitalHeal(
      jewels,
      'cannon',
      forced ? 0 : roll,
      /* O dano-base AUTORITATIVO desta nave, e mais nada. A mesma leitura. */
      BigInt(this.baseDamageMilli()),
    );
    if (selection === null) {
      return;
    }
    /*
     * A SELEÇÃO DE ALVO e a APLICAÇÃO são uma decisão só, neste instante.
     *
     * O §"troca de alvo entre disparo e evento não existe" é isto: não há um
     * pedido guardado que alguém resolva mais tarde, e portanto não há janela
     * onde o alvo possa mudar.
     */
    const application = applyVitalHealFractional({
      requestedMilli: selection.requestedMilli,
      currentHealth: this.health,
      maxHealth: this.maxHealth(),
    });
    if (application.appliedMilli <= 0n) {
      /*
       * Vida cheia, ou uma nave em baixo: a rolagem foi consumida na mesma - é o
       * que mantém a sequência independente do estado - e não há cura, não há
       * overheal e não há evento. Um facto que não aconteceu não é anunciado.
       */
      return;
    }
    this.health += vitalHealPoints(application.appliedMilli);
    const ship = this.shipPosition();
    this.events.push({
      type: 'vital-heal',
      tick: this.currentTick(),
      /*
       * A cura REALMENTE aplicada, em milli inteiro e sem reconversão: ela nasceu
       * em milli e é isso que o `+TOTAL` soma. `Number` é seguro aqui porque o
       * clamp já a provou não maior do que a vida em falta.
       */
      healedMilli: Number(application.appliedMilli),
      x: ship.x,
      y: ship.y,
    });
  }

  /**
   * DEV ONLY - a PRÓXIMA salva cura, se a nave tiver o Coração Vital.
   *
   * A gémea de {@link devForceNextVolleyBomb}, com as mesmas garantias e pela
   * mesma razão: substitui uma comparação, gasta-se numa salva, não acrescenta a
   * joia, não muda a magnitude e não desloca a sequência de números da corrida.
   *
   * Guardada pelo chamador (modo de depuração), e não concede coisa nenhuma: uma
   * sessão de depuração já não é económica.
   */
  devForceNextVitalHeal(): void {
    this.forceNextVitalHeal = true;
  }

  /**
   * Que proc por salva está forçado agora, gastando a bandeira.
   *
   * A Bomba primeiro quando as duas ferramentas estão armadas ao mesmo tempo -
   * uma escolha determinística, e a única forma de duas afordâncias
   * independentes não produzirem dois procs numa salva que só permite um.
   */
  private spendForcedVolleyProc(): VolleyProcId | null {
    if (this.forceNextVolleyBomb) {
      this.forceNextVolleyBomb = false;
      return 'atomic_blast';
    }
    if (this.forceNextVolleyMissile) {
      this.forceNextVolleyMissile = false;
      return 'missile';
    }
    return null;
  }

  private fireVolley(targetId: number): void {
    const target = this.findEntity(targetId);
    if (!target) {
      return;
    }
    const dirX = Math.cos(this.facing);
    const dirY = Math.sin(this.facing);
    // Perpendicular to the shot: the axis the projectiles of a single cannon
    // are spread along, so they leave side by side and parallel rather than
    // converging. The homing bends each of them toward the target afterwards.
    const perpX = -dirY;
    const perpY = dirX;
    const perCannon = this.projectilesPerCannon();
    const spacing = HUNT_BALANCE.ship.projectileSpacing;
    /*
     * Decidido no NASCIMENTO do tiro, exato, e nunca relido no impacto.
     *
     * Um projétil que perguntasse ao perfil quando bate escalaria com o que a
     * nave tivesse entretanto - um ponto alocado, uma fusão, um coração
     * incorporado a meio - e o jogador veria um tiro mudar de ideias em voo.
     */
    const damageMilli = this.totalDamagePerProjectileMilli();
    const muzzles = this.muzzlePositions();
    const ids: number[] = [];
    /*
     * **A rolagem da SALVA, e ela acontece exatamente aqui - uma vez.**
     *
     * Antes do laço, e é isso que a torna por salva. Dentro do laço seria uma
     * por projétil, que é a unidade do Ciclone e não a desta joia.
     */
    const { bomb, missile } = this.rollVolleyProcs();
    /*
     * **E a rolagem do Coração Vital, do SEU fluxo, uma vez por salva.**
     *
     * Aqui e não dentro do laço, pela mesma razão que a da Bomba está aqui: a
     * unidade é a SALVA. Um canhão de vinte e oito bocas tem exatamente a mesma
     * chance de curar que um de uma.
     *
     * Depois da loteria ofensiva e antes dos projéteis nascerem, porque a cura
     * não depende de acertar nem sequer de ter saído: ela acontece no disparo. E
     * porque uma posição fixa na ordem é o que torna a sequência reprodutível.
     */
    this.procVitalHeal();
    const born: ProjectileEntity[] = [];
    /*
     * A distância de cada projétil ao EIXO CENTRAL da salva.
     *
     * Medida aqui porque é aqui que se sabe onde cada um nasceu. `perp` é a
     * perpendicular ao disparo, portanto o produto interno com ela é exatamente
     * "quanto ao lado do eixo da nave" - e cobre os dois espalhamentos ao mesmo
     * tempo: o leque de UMA boca e a separação ENTRE bocas. Escolher pelo índice
     * dentro de um canhão daria o centro daquele canhão, e não o da salva.
     *
     * Só multiplicações e somas: aritmética IEEE 754 exatamente arredondada, que
     * dá o mesmo bit no Node e no Chromium. Nenhuma trigonometria entra na
     * escolha.
     */
    const lateral: number[] = [];
    const ship = this.shipPosition();

    // Every visible cannon fires from its OWN muzzle, simultaneously.
    for (const muzzle of muzzles) {
      for (let i = 0; i < perCannon; i += 1) {
        const offset = (i - (perCannon - 1) / 2) * spacing;
        const x = muzzle.x + perpX * offset;
        const y = muzzle.y + perpY * offset;
        const projectile: ProjectileEntity = {
          id: this.nextEntityId++,
          x,
          y,
          dirX,
          dirY,
          damageMilli,
          /*
           * A LOTERIA, e ela acontece exatamente aqui.
           *
           * Uma rolagem por projétil, do fluxo autoritativo desta corrida, no
           * instante em que ele nasce. Nunca no impacto: um sorteio no impacto
           * teria de reler o perfil, e o perfil pode ter mudado entre o disparo
           * e a batida.
           */
          jewelProc: this.rollOutgoingProc(),
          targetId,
          orphaned: false,
        };
        this.projectiles.push(projectile);
        born.push(projectile);
        lateral.push((x - ship.x) * perpX + (y - ship.y) * perpY);
        ids.push(projectile.id);
      }
    }

    if (bomb !== null && born.length > 0) {
      /*
       * **Exatamente um projétil da salva torna-se a bomba.**
       *
       * O mais próximo do eixo central, com desempate pelo menor índice - e
       * nunca um projétil ADICIONAL: `born` já está no array, e o que acontece
       * aqui é uma substituição no lugar.
       *
       * Ele perde o corte que a loteria lhe tinha dado. É o §2: a bomba ocupa a
       * oportunidade ofensiva DAQUELE projétil, e os outros vinte e sete
       * continuam a obedecer às regras que já tinham.
       */
      const index = centralVolleyIndex(lateral);
      const chosen = born[index];
      if (chosen === undefined) {
        throw new RangeError(`the central projectile of a volley of ${born.length} is missing`);
      }
      const mutable = chosen as {
        jewelProc: JewelProcSelection | null;
        atomicBomb?: AtomicBombSelection;
        remainingMs?: number;
        speed?: number;
      };
      mutable.jewelProc = null;
      mutable.atomicBomb = bomb;
      /* A sua própria velocidade - 80 % - e o seu próprio fim de vida. */
      mutable.speed = HUNT_BOMB_SPEED;
      mutable.remainingMs = HUNT_BALANCE.projectiles.bomb.lifetimeMs;
    }

    this.events.push({
      type: 'volley',
      projectileIds: ids,
      origins: muzzles.map((muzzle) => ({ x: muzzle.x, y: muzzle.y })),
      heading: this.facing,
    });

    /*
     * **O MÍSSIL, e ele nasce ADICIONALMENTE** (gate V28G.L).
     *
     * Depois da salva e depois do evento dela, porque é isso que ele é: a salva
     * saiu inteira - com o seu dano, o seu proc e a sua arte intactos, e com os
     * mesmos `projectileIds` que a cena sempre recebeu - e o míssil aparece ao
     * lado dela. Nenhum tiro comum é substituído, ao contrário do que a Bomba faz
     * a um deles vinte linhas acima.
     */
    if (missile !== null) {
      this.launchMissile(missile);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  O Arsenal Ígneo (gate V28G.L)                                     */
  /* ------------------------------------------------------------------ */

  /**
   * **Põe um míssil no ar.** Escolhe o alvo, aponta-o e regista-o.
   *
   * ## O alvo é escolhido AQUI, e uniformemente
   *
   * Ao nascer, e não no primeiro tique: um míssil sem alvo durante um tique
   * voaria um passo numa direção que ninguém decidiu. A escolha é uniforme entre
   * TODOS os inimigos vivos - qualquer mob, e o boss quando ele já desce - e sai
   * do fluxo de alvos desta corrida, portanto é determinística e reproduzível
   * pela re-simulação do servidor.
   *
   * Uma salva sem inimigo vivo nenhum não lança míssil. A alternativa seria
   * criá-lo para o explodir no mesmo tique, o que só produziria fogo que ninguém
   * pediu - e a caça nem sequer dispara sem alvo, por isso é um caso defensivo.
   *
   * ## A direção inicial é a MIRA da nave
   *
   * Não a direção do alvo. O míssil sai apontado para onde a salva saiu, e é a
   * perseguição que o curva a partir daí, a 240 graus por segundo. Nascer já
   * apontado ao alvo seria uma viragem instantânea, que é a primeira coisa que o
   * contrato proíbe.
   *
   * ## O teto, e a política que o contrato manda
   *
   * Derivado da cadência REAL desta corrida em vez de escrito. A caça não tem
   * tecto de cadência - o `CLAUDE.md` proíbe capar um atributo concedido por um
   * item, e a Speed Fast ao nível 10 sozinha vale +30,72 ataques por segundo -
   * portanto um número fixo aqui ou recusaria uma nave legal ou seria arbitrário.
   * O que existe é o pior caso legítimo DESTA nave: uma oportunidade por salva,
   * a cadência que ela tem agora, nove segundos de vida.
   *
   * Alcançá-lo é um defeito, e a defesa larga o mais ANTIGO - o que está mais
   * perto de expirar sozinho - em silêncio, sem explosão e sem dano: nada
   * aconteceu no mundo do jogo, e desenhar fogo por causa de um limite interno
   * seria mostrar ao jogador um facto que não existe.
   */
  private launchMissile(selection: MissileSelection): void {
    const target = this.chooseMissileTarget();
    if (target === null) {
      return;
    }
    const ship = this.shipPosition();
    this.missiles.push({
      id: this.nextEntityId++,
      x: ship.x,
      y: ship.y,
      dirX: Math.cos(this.facing),
      dirY: Math.sin(this.facing),
      damageMilli: selection.damageMilli,
      entropy: selection.entropy,
      targetId: target.id,
      remainingMs: MISSILE_LIFETIME_MS,
    });
    while (this.missiles.length > this.missileCeiling()) {
      this.missiles.shift();
    }
  }

  /**
   * O pior caso legítimo de mísseis simultâneos, para a cadência de AGORA.
   *
   * `ceil(salvas por segundo x 9) + 1`. O `+1` é a salva do instante zero, e
   * esquecê-lo descontaria exatamente o míssil que nasce no tique em que o mais
   * antigo morre. Recalculado a cada lançamento porque uma queda ou uma fusão
   * reconfiguram a MESMA sessão e podem mudar a cadência a meio.
   */
  private missileCeiling(): number {
    const perSecond = Math.max(0, this.totalAttacksPerSecond());
    return Math.ceil(perSecond * (MISSILE_LIFETIME_MS / 1000)) + 1;
  }

  /**
   * Um inimigo vivo, escolhido uniformemente do fluxo desta corrida.
   *
   * Mobs e boss na mesma lista e por ordem de id, o que é o que faz o índice
   * sorteado significar sempre o mesmo inimigo numa re-simulação. Um boss que
   * ainda se revela é intocável, exatamente como é para uma bala e para a Bomba:
   * um míssil não pode escolher o que um tiro não alcança.
   */
  private chooseMissileTarget(): MobEntity | BossEntity | null {
    const candidates: (MobEntity | BossEntity)[] = this.mobs.filter((mob) => mob.health > 0);
    if (this.boss && this.boss.phase === 'descending' && this.boss.health > 0) {
      candidates.push(this.boss);
    }
    if (candidates.length === 0) {
      return null;
    }
    candidates.sort((left, right) => left.id - right.id);
    const index = chooseMissileTargetIndex(
      candidates.length,
      this.missileTargetRng.nextIntInclusive(0, candidates.length - 1),
    );
    return index === null ? null : (candidates[index] ?? null);
  }

  /**
   * **Um tique de voo de cada míssil.** Perseguir, virar, andar, e resolver.
   *
   * Separado de {@link HuntSimulation.tickProjectiles} porque são entidades
   * diferentes com regras que não se tocam - e mantê-las separadas é o que faz
   * este gate não poder alterar um tiro comum, uma bala de pet ou uma bomba por
   * acidente.
   */
  private tickMissiles(dt: number): void {
    const survivors: MissileEntity[] = [];
    for (const missile of this.missiles) {
      if (this.stepMissile(missile, dt)) {
        survivors.push(missile);
      }
    }
    this.missiles = survivors;
  }

  /** Devolve se o míssil continua no ar depois deste tique. */
  private stepMissile(missile: MissileEntity, dt: number): boolean {
    missile.remainingMs -= dt * 1000;
    if (missile.remainingMs <= 0) {
      /* **Nove segundos, e acabou.** Explode onde está, e não fere ninguém. */
      this.explodeMissile(missile, null);
      return false;
    }
    /*
     * **O RETARGET, e ele acontece antes de qualquer movimento.**
     *
     * Um alvo que morreu já não é um alvo, e continuar a persegui-lo por um
     * tique poria o míssil a curvar na direção de um cadáver. A escolha nova sai
     * do mesmo fluxo e é uniforme entre os inimigos vivos, como a primeira foi.
     *
     * Sem inimigo nenhum vivo, o contrato é explícito: explode visualmente e
     * desaparece. Não fica à espera, não paira e não escolhe uma direção.
     */
    let target = missile.targetId === null ? null : this.findEntity(missile.targetId);
    if (!target || target.health <= 0) {
      const next = this.chooseMissileTarget();
      if (next === null) {
        this.explodeMissile(missile, null);
        return false;
      }
      missile.targetId = next.id;
      target = next;
    }
    /*
     * A viragem, limitada a 240 graus por segundo.
     *
     * A mesma forma que a perseguição de um tiro comum usa - diferença de
     * ângulos, normalizada para `[-pi, pi]`, cortada pelo passo do tique - com o
     * limite DESTA entidade. Nunca um salto para o ângulo desejado: o corte é o
     * que faz a trajetória ser um arco.
     */
    /*
     * **O ponto de INTERCEPTAÇÃO, e não onde o alvo está** (gate V28G.L.1).
     *
     * Um mob desce a uma velocidade conhecida, portanto o encontro é previsível:
     * o tempo até lá é a distância a dividir pela velocidade do míssil, e o ponto
     * é onde o mob estará nesse instante. Perseguir a posição atual é chegar
     * sempre atrasado e arrastar-se atrás dele.
     */
    const travel = HUNT_MISSILE_SPEED * dt;
    const aim = huntMissileAimPoint(missile, target, this.enemyVelocity(target));
    const desiredX = aim.x - missile.x;
    const desiredY = aim.y - missile.y;
    const desiredLen = Math.hypot(desiredX, desiredY);
    if (desiredLen > 0) {
      const currentAngle = Math.atan2(missile.dirY, missile.dirX);
      const desiredAngle = Math.atan2(desiredY / desiredLen, desiredX / desiredLen);
      let diff = desiredAngle - currentAngle;
      while (diff > Math.PI) diff -= 2 * Math.PI;
      while (diff < -Math.PI) diff += 2 * Math.PI;
      const maxTurn = HUNT_MISSILE_MAX_TURN_RAD_PER_SECOND * dt;
      /*
       * **E a regra que mata a órbita.**
       *
       * Saturado no limite de giro, o míssil descreve uma circunferência de raio
       * `v / w`. Um alvo DENTRO dela nunca é alcançado, por mais voltas que ele
       * dê - não lhe falta vontade, falta-lhe raio. Nesse caso ele vira ao
       * CONTRÁRIO: a distância cresce, o alvo sai do círculo, e a perseguição
       * volta a poder fechar.
       *
       * O giro continua limitado aos mesmos 240 graus por segundo e a velocidade
       * continua a mesma. O que muda é o sinal.
       */
      const inside = huntAimIsInsideTurn(missile, aim, this.missileCaptureRadius(target));
      const turn = inside
        ? -Math.sign(diff || 1) * maxTurn
        : Math.max(-maxTurn, Math.min(maxTurn, diff));
      const newAngle = currentAngle + turn;
      missile.dirX = Math.cos(newAngle);
      missile.dirY = Math.sin(newAngle);
    }
    /*
     * **A colisão é VARRIDA sobre o passo inteiro.**
     *
     * O ponto de chegada é calculado primeiro e a travessia é testada contra o
     * SEGMENTO, não contra o ponto: a correção da órbita põe o míssil a atacar de
     * frente e à velocidade máxima, e um alvo pequeno cabe inteiro entre duas
     * amostras.
     */
    const fromX = missile.x;
    const fromY = missile.y;
    missile.x += missile.dirX * travel;
    missile.y += missile.dirY * travel;

    /*
     * **O LIMITE DA ARENA bloqueia-o.** Explode ali, e não fere ninguém.
     *
     * O limite exato e não a margem de despawn que um tiro comum usa: um tiro que
     * sai do ecrã desaparece em silêncio, e este contrato pede que o míssil
     * EXPLODA ao tocar o limite - portanto o limite tem de ser o que o jogador vê.
     */
    if (
      missile.x < 0 ||
      missile.y < 0 ||
      missile.x > HUNT_BALANCE.arena.width ||
      missile.y > HUNT_BALANCE.arena.height
    ) {
      this.explodeMissile(missile, null);
      return false;
    }

    /*
     * **Qualquer inimigo hostil**, e não só o que ele persegue.
     *
     * O contrato: "ao tocar qualquer inimigo hostil durante o percurso, atinge
     * somente aquele inimigo". O mais próximo, com desempate pelo MENOR ID -
     * escrito e não deixado à ordem do array, porque o boss é considerado depois
     * dos mobs e não é o último a nascer.
     */
    let best: { entity: MobEntity | BossEntity; at: number } | null = null;
    const consider = (enemy: MobEntity | BossEntity) => {
      if (enemy.health <= 0) {
        return;
      }
      const reach = this.missileCaptureRadius(enemy);
      /*
       * O instante do toque sobre o SEGMENTO, e não a distância no fim dele.
       * `null` quer dizer que o segmento inteiro passou ao lado.
       */
      const at = huntSweepCircle(fromX, fromY, missile.x - fromX, missile.y - fromY, enemy, reach);
      if (at === null) {
        return;
      }
      if (best === null || at < best.at || (at === best.at && enemy.id < best.entity.id)) {
        best = { entity: enemy, at };
      }
    };
    for (const mob of this.mobs) {
      consider(mob);
    }
    if (this.boss && this.boss.phase === 'descending') {
      consider(this.boss);
    }
    const struck = best as { entity: MobEntity | BossEntity; at: number } | null;
    if (struck !== null) {
      /*
       * Ele pára ONDE tocou. Um míssil que detonasse no fim do passo mostraria
       * fogo dezoito unidades adiante do sítio onde acertou.
       */
      missile.x = fromX + (missile.x - fromX) * struck.at;
      missile.y = fromY + (missile.y - fromY) * struck.at;
      this.explodeMissile(missile, struck.entity);
      return false;
    }
    return true;
  }

  /**
   * **A explosão do míssil.** Um golpe, ou nenhum, e o anúncio.
   *
   * ## As regras, e nenhuma delas é um caso especial
   *
   * - **só o inimigo atingido leva**, e leva uma vez. Não há área, não há
   *   vizinhos e não há segunda passagem;
   * - **contra o limite ou por fim de vida não há alvo nenhum**, e a ausência
   *   escreve-se `direct === null`. Nenhuma dessas explosões causa dano;
   * - **não há empurrão.** Nem aqui nem no duelo: o orçamento inteiro desta joia
   *   é pago em dano, e é por isso que a magnitude é 90 % em vez dos 112,5 % da
   *   Bomba, que compra controlo com os outros 25 %.
   *
   * ## A origem é `jewel`, e o que isso desliga sozinho
   *
   * Tudo. `damageOriginMayTriggerJewel('jewel')` é falso, e é essa tabela que faz
   * o golpe do míssil não acionar outro proc, não acionar o Roubo de Vida, não
   * criar Ciclone e não lançar um segundo míssil. A profundidade de recursão é um
   * porque não existe aresta no grafo que lhe dê duas.
   *
   * ## O raio anunciado é ZERO, e isso é a verdade
   *
   * O evento de explosão carrega o raio que a SIMULAÇÃO usou, e o do míssil não
   * existe. Zero diz "não houve área", e é o que impede uma cena de desenhar um
   * círculo que pareça uma zona de dano.
   */
  private explodeMissile(missile: MissileEntity, direct: MobEntity | BossEntity | null): void {
    let hits = 0;
    if (direct !== null) {
      /* A origem que fecha o grafo. Nunca `cannon`, e nunca `pet`. */
      this.applyHit(direct, missile.damageMilli, 'jewel', missile.id);
      hits = 1;
    }
    this.events.push({
      type: 'explosion',
      x: missile.x,
      y: missile.y,
      /* Sem área: o raio é mesmo zero, e a cena desenha fogo compacto. */
      radius: 0,
      hits,
      projectileId: missile.id,
      effectId: MISSILE_EFFECT,
    });
  }

  /** Os mísseis em voo, para a cena os desenhar (gate V28G.L). */
  getMissiles(): readonly MissileEntity[] {
    return this.missiles;
  }

  /**
   * A que distância deste inimigo o míssil o toca.
   *
   * O raio do míssil mais o do INIMIGO - e é a segunda parcela que o contrato
   * exige: um boss é dez vezes maior do que um mob, e uma captura que só olhasse
   * para o centro fá-lo-ia atravessar-lhe metade do corpo antes de acertar.
   */
  private missileCaptureRadius(enemy: MobEntity | BossEntity): number {
    return HUNT_MISSILE_HIT_RADIUS + enemy.size * HUNT_BALANCE.mobs.hitRadiusFraction;
  }

  /**
   * O deslocamento por segundo de um inimigo, para o míssil o prever.
   *
   * Derivado das MESMAS constantes que os movem - a velocidade do mapa para um
   * mob, e a fração de descida para o boss - em vez de guardado. Um segundo sítio
   * a lembrar-se da velocidade de um mob seria um sítio para divergir do
   * primeiro, e a previsão passaria a apontar para onde ninguém vai.
   *
   * Os inimigos desta caça descem: a componente horizontal é zero, e escrevê-la
   * é o que torna a previsão exata em vez de aproximada.
   */
  private enemyVelocity(enemy: MobEntity | BossEntity): { readonly x: number; readonly y: number } {
    if (enemy.kind === 'boss') {
      return {
        x: 0,
        y: HUNT_BALANCE.mobs.descentSpeed * HUNT_BALANCE.boss.descentSpeedFraction,
      };
    }
    return { x: 0, y: HUNT_BALANCE.mobs.descentSpeed };
  }

  /**
   * **DEV ONLY — a PRÓXIMA salva lança um míssil**, se a nave tiver a joia.
   *
   * Gémea de {@link HuntSimulation.devForceNextVolleyBomb}, com as mesmas
   * garantias e pela mesma razão: a chance real ao topo do contrato é de 10 % por
   * salva, e esperar por ela numa avaliação manual é uma moeda ao ar.
   *
   * O que ela muda é UMA comparação. A rolagem continua a sair do fluxo, portanto
   * ligar isto não desloca um spawn, um temporizador de boss nem um proc - e o
   * dano, o alvo, a velocidade, o giro, a colisão e o tempo de vida saem todos do
   * caminho normal. Uma nave sem Arsenal Ígneo continua a não lançar míssil
   * nenhum.
   */
  devForceNextVolleyMissile(): void {
    this.forceNextVolleyMissile = true;
  }

  /* ------------------------------------------------------------------ */
  /*  O Orbe da Víbora (gate V28G.M)                                    */
  /* ------------------------------------------------------------------ */

  /**
   * **Um acerto direto de canhão sorteia o seu veneno**, e no máximo um stack.
   *
   * ## O que decide o sorteio, e o que não decide nada
   *
   * A rolagem sai do fluxo desta corrida, e a comparação é contra o intervalo
   * da joia. `selectPoisonApplication` responde `null` por três razões
   * diferentes - a rolagem caiu fora, a nave não tem a joia, a origem não pode
   * originar - e nenhuma delas produz stack.
   *
   * ## O alvo morto sai ANTES da rolagem
   *
   * Porque um stack num cadáver é um stack que ninguém paga, e porque o §"se o
   * tiro matar, nenhum stack nasce" é uma regra sobre o que EXISTE. Sair antes é
   * também o que faz a sequência ser a mesma numa re-simulação: o servidor
   * reproduz as mesmas mortes e portanto as mesmas saídas.
   *
   * ## A magnitude é congelada AQUI
   *
   * `baseDamageMilli()` e nada mais: sem a Chama, sem os Corações, sem o
   * ataque-bónus do equipamento, sem crítico e sem outro proc. É a mesma leitura
   * que a loteria ofensiva faz no nascimento de um tiro. As seis parcelas saem
   * deste número e não voltam a perguntar nada a ninguém.
   *
   * @param origin confirmada e nunca inferida: um projétil de pet chega aqui com
   *   `pet`, e a tabela fechada é que o recusa.
   */
  private poisonDirectHit(direct: MobEntity | BossEntity, origin: DamageOrigin): void {
    if (!damageOriginMayTriggerJewel(origin)) {
      /*
       * **A tabela fechada, ANTES da rolagem.**
       *
       * Perguntada aqui e não só dentro de `selectPoisonApplication` - que a
       * pergunta na mesma - por uma razão de FLUXO: uma bala de pet chega a este
       * método como qualquer outro acerto direto, e deixá-la rolar consumiria um
       * valor do fluxo do veneno. Uma corrida com pet deixaria de ter a mesma
       * sequência de veneno que uma sem, e a diferença não seria da joia.
       *
       * O que decide continua a ser a tabela, e não uma lista de exclusões
       * escrita aqui: uma origem nova sem decisão é um erro de compilação lá.
       */
      return;
    }
    if (direct.health <= 0) {
      return;
    }
    const roll = this.poisonRng.nextIntInclusive(0, PROC_CHANCE_DENOMINATOR - 1);
    /*
     * FERRAMENTA DE DESENVOLVIMENTO, e ela não mexe em número nenhum.
     *
     * A rolagem foi feita e o valor foi consumido acima, portanto o fluxo avança
     * exatamente como avançaria sem isto - a sequência de uma corrida com a
     * ferramenta ligada é a mesma de uma sem. O que muda é a COMPARAÇÃO: em vez
     * da rolagem real usa-se ZERO, que cai sempre dentro do intervalo da joia.
     * Uma nave sem a joia continua a não envenenar, porque forçar a rolagem não
     * é forçar a posse.
     */
    const forced = this.forceNextPoison;
    this.forceNextPoison = false;
    const applied = selectPoisonApplication(
      this.config.jewels ?? EMPTY_JEWEL_SOCKET_PROFILE,
      origin,
      forced ? 0 : roll,
      BigInt(this.baseDamageMilli()),
    );
    if (applied === null || applied.totalDamageMilli <= 0n) {
      return;
    }
    /*
     * **Um proc, um stack, e nunca uma renovação.**
     *
     * `push` e não uma procura por um stack no mesmo alvo: os stacks são
     * independentes por contrato, cada um com o seu relógio e a sua magnitude.
     * Vinte e um projéteis de uma salva que acertem no mesmo mob produzem até
     * vinte e um stacks, cada um com os seus seis segundos a contar do seu
     * próprio instante.
     */
    this.poison.push({
      id: this.nextPoisonId,
      targetId: direct.id,
      totalDamageMilli: applied.totalDamageMilli,
      appliedAt: this.simTimeMs,
      ticksDone: 0,
    });
    this.nextPoisonId += 1;
  }

  /**
   * **As parcelas vencidas deste tique**, por ordem de criação dos stacks.
   *
   * ## Porque a lista é copiada antes de a percorrer
   *
   * Porque uma parcela pode MATAR, e uma morte remove todos os stacks daquele
   * inimigo de `this.poison` no meio da passagem - e remove o próprio mob de
   * `this.mobs`. Percorrer a lista viva enquanto ela encolhe saltaria elementos;
   * percorrer uma cópia e reencontrar o alvo antes de cada parcela dá a resposta
   * certa nos dois casos.
   *
   * ## A origem é `jewel`, e o que isso desliga sozinho
   *
   * Tudo. `damageOriginMayTriggerJewel('jewel')` é falso, e é essa tabela que
   * faz uma parcela não acionar proc, não acionar Roubo de Vida e não acionar
   * outro veneno. A profundidade de recursão é um porque não existe aresta no
   * grafo que lhe dê duas.
   *
   * ## O número de dano viaja no evento que já existe
   *
   * `applyHit` emite o `hit` de sempre, com a magnitude realmente aplicada
   * depois do clamp do overkill, e o renderer imprime-o como imprime qualquer
   * outro. Sem `projectileId`, porque nenhum tiro trouxe isto: o projétil que
   * semeou o stack já não existe há segundos, e inventar-lhe um id seria dar a
   * uma cena uma chave falsa para procurar uma cor.
   */
  private tickPoison(): void {
    if (this.poison.length === 0) {
      return;
    }
    const now = this.simTimeMs;
    for (const stack of [...this.poison]) {
      let target = this.findEntity(stack.targetId);
      while (
        target !== null &&
        target.health > 0 &&
        poisonPendingTicks(stack, now, POISON_TICK_INTERVAL_MS) > 0
      ) {
        stack.ticksDone += 1;
        this.applyHit(
          target,
          poisonTickDamageMilli(stack.totalDamageMilli, stack.ticksDone),
          'jewel',
        );
        /*
         * Reencontrado a cada volta: `applyHit` remove um mob morto da lista, e
         * uma referência guardada apontaria para um inimigo que já não existe.
         */
        target = this.findEntity(stack.targetId);
      }
    }
    /*
     * O que sai da lista: o que já pagou as seis, e o que ficou sem alvo vivo.
     *
     * A remoção do gasto acontece no MESMO tique da sexta parcela, que é o
     * §"depois do sexto tick, o stack é removido" - e é também o que faz a marca
     * desaparecer imediatamente, porque a marca é o stack.
     */
    this.poison = this.poison.filter((stack) => {
      if (poisonIsSpent(stack)) {
        return false;
      }
      const target = this.findEntity(stack.targetId);
      return target !== null && target.health > 0;
    });
  }

  /** Todos os stacks de um inimigo saem juntos. Chamado quando ele morre. */
  private clearPoisonOf(targetId: number): void {
    if (this.poison.length === 0) {
      return;
    }
    this.poison = this.poison.filter((stack) => stack.targetId !== targetId);
  }

  /**
   * Os stacks ativos, em ordem de criação (gate V28G.M).
   *
   * A cena lê isto para desenhar uma gota por stack e para saber quando cada uma
   * pulsa - `ticksDone` sobe uma vez por parcela. É estado autoritativo e nada
   * visual: onde a marca fica, que tamanho tem e como pulsa são decisões do
   * renderer, derivadas destes números.
   */
  getPoisonStacks(): readonly PoisonStack[] {
    return this.poison;
  }

  /**
   * DEV ONLY - o PRÓXIMO acerto direto envenena, se a nave tiver a joia.
   *
   * Irmão de {@link devForceNextVolleyBomb} e de
   * {@link devForceNextVolleyMissile}, com as mesmas garantias: muda UMA
   * comparação, a rolagem continua a ser feita e o valor continua a sair do
   * fluxo, portanto ligar isto não desloca um spawn, um temporizador de boss nem
   * um proc. Não toca em magnitude, duração, número de parcelas, chance real ou
   * instantânea.
   *
   * **Não acrescenta a joia.** Uma nave sem o Orbe da Víbora continua a não
   * envenenar nada, porque `selectPoisonApplication` devolve `null` quando o
   * perfil não a tem.
   *
   * Um acerto só. Consome-se ao ser usado, para que uma tecla carregada por
   * engano não transforme a corrida inteira.
   *
   * Guardado pelo chamador (modo de depuração), como `devForceDeath` é, e não
   * concede coisa nenhuma: uma sessão de depuração já não é económica.
   */
  devForceNextPoison(): void {
    this.forceNextPoison = true;
  }

  /* ------------------------------------------------------------------ */
  /*  A Garra Hemorrágica (gate V28G.N)                                 */
  /* ------------------------------------------------------------------ */

  /**
   * **Um acerto direto de canhão sorteia a sua marca**, e no máximo uma.
   *
   * ## A tabela fechada, ANTES da rolagem
   *
   * Perguntada aqui e não só dentro de `selectBleedingApplication` - que a
   * pergunta na mesma - por uma razão de FLUXO: uma bala de pet chega a este
   * método como qualquer outro acerto direto, e deixá-la rolar consumiria um
   * valor do fluxo do sangramento. Uma corrida com pet deixaria de ter a mesma
   * sequência que uma sem ele, e a diferença não seria da joia.
   *
   * ## O alvo morto sai ANTES da rolagem
   *
   * Porque o §"se o tiro matar o alvo, nenhuma marca nasce" é uma regra sobre o
   * que EXISTE. Sair antes é também o que faz a sequência ser a mesma numa
   * re-simulação: o servidor reproduz as mesmas mortes e portanto as mesmas
   * saídas.
   *
   * ## A magnitude é congelada AQUI
   *
   * `baseDamageMilli()` e nada mais: sem a Chama, sem os Corações, sem o
   * ataque-bónus do equipamento, sem crítico e sem outro proc. A detonação SOMA
   * estes números e não volta a perguntar nada a ninguém.
   *
   * @param origin confirmada e nunca inferida: um projétil de pet chega aqui com
   *   `pet`, e a tabela fechada é que o recusa.
   */
  private bleedingDirectHit(direct: MobEntity | BossEntity, origin: DamageOrigin): void {
    if (!damageOriginMayTriggerJewel(origin)) {
      return;
    }
    if (direct.health <= 0) {
      return;
    }
    const roll = this.bleedingRng.nextIntInclusive(0, PROC_CHANCE_DENOMINATOR - 1);
    /*
     * FERRAMENTA DE DESENVOLVIMENTO, e ela não mexe em número nenhum.
     *
     * A rolagem foi feita e o valor foi consumido acima, portanto o fluxo avança
     * exatamente como avançaria sem isto. O que muda é a COMPARAÇÃO: em vez da
     * rolagem real usa-se ZERO, que cai sempre dentro do intervalo da joia. Uma
     * nave sem a joia continua a não marcar, porque forçar a rolagem não é
     * forçar a posse.
     */
    const forced = this.forceNextBleeding;
    this.forceNextBleeding = false;
    const applied = selectBleedingApplication(
      this.config.jewels ?? EMPTY_JEWEL_SOCKET_PROFILE,
      origin,
      forced ? 0 : roll,
      BigInt(this.baseDamageMilli()),
    );
    if (applied === null || applied.damageMilli <= 0n) {
      return;
    }
    /*
     * **Um proc, uma marca, e nunca uma renovação.**
     *
     * `push` e não uma procura por uma marca no mesmo alvo: as marcas são
     * independentes por contrato, cada uma com a sua magnitude congelada. Vinte e
     * um projéteis de uma salva que acertem no mesmo mob produzem até vinte e uma
     * marcas, e é isso que faz a detonação crescer linearmente.
     */
    this.bleeding.push({
      id: this.nextBleedingId,
      targetId: direct.id,
      damageMilli: applied.damageMilli,
      appliedAt: this.simTimeMs,
    });
    this.nextBleedingId += 1;
  }

  /**
   * **A ORDEM: todas as marcas desta corrida rebentam no mesmo tique.**
   *
   * Chamada pelo caminho AUTORITATIVO da caça - o servidor, dentro da transação
   * que liquida a janela - e nunca por um relógio. Ver
   * `docs/gates/v28gn-bleeding-contract.md`, §"a caça".
   *
   * ## Um golpe por ALVO, e nunca um por marca
   *
   * As marcas são agrupadas pelo inimigo que as carrega, e cada inimigo leva
   * **uma** instância de dano igual à soma milli das marcas que tem. Um golpe por
   * marca imprimiria vinte números onde o contrato pede um, e faria vinte clamps
   * de overkill em vez de um - que é aritmética diferente, não apresentação
   * diferente.
   *
   * ## A ordem é a de CRIAÇÃO, e não a de um `Map`
   *
   * Os alvos são visitados pela ordem da primeira marca que cada um recebeu, que
   * é uma propriedade da lista e não do agrupamento. Duas máquinas que restaurem
   * a mesma instantânea produzem a mesma sequência de golpes.
   *
   * ## A origem é `jewel`, e o que isso desliga sozinho
   *
   * Tudo. `damageOriginMayTriggerJewel('jewel')` é falso, e é essa tabela que faz
   * uma detonação não acionar proc, não acionar Roubo de Vida, não envenenar e
   * não semear outra marca.
   *
   * @returns o que rebentou, por alvo, para o chamador anunciar. Vazio quando
   *   não havia marcas - e nesse caso nada aconteceu, que é o §"sem marcas, o
   *   comando é um `no-op` seguro".
   */
  detonateBleeding(): readonly HuntBleedDetonationTarget[] {
    if (this.bleeding.length === 0) {
      return [];
    }
    if (this.phase === 'dead') {
      /*
       * Um destroço não dá ordens.
       *
       * Inalcançável hoje - `die()` esvazia a lista, portanto o ramo acima já
       * teria devolvido - e escrito na mesma para que a regra seja lida aqui em
       * vez de depender de uma limpeza noutro sítio.
       */
      return [];
    }
    /*
     * Agrupado por alvo, e a ordem de iteração é a de INSERÇÃO deste `Map` -
     * que aqui é determinística, porque a lista de onde ela vem está ordenada
     * por id monotónico e nada a reordena.
     */
    const byTarget = new Map<number, BleedingMark[]>();
    for (const mark of this.bleeding) {
      const existing = byTarget.get(mark.targetId);
      if (existing) {
        existing.push(mark);
      } else {
        byTarget.set(mark.targetId, [mark]);
      }
    }

    /*
     * As marcas saem ANTES de o dano ser aplicado, e a ordem é uma regra: um
     * golpe pode matar, e uma morte chama `clearBleedingOf` no meio da passagem.
     * Retirá-las primeiro faz a limpeza encontrar uma lista de onde já saíram.
     */
    this.bleeding = [];
    const detonated: HuntBleedDetonationTarget[] = [];
    for (const [targetId, marks] of byTarget) {
      const target = this.findEntity(targetId);
      if (target === null || target.health <= 0) {
        /*
         * O alvo já não existe. As marcas dele já tinham saído com ele, portanto
         * este ramo é inalcançável por morte - e fica escrito para que um alvo
         * que desapareça por outra via não produza um golpe no vazio nem um
         * anúncio de uma coisa que não se viu.
         */
        continue;
      }
      /*
       * UMA instância de dano, igual à SOMA dos milli congelados.
       *
       * Nunca `quantidade x dano atual`: cada marca vale o que valia quando
       * nasceu, e uma nave que melhorou entretanto tem marcas de magnitudes
       * diferentes acumuladas no mesmo alvo.
       */
      const before = target.health;
      this.applyHit(target, bleedingDetonationMilli(marks), 'jewel');
      /*
       * O que foi REALMENTE aplicado, medido na vida e não recalculado.
       *
       * Depois do clamp de overkill, portanto igual ao `amount` do evento `hit`
       * que `applyHit` acabou de emitir. Medido aqui porque na caça este evento
       * não chega ao cliente: a detonação acontece dentro da transação do
       * servidor, que drena a fila de apresentação depois de re-simular. O número
       * canónico de dano viaja na RESPOSTA, e é este.
       */
      detonated.push({
        targetId,
        marks: marks.length,
        applied: Math.max(0, before - target.health),
      });
    }
    return detonated;
  }

  /** Todas as marcas de um inimigo saem juntas. Chamado quando ele morre. */
  private clearBleedingOf(targetId: number): void {
    if (this.bleeding.length === 0) {
      return;
    }
    this.bleeding = this.bleeding.filter((mark) => mark.targetId !== targetId);
  }

  /* ------------------------------------------------------------------ */
  /*  **O Diamante Serrilhado** (gate V28G.P)                           */
  /* ------------------------------------------------------------------ */

  /** O nível do Diamante Serrilhado desta corrida, ou 0 se ela não o tem. */
  private sawLevel(): number {
    return sawLevelOf(this.config.jewels ?? EMPTY_JEWEL_SOCKET_PROFILE);
  }

  /**
   * **Um tique das serras: a órbita anda, e o que ela tocar leva.**
   *
   * A mesma lei do duelo, com o vocabulário desta caça: o alvo é um mob ou um
   * boss em vez de um adversário, e o relógio conta milissegundos simulados em
   * vez de tiques de 50 ms. A contagem, o multiplicador, o intervalo e o
   * orçamento saem todos de `damage/saw.ts`, que é o mesmo ficheiro que o duelo
   * lê - é isso que faz os dois modos partilharem a fórmula em vez de a
   * copiarem.
   *
   * Uma nave morta não gira e não corta: `tick` não corre na fase `dead`, e
   * `die` larga a órbita, o resto e os relógios no instante da morte.
   */
  private tickSaws(): void {
    const level = this.sawLevel();
    if (level <= 0) {
      /*
       * Sem a joia: a órbita não existe, e a saída antecipada não é uma
       * otimização. É o que faz uma corrida sem esta joia produzir exatamente o
       * estado que produzia antes deste gate - fase, resto e entropia incluídos.
       */
      this.sawPhase = SAW_INITIAL_PHASE;
      return;
    }
    const previous = this.sawPhase;
    this.sawPhase = sawAdvancePhase(previous, HUNT_TICK_MS, level);
    this.resolveSawContacts(level, previous);
  }

  /**
   * **As lâminas contra os inimigos no campo, neste tique.**
   *
   * ## O varrimento, e a conta que o justifica
   *
   * A órbita é amostrada em {@link HUNT_SAW_SUBSTEPS} posições dentro do tique.
   * Ao nível 30 uma volta dura 720 ms, portanto um tique de 16 ms avança 8 graus
   * - a 74 unidades de raio, uma corda de 10,3 unidades - contra uma janela de
   * contacto que vale pelo menos `16 + 32 = 48` unidades de raio num mob comum.
   * Não haveria atravessamento nem sem varrer; varrer é o que mantém a mesma
   * disciplina dos projéteis e o que torna a margem indiferente ao dia em que
   * alguém aumentar a velocidade.
   *
   * ## A fase larga
   *
   * Um teste de distância por inimigo antes do laço, contra o alcance máximo da
   * órbita. Uma caçada tem dezenas de mobs no ecrã e a esmagadora maioria está
   * longe da nave: sem ele, cada tique pagaria `4 lâminas x 4 sub-passos` testes
   * por mob para descobrir isso.
   */
  private resolveSawContacts(level: number, previousPhase: number): void {
    const count = sawBladeCountForLevel(level);
    if (count <= 0) {
      return;
    }
    const enemies: (MobEntity | BossEntity)[] = [...this.mobs];
    if (this.boss !== null) {
      enemies.push(this.boss);
    }
    if (enemies.length === 0) {
      return;
    }
    const ship = this.shipPosition();
    const orbit = sawOrbitRadius(this.hullRadius());
    const bladeRadius = sawBladeRadius(this.hullRadius());
    /* O mais longe que uma lâmina pode tocar, medido do centro da nave. */
    const reach = orbit + bladeRadius;
    const near = enemies.filter((enemy) => {
      if (enemy.health <= 0) {
        return false;
      }
      const enemyRadius = enemy.size * HUNT_BALANCE.mobs.hitRadiusFraction;
      const limit = reach + enemyRadius;
      return (
        (enemy.x - ship.x) * (enemy.x - ship.x) + (enemy.y - ship.y) * (enemy.y - ship.y) <=
        limit * limit
      );
    });
    if (near.length === 0) {
      return;
    }
    const advance = HUNT_TICK_MS * sawSpeedNumerator(level);
    for (let step = 1; step <= HUNT_SAW_SUBSTEPS; step += 1) {
      const phase =
        (previousPhase + Math.floor((advance * step) / HUNT_SAW_SUBSTEPS)) % SAW_ORBIT_PHASE_UNITS;
      for (let index = 0; index < count; index += 1) {
        const angle = sawBladeStep(phase, index, count);
        const direction = sawDirection(angle);
        const bladeX = ship.x + (direction.x * orbit) / SAW_VECTOR_SCALE;
        const bladeY = ship.y + (direction.y * orbit) / SAW_VECTOR_SCALE;
        for (const enemy of near) {
          if (enemy.health <= 0) {
            continue;
          }
          if (!this.sawPairDue(index, enemy.id, level)) {
            continue;
          }
          const contactRadius = bladeRadius + enemy.size * HUNT_BALANCE.mobs.hitRadiusFraction;
          const dx = enemy.x - bladeX;
          const dy = enemy.y - bladeY;
          if (dx * dx + dy * dy > contactRadius * contactRadius) {
            continue;
          }
          this.sawStrike(index, enemy, level);
        }
      }
    }
  }

  /** O relógio deste par lâmina→alvo, ou `undefined` se nunca se tocaram. */
  private sawPairOf(bladeIndex: number, targetId: number): HuntSawContact | undefined {
    return this.sawContacts.find(
      (contact) => contact.bladeIndex === bladeIndex && contact.targetId === targetId,
    );
  }

  /**
   * Se ESTE par lâmina→alvo já pode voltar a doer.
   *
   * Um par que nunca se tocou está sempre pronto: uma lâmina que alcança um
   * inimigo cobra no instante em que o alcança, e não um intervalo depois.
   */
  private sawPairDue(bladeIndex: number, targetId: number, level: number): boolean {
    const contact = this.sawPairOf(bladeIndex, targetId);
    if (contact === undefined) {
      return true;
    }
    return sawContactDue(this.simTimeMs - contact.lastContactMs, level);
  }

  /**
   * **Uma instância de dano de UMA lâmina sobre UM inimigo.**
   *
   * O relógio é marcado ANTES de o dano existir, e o carry é consumido no mesmo
   * instante: o que eles contam é o CONTACTO, e o contacto aconteceu. Marcá-lo
   * depois deixaria uma lâmina que encontrou um mob já sem vida livre para
   * voltar a tentar no sub-passo seguinte - uma serra a bater quatro vezes num
   * tique.
   *
   * A origem é `saw`, e é ela que decide o resto: o Roubo de Vida entra sozinho
   * dentro de `applyHit` pela tabela fechada, e o Ciclone, o Veneno e a Garra
   * são sorteados a seguir. O Núcleo Atômico e o Arsenal Ígneo não entram, e
   * `damageOriginMayTrigger` recusa-os pelo par `(origem, efeito)` - não por não
   * haver chamador.
   */
  private sawStrike(bladeIndex: number, enemy: MobEntity | BossEntity, level: number): void {
    const existing = this.sawPairOf(bladeIndex, enemy.id);
    if (existing === undefined) {
      this.sawContacts.push({ bladeIndex, targetId: enemy.id, lastContactMs: this.simTimeMs });
    } else {
      existing.lastContactMs = this.simTimeMs;
    }
    const resolved = sawContactDamageMilli(
      /*
       * O dano-base AUTORITATIVO desta nave, NESTE instante, e mais nada: sem a
       * Chama, sem os Corações, sem crítico e sem outro proc. A mesma leitura
       * que todas as outras joias fazem.
       */
      BigInt(this.baseDamageMilli()),
      level,
      this.sawCarryMilli,
    );
    this.sawCarryMilli = resolved.carry;
    if (resolved.damageMilli <= 0n) {
      return;
    }
    /*
     * A identidade desta instância, tirada do MESMO contador das entidades.
     *
     * Um espaço de ids só. A cena procura a cor congelada do projétil que
     * causou um proc por este número; um contacto não é um projétil, portanto
     * ela não encontra nada e usa a cor por omissão - que é a resposta certa, e
     * não um erro a evitar.
     */
    const contactId = this.nextEntityId;
    this.nextEntityId += 1;
    const before = enemy.health;
    this.applyHit(enemy, resolved.damageMilli, 'saw', contactId);
    if (enemy.health >= before) {
      /* Nada foi aplicado: o golpe não chegou a um ponto, ou o alvo já caíra. */
      return;
    }
    if (enemy.health <= 0) {
      /*
       * O contacto matou. Nada mais é semeado: um stack, uma marca ou um corte
       * num cadáver é uma coisa que ninguém paga, e é a mesma regra que um
       * acerto direto de canhão já segue.
       */
      return;
    }
    this.sawTriggers(enemy, contactId);
  }

  /**
   * **Os três sorteios que um contacto confirmado faz**, do fluxo das serras.
   *
   * Pela ordem: o Ciclone, o Veneno e a Garra - a mesma ordem em que um acerto
   * de canhão os resolve, para as duas fontes não terem semânticas diferentes.
   * A ordem é o contrato do fluxo: mudá-la muda toda a sequência a partir dali.
   *
   * Sem ferramentas de desenvolvimento. `devForceNextPoison`,
   * `devForceNextBleeding` e o crédito do Ciclone são sobre o TIRO, e gastar um
   * aqui roubaria à salva seguinte o efeito que o operador pediu para ver.
   */
  private sawTriggers(enemy: MobEntity | BossEntity, contactId: number): void {
    const procRoll = this.sawRng.nextIntInclusive(0, PROC_CHANCE_DENOMINATOR - 1);
    const entropy = this.sawRng.nextIntInclusive(0, 0xffff_ffff);
    const selection = selectOutgoingProc(
      this.config.jewels ?? EMPTY_JEWEL_SOCKET_PROFILE,
      'saw',
      procRoll,
      entropy,
      BigInt(this.baseDamageMilli()),
    );
    if (selection !== null && selection.cutMilli > 0n && enemy.health > 0) {
      /* A origem que fecha o grafo: um corte não origina outro. */
      this.applyHit(enemy, selection.cutMilli, 'jewel', contactId);
      this.events.push({
        type: 'proc',
        procId: selection.id,
        targetId: enemy.id,
        projectileId: contactId,
        tick: this.currentTick(),
        x: enemy.x,
        y: enemy.y,
      });
    }
    this.poisonDirectHit(enemy, 'saw');
    this.bleedingDirectHit(enemy, 'saw');
  }

  /** Todos os relógios de contacto de um inimigo saem juntos. Ele morreu. */
  private clearSawContactsOf(targetId: number): void {
    if (this.sawContacts.length === 0) {
      return;
    }
    this.sawContacts = this.sawContacts.filter((contact) => contact.targetId !== targetId);
  }

  /**
   * **As lâminas desta nave, como uma CENA as lê** (gate V28G.P).
   *
   * Derivadas do estado autoritativo a cada leitura, e nunca guardadas: a
   * posição é a fase mais a órbita, portanto a cena desenha exatamente onde a
   * colisão aconteceu. Vazio sem a joia e vazio depois da morte, que são os dois
   * casos em que não há nada para desenhar.
   *
   * Não lê inventário, perfil, socket nem nível: recebe a fase que a simulação
   * já tem e devolve posições. É o mesmo contrato que `getBleedingMarks` segue.
   */
  getSawBlades(): readonly HuntSawBlade[] {
    const level = this.sawLevel();
    const count = sawBladeCountForLevel(level);
    if (count <= 0 || this.phase === 'dead') {
      return NO_SAW_BLADES;
    }
    const ship = this.shipPosition();
    const orbit = sawOrbitRadius(this.hullRadius());
    const radius = sawBladeRadius(this.hullRadius());
    const blades: HuntSawBlade[] = [];
    for (let index = 0; index < count; index += 1) {
      const step = sawBladeStep(this.sawPhase, index, count);
      const direction = sawDirection(step);
      blades.push({
        index,
        x: ship.x + (direction.x * orbit) / SAW_VECTOR_SCALE,
        y: ship.y + (direction.y * orbit) / SAW_VECTOR_SCALE,
        step,
        radius,
      });
    }
    return blades;
  }

  /**
   * As marcas ativas, em ordem de criação (gate V28G.N).
   *
   * A cena lê isto para desenhar um corte por marca, e o painel para contar
   * quantas o jogador tem. É estado autoritativo e nada visual.
   */
  getBleedingMarks(): readonly BleedingMark[] {
    return this.bleeding;
  }

  /**
   * DEV ONLY - o PRÓXIMO acerto direto marca, se a nave tiver a joia.
   *
   * Irmão de {@link devForceNextPoison}, com as mesmas garantias: muda UMA
   * comparação, a rolagem continua a ser feita e o valor continua a sair do
   * fluxo, portanto ligar isto não desloca um spawn, um temporizador de boss nem
   * um proc. Não toca em magnitude, chance real ou instantânea.
   *
   * **Não acrescenta a joia.** Uma nave sem a Garra Hemorrágica continua a não
   * marcar nada, porque `selectBleedingApplication` devolve `null` quando o
   * perfil não a tem.
   *
   * Um acerto só. Consome-se ao ser usado, para que uma tecla carregada por
   * engano não transforme a corrida inteira.
   *
   * Guardado pelo chamador (modo de depuração), como `devForceDeath` é.
   */
  devForceNextBleeding(): void {
    this.forceNextBleeding = true;
  }

  /**
   * **Todos os inimigos no campo**, mobs e chefe, numa lista só.
   *
   * A ordem é a dos mobs seguida do chefe, e é ESTÁVEL: as cartas em área
   * percorrem-na e um empate resolve-se pela mesma ordem em qualquer máquina.
   */
  private enemyList(): (MobEntity | BossEntity)[] {
    return this.boss === null ? [...this.mobs] : [...this.mobs, this.boss];
  }

  /**
   * O meio da nave, e o tamanho desenhado dela.
   *
   * As cartas medem alcances em proporções do casco, exactamente como no duelo
   * - é o que faz uma carta ter a mesma FORMA nos dois modos.
   */
  private shipSize(): number {
    return HUNT_BALANCE.ship.displayHeight;
  }

  /**
   * **O raio do CORPO da Esfera Ígnea nesta arena** (gate V28I.C).
   *
   * Trinta por cento da nave, ou sessenta por cento dela em diâmetro - o número
   * que o dono deu. É esta a medida que o corpo desenhado, o brilho e a colisão
   * usam, e é por isso que ela é uma função e não três contas parecidas.
   *
   * Não sai do raio da explosão, ao contrário do duelo: lá a bola cresce até
   * quase preencher o próprio rebentamento porque quinze segundos a pagam; aqui
   * a explosão continua a valer os seis raios de casco aprovados, e o corpo é
   * uma bola de fogo do tamanho da nave.
   */
  fireballBodyRadius(): number {
    return (this.shipSize() * HUNT_FIREBALL_BODY_BPS) / 10_000;
  }

  /**
   * Para onde a nave aponta, como um vector unitário.
   *
   * Derivado do ALVO actual, que é para onde o nariz dela aponta: a caça não
   * tem mira de jogador, e a nave aponta ao que está a atacar. Sem alvo, aponta
   * para cima - a direcção de onde os mobs vêm.
   */
  private shipAim(): { readonly x: number; readonly y: number } {
    const target = this.findEntity(this.currentTargetId() ?? -1);
    const here = this.shipPosition();
    if (target === null) {
      return { x: 0, y: -1 };
    }
    const dx = target.x - here.x;
    const dy = target.y - here.y;
    const length = Math.hypot(dx, dy);
    return length === 0 ? { x: 0, y: -1 } : { x: dx / length, y: dy / length };
  }

  private findEntity(id: number): MobEntity | BossEntity | null {
    if (this.boss?.id === id) {
      return this.boss;
    }
    return this.mobs.find((mob) => mob.id === id) ?? null;
  }

  /* ------------------------------------------------------------------ */
  /*  Projectiles                                                       */
  /* ------------------------------------------------------------------ */

  /**
   * **Um tique de voo de uma bomba.** Devolve se ela continua no ar.
   *
   * ## Porque ela não persegue
   *
   * Porque não lhe foi pedido, e uma perseguição é uma decisão de conteúdo. O §3
   * diz que ela "nasce no mesmo ponto e segue a mesma direção do projétil que
   * substituiu" e depois lista, exaustivamente, as quatro coisas que a fazem
   * detonar. Uma bomba de proximidade que também caçasse seria um míssil, e o
   * gate não descreve um míssil.
   *
   * A consequência é a que se quer: ela vai onde foi apontada, e o jogador vê-a
   * ir. Um alvo que se desvia sobrevive à passagem dela.
   *
   * ## As quatro detonações, e a ordem em que são testadas
   *
   * 1. **fim de vida** - antes de andar, porque o tempo passou de qualquer
   *    maneira;
   * 2. **limite do mapa** - a arena inteira, e não a margem de desaparecimento
   *    dos tiros comuns: uma bomba explode NA borda, e não oitenta unidades
   *    depois dela, fora do ecrã;
   * 3. **proximidade** - o inimigo mais próximo dentro do raio, empate pelo
   *    menor id, que é o desempate canónico desta simulação;
   * 4. e nada mais. A caça não tem paredes nem obstáculos - o §3 nomeia-os
   *    porque o duelo tem - portanto aqui não há um terceiro teste a fingir que
   *    existe uma geometria que não existe.
   */
  private stepBomb(projectile: ProjectileEntity, dt: number): boolean {
    if (projectile.remainingMs !== undefined) {
      projectile.remainingMs -= dt * 1000;
      if (projectile.remainingMs <= 0) {
        this.detonate(projectile, null);
        return false;
      }
    }
    const travel = projectile.speed ?? HUNT_BOMB_SPEED;
    projectile.x += projectile.dirX * travel * dt;
    projectile.y += projectile.dirY * travel * dt;

    if (
      projectile.x < 0 ||
      projectile.y < 0 ||
      projectile.x > HUNT_BALANCE.arena.width ||
      projectile.y > HUNT_BALANCE.arena.height
    ) {
      this.detonate(projectile, null);
      return false;
    }

    /*
     * A PROXIMIDADE, sobre todos os inimigos e não só sobre o alvo da salva.
     *
     * Um tiro comum só pode magoar `targetId` - é o contrato dele. Uma bomba
     * detona quando **um inimigo** entra no raio, seja qual for, porque é uma
     * bomba: ela não escolheu ninguém, foi apontada para um sítio.
     *
     * Um boss a revelar-se é intocável, exatamente como é para uma bala. Uma
     * explosão não pode alcançar o que um tiro não alcança.
     */
    let best: { readonly entity: MobEntity | BossEntity; readonly distance: number } | null = null;
    const consider = (enemy: MobEntity | BossEntity) => {
      if (enemy.health <= 0) {
        return;
      }
      const reach = HUNT_BOMB_PROXIMITY_RADIUS + enemy.size * HUNT_BALANCE.mobs.hitRadiusFraction;
      const distance = Math.hypot(enemy.x - projectile.x, enemy.y - projectile.y);
      if (distance > reach) {
        return;
      }
      /*
       * O mais próximo, e um empate exato vai para o MENOR ID.
       *
       * Escrito e não deixado à ordem do array: os mobs estão por id crescente,
       * mas o boss é considerado depois deles e não é o último a nascer -
       * portanto "o primeiro que apareceu no laço" não é "o de menor id", e a
       * regra do §3 é a segunda.
       */
      if (
        best === null ||
        distance < best.distance ||
        (distance === best.distance && enemy.id < best.entity.id)
      ) {
        best = { entity: enemy, distance };
      }
    };
    for (const mob of this.mobs) {
      consider(mob);
    }
    if (this.boss && this.boss.phase === 'descending') {
      consider(this.boss);
    }
    const struck = best as { readonly entity: MobEntity | BossEntity } | null;
    if (struck !== null) {
      this.detonate(projectile, struck.entity);
      return false;
    }
    return true;
  }

  private tickProjectiles(dt: number): void {
    const { speed, maxTurnRadPerSecond, hitRadius, despawnMargin } = HUNT_BALANCE.projectiles;
    const survivors: ProjectileEntity[] = [];
    for (const projectile of this.projectiles) {
      /*
       * **A BOMBA voa por outras regras, e sai do laço aqui** (gate V28G.J).
       *
       * Não persegue, não tem alvo, não órfã e não desaparece na margem. O que
       * ela faz está inteiro em {@link stepBomb}, num sítio só, porque enfiar
       * quatro condições novas no percurso de um tiro comum seria fazer todos os
       * projéteis pagarem por uma entidade que quase nenhum é.
       */
      if (projectile.atomicBomb !== undefined) {
        if (this.stepBomb(projectile, dt)) {
          survivors.push(projectile);
        }
        continue;
      }
      const target = projectile.orphaned ? null : this.findEntity(projectile.targetId);
      if (!target && !projectile.orphaned) {
        // The original target died: keep the last heading forever.
        projectile.orphaned = true;
      }
      if (target && projectile.detonates) {
        // Remember where the target is, every tick. If it dies before this shot
        // arrives, the shot still knows where to go off.
        projectile.fuseX = target.x;
        projectile.fuseY = target.y;
      }
      /*
       * A missile BOOSTS off the rail, and it does not fly forever.
       *
       * Both are simulation state carried on the projectile, so a checkpoint
       * restores a missile mid-boost exactly where it was rather than at full
       * speed with a fresh fuse.
       */
      if (projectile.remainingMs !== undefined) {
        projectile.remainingMs -= dt * 1000;
        if (projectile.remainingMs <= 0) {
          // Out of fuel: it goes off where it is, and hurts nothing. `null` for
          // the direct target is what makes the detonation harmless.
          this.detonate(projectile, null);
          continue;
        }
      }
      if (projectile.speed !== undefined && projectile.acceleration !== undefined) {
        projectile.speed = Math.min(
          projectile.maxSpeed ?? projectile.speed,
          projectile.speed + projectile.acceleration * dt,
        );
      }
      if (target) {
        // Slight homing toward the ORIGINAL target only, turn-rate limited.
        const desiredX = target.x - projectile.x;
        const desiredY = target.y - projectile.y;
        const desiredLen = Math.hypot(desiredX, desiredY) || 1;
        const currentAngle = Math.atan2(projectile.dirY, projectile.dirX);
        const desiredAngle = Math.atan2(desiredY / desiredLen, desiredX / desiredLen);
        let diff = desiredAngle - currentAngle;
        while (diff > Math.PI) diff -= 2 * Math.PI;
        while (diff < -Math.PI) diff += 2 * Math.PI;
        // A missile turns at its OWN rate, which is slower than a bolt's - a
        // tight turn draws a straight line, and the arc is the point of it.
        const maxTurn = (projectile.maxTurn ?? maxTurnRadPerSecond) * dt;
        const turn = Math.max(-maxTurn, Math.min(maxTurn, diff));
        const newAngle = currentAngle + turn;
        projectile.dirX = Math.cos(newAngle);
        projectile.dirY = Math.sin(newAngle);
      }
      const travel = projectile.speed ?? speed;
      projectile.x += projectile.dirX * travel * dt;
      projectile.y += projectile.dirY * travel * dt;

      if (target) {
        const targetRadius =
          target.kind === 'boss'
            ? target.size * HUNT_BALANCE.mobs.hitRadiusFraction
            : target.size * HUNT_BALANCE.mobs.hitRadiusFraction;
        const distance = Math.hypot(target.x - projectile.x, target.y - projectile.y);
        if (distance <= targetRadius + hitRadius) {
          this.detonate(projectile, target);
          continue; // projectile consumed by the impact
        }
      }

      /*
       * An orphaned shot that detonates still goes off.
       *
       * Its target died, so there is nothing to hit directly. A ball goes on
       * because the blast was already on its way and the enemies around where
       * that target stood are the ones it was going to catch; a missile goes on
       * because that is where it was aimed, and it explodes there harmlessly
       * rather than hunting for someone else. Neither ever picks a new target.
       * A shot that does not detonate keeps the old behaviour: fly on, damage
       * nothing, despawn at the edge.
       */
      if (projectile.orphaned && projectile.detonates) {
        const toFuseX = (projectile.fuseX ?? projectile.x) - projectile.x;
        const toFuseY = (projectile.fuseY ?? projectile.y) - projectile.y;
        const passed = toFuseX * projectile.dirX + toFuseY * projectile.dirY <= 0;
        if (passed || Math.hypot(toFuseX, toFuseY) <= hitRadius) {
          this.detonate(projectile, null);
          continue;
        }
      }

      const out =
        projectile.x < -despawnMargin ||
        projectile.x > HUNT_BALANCE.arena.width + despawnMargin ||
        projectile.y < -despawnMargin ||
        projectile.y > HUNT_BALANCE.arena.height + despawnMargin;
      if (!out) {
        survivors.push(projectile);
      }
    }
    this.projectiles = survivors;
  }

  /**
   * Resolves one projectile arriving, exploding or not.
   *
   * A shot with no blast radius is exactly what it always was: one hit on one
   * enemy. A shot with one adds an area pass on top, and the rules of that pass
   * are the delicate part:
   *
   * - the enemy struck DIRECTLY takes the direct damage and nothing else. It
   *   does not also take the splash, because the same explosion paying it twice
   *   is precisely the double-count this codebase spends its effort avoiding;
   * - every OTHER live enemy inside the radius takes the splash, with no cap on
   *   how many. A limit would be a balance decision nobody has taken, and it
   *   would make the same wave resolve differently depending on spawn order;
   * - a `revealing` boss is untouchable, exactly as it is for a direct shot. A
   *   blast must not reach what a bullet cannot;
   * - each victim goes through `applyHit`, so each produces its own damage
   *   number, its own kill, its own XP and its own Gold - through the single
   *   cumulative-total path. There is no second reward route to get wrong.
   *
   * The victim list is collected BEFORE any damage is applied. `applyHit`
   * removes a dead mob from `this.mobs`, and mutating that array while deciding
   * who is in the blast would make the outcome depend on the order the kills
   * happened in - which is the one thing a re-simulation must not disagree
   * about.
   */
  /**
   * **A explosão do Núcleo Atômico, na caça.**
   *
   * ## O que é igual ao duelo, e porque tinha de ser
   *
   * As regras de dano, inteiras. O alvo que acionou a proximidade leva o dano
   * NORMAL do projétil que a bomba substituiu, com origem `cannon`, e a seguir o
   * dano adicional da explosão, com origem `jewel`; todos os outros inimigos no
   * raio levam só o adicional; contra o limite ou por fim de vida não há alvo
   * direto e só existe a área. O §4 diz o mesmo para os dois modos, e o dano-base
   * de que a magnitude sai é o da nave atacante em ambos.
   *
   * ## O que é diferente, e é uma REGRA e não um esquecimento
   *
   * **Nada é empurrado.** O §7 é explícito: mobs e boss não são empurrados, e
   * nenhuma posição, velocidade, alvo, orientação ou calendário de chegada muda
   * por causa da explosão. Isso não é conseguido aqui apagando um efeito - é
   * conseguido não existindo: o empurrão vive em `pvp/knockback.ts`, este
   * ficheiro não o importa, e o compilador garante isso melhor do que uma
   * verificação garantiria.
   *
   * O VFX aparece na mesma. A ausência é da simulação, não do renderer.
   *
   * ## Porque não reutiliza `blastRadius` e `splashDamageMilli`
   *
   * Porque a bola do pet dá ao alvo direto **só** o dano direto - está escrito
   * no método abaixo, e é deliberado: "a mesma explosão a pagar-lhe duas vezes é
   * precisamente a contagem dupla que este código gasta o seu esforço a evitar".
   * A Bomba paga-lhe as duas parcelas, porque são duas coisas: um tiro que
   * acertou e uma explosão que o apanhou. Enfiar as duas regras num método com
   * uma bandeira faria a mais delicada das duas depender de um booleano.
   */
  private detonateBomb(
    projectile: ProjectileEntity,
    bomb: AtomicBombSelection,
    direct: MobEntity | BossEntity | null,
  ): void {
    /*
     * O centro: o inimigo atingido quando existe, senão onde a bomba está.
     *
     * A mesma regra do Ciclone, e é o mesmo centro que viaja no evento - o
     * círculo que o jogador vê é o círculo que decidiu quem levou.
     */
    const x = direct === null ? projectile.x : direct.x;
    const y = direct === null ? projectile.y : direct.y;

    /*
     * A lista fecha ANTES de qualquer dano.
     *
     * `applyHit` remove um mob morto de `this.mobs`, e decidir quem está no raio
     * enquanto isso acontece faria o resultado depender da ordem das mortes -
     * que é a única coisa sobre a qual uma re-simulação não pode discordar.
     */
    const victims: (MobEntity | BossEntity)[] = [];
    for (const mob of this.mobs) {
      if (Math.hypot(mob.x - x, mob.y - y) <= HUNT_BOMB_BLAST_RADIUS) {
        victims.push(mob);
      }
    }
    const boss = this.boss;
    if (
      boss &&
      boss.phase === 'descending' &&
      Math.hypot(boss.x - x, boss.y - y) <= HUNT_BOMB_BLAST_RADIUS
    ) {
      victims.push(boss);
    }

    if (direct !== null) {
      /*
       * O dano NORMAL, e primeiro: é dano de canhão, e continua a curar quem o
       * deu se a nave trouxer Roubo de Vida. A ordem importa - um alvo que morre
       * do tiro não chega a receber a explosão, e `applyHit` recusa um alvo sem
       * vida.
       */
      this.applyHit(direct, projectile.damageMilli, 'cannon', projectile.id);
    }
    let touched = 0;
    for (const victim of victims) {
      if (victim.health <= 0) {
        continue;
      }
      /*
       * A explosão, com origem `jewel` - e é essa tabela fechada que faz este
       * golpe não acionar outro proc, não acionar o Roubo de Vida, não criar
       * Ciclone e não voltar a aplicar a própria bomba. Nada disso é uma
       * verificação escrita aqui.
       */
      this.applyHit(victim, bomb.blastMilli, 'jewel', projectile.id);
      touched += 1;
    }

    this.events.push({
      type: 'explosion',
      effectId: ATOMIC_BLAST_EFFECT,
      x,
      y,
      /* O raio MECÂNICO, o mesmo que decidiu quem levou. */
      radius: HUNT_BOMB_BLAST_RADIUS,
      hits: touched,
      projectileId: projectile.id,
    });
  }

  private detonate(projectile: ProjectileEntity, direct: MobEntity | BossEntity | null): void {
    if (projectile.atomicBomb !== undefined) {
      this.detonateBomb(projectile, projectile.atomicBomb, direct);
      return;
    }
    const radius = projectile.blastRadius ?? 0;
    const splash = projectile.splashDamageMilli ?? 0n;
    // Centred on the enemy that was struck, or on the remembered point when the
    // target died first - never on some interpolated position of the sprite.
    const x = direct ? direct.x : (projectile.fuseX ?? projectile.x);
    const y = direct ? direct.y : (projectile.fuseY ?? projectile.y);
    const origin: DamageOrigin = projectile.fromPet === true ? 'pet' : 'cannon';

    const victims: (MobEntity | BossEntity)[] = [];
    if (radius > 0 && splash > 0n) {
      for (const mob of this.mobs) {
        if (direct && mob.id === direct.id) {
          continue;
        }
        if (Math.hypot(mob.x - x, mob.y - y) <= radius) {
          victims.push(mob);
        }
      }
      const boss = this.boss;
      if (
        boss &&
        boss.phase === 'descending' &&
        (!direct || boss.id !== direct.id) &&
        Math.hypot(boss.x - x, boss.y - y) <= radius
      ) {
        victims.push(boss);
      }
    }

    if (direct) {
      this.applyHit(direct, projectile.damageMilli, origin, projectile.id);
      /*
       * O corte, e SÓ no alvo direto.
       *
       * Fora do laço do splash de propósito: uma explosão que apanha oito mobs
       * não produz oito cortes. O contrato do Ciclone é um corte no inimigo
       * ATINGIDO, e multiplicá-lo pelo raio faria a magnitude - que foi
       * calculada contra a cadência de uma nave - valer oito vezes.
       *
       * A ordem importa: o tiro comum aplica-se primeiro e o corte depois, o
       * que faz o corte encontrar a vida já reduzida. Um alvo que morre do tiro
       * não recebe o corte, e `applyHit` recusa um alvo sem vida.
       */
      const cut = this.applyJewelProc(projectile, direct, origin);
      /*
       * **O VENENO, e só no alvo direto de um tiro de canhão** (V28G.M).
       *
       * Fora do laço do splash pela mesma razão que o corte está: uma explosão
       * que apanha oito mobs não envenena oito. O contrato é explícito - só um
       * acerto DIRETO de projétil de canhão aplica - e o splash não é um.
       *
       * A BOMBA não chega aqui: `detonate` desvia para `detonateBomb` na
       * primeira linha, e é assim que o §"não pode aplicar: Bomba Atômica" fica
       * cumprido pela estrutura em vez de por uma condição. O míssil resolve-se
       * em `explodeMissile`, que também não passa por aqui. E o pet entra com
       * origem `pet`, que a tabela fechada recusa.
       *
       * O alvo pode ter morrido do tiro. `poisonDirectHit` pergunta-o antes de
       * gastar a rolagem, porque um stack num cadáver é um stack que ninguém
       * paga - e porque um mob morto já saiu de `this.mobs`.
       */
      this.poisonDirectHit(direct, origin);
      /*
       * **O SANGRAMENTO, na mesma linha e pelas mesmas três razões** (V28G.N).
       *
       * Aqui e não noutro sítio, porque é aqui que as três condições do §
       * "Aplicação das marcas" são todas verdadeiras ao mesmo tempo: houve um
       * ACERTO DIRETO, o alvo é o que a colisão escolheu, e a origem chega
       * confirmada para a tabela fechada a recusar quando for de pet.
       *
       * A Bomba não chega aqui - o ramo dela desvia para a explosão acima - e o
       * míssil resolve-se em `explodeMissile`. O splash, o corte do Ciclone, o
       * Roubo de Vida, o veneno e outra marca entram em `applyHit` com origem
       * `jewel`, que a tabela fechada já recusa.
       *
       * Um sorteio PRÓPRIO ao lado do do veneno: uma marca e um stack podem
       * nascer do mesmo acerto, e é isso que dois fluxos separados significam.
       */
      this.bleedingDirectHit(direct, origin);
      if (cut !== null) {
        /*
         * O anúncio, e só depois de o golpe ter mesmo acontecido.
         *
         * A posição é a do ALVO e não a do projétil: o corte nasce sobre o
         * casco atingido, e um efeito centrado no ponto onde a bala estava
         * ficaria meio corpo ao lado num alvo grande. **A mesma regra nos dois
         * modos** - o duelo usava o projétil e passou a usar o alvo, para uma
         * apresentação partilhada não ter de saber em que modo está.
         *
         * `procId` vem do que foi APLICADO, e não de uma releitura do projétil
         * com um valor por omissão: um `?? ''` publicaria um proc sem efeito
         * nenhum no dia em que a leitura falhasse.
         */
        this.events.push({
          type: 'proc',
          procId: cut,
          targetId: direct.id,
          projectileId: projectile.id,
          tick: this.currentTick(),
          x: direct.x,
          y: direct.y,
        });
      }
    }
    for (const victim of victims) {
      if (victim.health <= 0) {
        continue;
      }
      // The splash carries the same shot's id: everything one detonation did
      // was done in the colour that shot was born with.
      this.applyHit(victim, splash, origin, projectile.id);
    }

    /*
     * The explosion is announced whenever the shot DETONATES, radius or not.
     *
     * A missile's blast radius is zero because it damages nobody but the enemy
     * it struck - but the impact is still an explosion, and the renderer has to
     * draw one. Gating the event on the radius would have left every missile
     * landing in silence.
     */
    if (projectile.detonates || radius > 0) {
      this.events.push({
        type: 'explosion',
        x,
        y,
        radius,
        hits: victims.length + (direct ? 1 : 0),
        projectileId: projectile.id,
      });
    }
  }

  /**
   * O efeito do proc que este projétil trazia, se trazia algum.
   *
   * ## O que é lido, e o que deliberadamente não é
   *
   * ## Nada é decidido aqui
   *
   * A magnitude foi congelada no nascimento, dentro da seleção. Este método
   * aplica-a e não reavalia coisa nenhuma: não lê o perfil, não conhece uma
   * joia pelo nome, não sabe o que é um `levelSum`, e não pergunta ao estado
   * atual da nave. Comprar um ponto, trocar de nave, incorporar um coração ou
   * desencaixar a própria joia enquanto o tiro está no ar não muda o corte -
   * exatamente como não muda `damageMilli`.
   *
   * ## Porque a origem é `jewel`
   *
   * Para que o corte não possa originar coisa nenhuma.
   * `damageOriginMayTriggerJewel` devolve falso para `jewel`, e é a tabela
   * fechada que garante que a profundidade de recursão é exatamente um - não um
   * contador que alguém teria de decrementar.
   *
   * @returns o EFEITO que foi realmente aplicado, ou `null` quando não houve
   *   corte. Devolver o id em vez do dano é o que permite ao chamador anunciar
   *   sem reler o projétil: o que se anuncia é o que aconteceu.
   */
  private applyJewelProc(
    projectile: ProjectileEntity,
    direct: MobEntity | BossEntity,
    origin: DamageOrigin,
  ): OutgoingJewelProcId | null {
    const selection = projectile.jewelProc;
    if (selection === null) {
      return null;
    }
    if (origin !== 'cannon') {
      /*
       * Inalcançável hoje - só um tiro de canhão nasce selecionado - e escrito
       * na mesma. A origem é o que decide, e derivá-la do campo em vez de a
       * confirmar seria voltar a inferir uma classificação de um valor.
       */
      return null;
    }
    if (selection.cutMilli <= 0n) {
      return null;
    }
    /*
     * O alvo já morreu do tiro comum: `applyHit` recusa um casco sem vida, e o
     * corte não tem onde cair. Perguntado ANTES de aplicar, para o chamador
     * saber que não houve facto nenhum a anunciar.
     */
    if (direct.health <= 0) {
      return null;
    }
    this.applyHit(direct, selection.cutMilli, 'jewel', projectile.id);
    return selection.id;
  }

  /**
   * A cura de um golpe confirmado, e a única coisa que mexe na vida por cima.
   *
   * ## A ordem, e porque é a única possível
   *
   * dano → cura → (morte do alvo). A base da cura é o que o golpe REMOVEU,
   * portanto ela não pode ser calculada antes de ele acontecer. Um alvo que
   * morre do golpe não anula a cura: o dano comum aconteceu mesmo, e é dele que
   * ela sai.
   *
   * ## Nunca ressuscita
   *
   * `resolveLifestealHeal` recusa uma nave com vida a zero, e a recusa vive lá
   * em vez de aqui - um chamador que se lembrasse dela seria um chamador; a
   * fronteira é a função. Na caça o caso é estruturalmente inalcançável por
   * projétil, porque `die()` esvazia o campo, e a guarda existe na mesma.
   *
   * ## O anúncio, e só quando houve cura MESMO
   *
   * O evento sai depois do clamp e só quando `applied > 0`. Vida cheia, nave em
   * baixo ou uma cura que ficou toda no resto não produzem facto nenhum, e
   * portanto não produzem evento nenhum - uma apresentação que desenhasse um
   * sifão nesses casos desenharia uma coisa que não aconteceu.
   *
   * Ele diz o EFEITO, nunca a joia; o alvo drenado; o projétil que o causou; e
   * a posição autoritativa do alvo. Não diz quanto curou, que nível tem, quantas
   * cópias, nem nada de arte - a cura já viaja como vida a subir no estado, e a
   * arte é do cliente.
   *
   * @param appliedDamage o dano REALMENTE removido, na unidade da simulação.
   * @param target o inimigo drenado, para o anúncio dizer de onde o fluxo parte.
   * @param projectileId o tiro que o causou. Sem ele não há chave de
   *   deduplicação, e por isso não há anúncio: hoje só o feixe do pet chega aqui
   *   sem um, e a origem `pet` já não passa da guarda acima.
   */
  private stealLife(
    appliedDamage: number,
    target: MobEntity | BossEntity,
    projectileId: number | undefined,
  ): void {
    const lifestealBps = this.jewelLifestealBps();
    if (lifestealBps <= 0n) {
      /*
       * Sem a joia, nem uma multiplicação. A saída antecipada não é uma
       * otimização: é o que faz uma corrida sem joias produzir exatamente o
       * estado que produzia antes deste gate, incluindo o resto.
       */
      return;
    }
    const result = resolveLifestealHeal({
      /*
       * De volta a milli, e a conversão está PROVADA em vez de esperada.
       *
       * `applied` sai de `resolveStrike` por um de dois ramos, e nenhum deles
       * pode ser grande:
       *
       * - golpe não letal - `applied` é `Number(damageMilli) / 1000`, e
       *   `damageMilli` estava estritamente abaixo de `vida x 1000`, que já é
       *   um inteiro seguro. Multiplicar por mil devolve exatamente o `bigint`
       *   de partida;
       * - golpe letal - `applied` é a vida que o alvo tinha, que é finita por
       *   definição. Um dano de 10^43 milli não chega aqui como 10^43: chega
       *   como a vida do alvo, porque o overkill nunca foi contado.
       *
       * Portanto o produto arbitrariamente grande nunca precisa de existir como
       * `double`, que é a propriedade que `damage/exact.ts` inteiro defende. O
       * arredondamento existe para a vida de um mob ser um float que pode não
       * cair exatamente num milésimo, e quantizar é a resposta honesta - não é
       * um truncamento a aceitar um valor malformado.
       */
      appliedDamageMilli: BigInt(Math.round(appliedDamage * HEAL_MILLI_PER_UNIT)),
      lifestealBps,
      carryBps: this.lifestealCarryBps,
      currentHealth: this.health,
      maxHealth: this.maxHealth(),
    });
    this.lifestealCarryBps = result.nextCarryBps;
    if (result.applied <= 0) {
      // Vida cheia: a passiva continua ativa, e o benefício deste instante é
      // zero. Sem overheal, sem escudo, e sem nada guardado para depois. E sem
      // evento: não houve facto nenhum a anunciar.
      return;
    }
    this.health += result.applied;
    if (projectileId === undefined) {
      /*
       * Sem chave de deduplicação não há anúncio, e a cura acontece na mesma.
       *
       * Inalcançável hoje - o único golpe sem projétil é o feixe do pet, e a
       * origem `pet` nunca chega a esta função - e escrito assim porque a
       * alternativa seria inventar um id, que é a maneira de um cliente desenhar
       * o mesmo sifão duas vezes depois de um replay.
       */
      return;
    }
    this.events.push({
      type: 'proc',
      procId: LIFESTEAL_HEAL_EFFECT,
      targetId: target.id,
      projectileId,
      tick: this.currentTick(),
      /*
       * A vida que a nave REALMENTE ganhou, em milli inteiro.
       *
       * `result.applied` é a unidade da simulação - um `number` fracionário,
       * porque a vida de uma nave é um float - e o arredondamento é a mesma
       * quantização que a entrada deste método já faz. Sempre maior que zero:
       * o ramo acima já saiu quando não houve cura.
       */
      healedMilli: Math.round(result.applied * HEAL_MILLI_PER_UNIT),
      /*
       * A posição do ALVO, que é de onde o fluxo parte. A mesma regra do corte,
       * e pela mesma razão: o projétil está na borda do casco no instante da
       * colisão, e um efeito centrado nele fica meio corpo ao lado num alvo
       * grande. O destino - a nave - é conhecido pela cena, que a desenha.
       */
      x: target.x,
      y: target.y,
    });
  }

  /**
   * Resolves one hit, whoever fired it.
   *
   * ONE path for the ship and the pet, on purpose. A kill is worth what a kill
   * is worth: the same overkill clamp, the same cumulative-total settlement,
   * the same single `kill` event. A second path for the pet is exactly how a
   * mob would end up paying twice.
   *
   * ## The origin, and why it replaced `fromPet = false`
   *
   * A default parameter is how a call site inherits a classification nobody
   * chose for it: a new caller that forgot the third argument was silently a
   * cannon shot. Every call site now NAMES its origin, and the boolean is
   * derived from it rather than the other way round.
   *
   * The `hit` EVENT keeps its `fromPet` field. That is presentation with a real
   * consumer - the renderer colours a pet's damage differently - and widening
   * the bridge to carry a three-valued origin would be changing a wire for a
   * distinction nothing outside the simulation can act on yet.
   *
   * The origin also decides whether the blow may set off a jewel, through the
   * closed table in `damage/origin.ts`. `pet` may not, which is a real product
   * decision rather than an oversight: the contract prices every chance against
   * ONE ship's cadence, and letting a pet proc would multiply each of them by
   * however many pets are alive.
   */
  private applyHit(
    target: MobEntity | BossEntity,
    damageMilli: bigint,
    origin: DamageOrigin,
    projectileId?: number,
  ): void {
    const fromPet = origin === 'pet';
    /*
     * A fronteira, e a única (gate V28F.B).
     *
     * `resolveStrike` decide a letalidade COMPARANDO um `bigint` com o tecto
     * `vida x 1000`, e só converte no ramo que a comparação já provou pequeno.
     * Um golpe de 10^43 milli e um de 40 400 matam um mob de 30 da mesma
     * maneira - o produto arbitrariamente grande nunca precisa de existir como
     * `number`.
     *
     * A vida de um mob é um float e sempre foi, portanto aqui não há resto a
     * carregar: `applied` pode ser fracionário e é aplicado tal como é. O duelo,
     * cuja vida é inteira por formato, usa `resolveIntegerStrike` e carrega o
     * resto no casco.
     *
     * Overkill continua a não ser contado: `applied` nunca passa a vida que
     * resta, que é o que a resolução garante em vez de um `Math.min` a seguir.
     */
    const applied = resolveStrike(damageMilli, target.health).applied;
    if (applied <= 0) {
      return;
    }
    target.health -= applied;
    this.recordDamage(applied);
    if (fromPet && this.pet) {
      // Attribution only - it is already inside the session total above, and
      // the Overview shows it as a share of that rather than as an addition.
      this.pet.damageDealt += applied;
    }
    this.events.push({
      type: 'hit',
      targetId: target.id,
      amount: applied,
      x: target.x,
      y: target.y,
      fromPet,
      // Omitted rather than set to undefined for a hit no shot caused, so the
      // event shape says "there was no projectile" instead of "there was one,
      // called nothing".
      ...(projectileId === undefined ? {} : { projectileId }),
    });
    /*
     * O Roubo de Vida, aqui e em mais nenhum sítio (gate V28G.I).
     *
     * ## Porque nesta linha, e depois do número do golpe
     *
     * Porque este é o único ponto da caça onde um golpe já ACONTECEU e o que
     * ele removeu ainda está na mão. `applied` é o dano depois do clamp do
     * overkill - um tiro de 40 num mob com 3 de vida vale 3 aqui - e é essa a
     * base que o contrato manda usar, revogando a D-4.3 do V28D.2.
     *
     * DEPOIS do evento de dano, e não antes: a ordem da fila é a ordem de
     * leitura de uma cena, primeiro o número do golpe e depois o sifão que ele
     * produziu. A ordem MECÂNICA é indiferente - a cura já foi decidida por
     * `applied`, e nada entre as duas linhas lhe toca.
     *
     * Como é UM golpe por chamada, o direto e cada vítima do splash contam
     * exatamente uma vez, sem que ninguém tenha de as separar: quem detona
     * chama isto uma vez pelo alvo e uma vez por vítima, e a lista do raio já
     * exclui o alvo direto.
     *
     * ## Porque a pergunta é à tabela fechada
     *
     * `damageOriginMayTriggerJewel` responde `true` só para `cannon`. Não há
     * lista de exclusões a manter - o pet não cura, o corte do Ciclone não cura,
     * e uma origem nova sem decisão é um erro de compilação. É a mesma tabela
     * que impede um proc de originar outro, e a profundidade continua a ser um.
     *
     * ## Nada aqui é reavaliado
     *
     * O coeficiente vem do perfil congelado da sessão. Não há consulta, não há
     * I/O, e não há releitura de catálogo: `resolveJewelEffects` lê um `Map`
     * construído ao carregar o módulo.
     */
    if (damageOriginMayTriggerJewel(origin)) {
      this.stealLife(applied, target, projectileId);
    }
    /*
     * Where the procs will hang, and the reason the origin is not a boolean.
     *
     * Empty today: `jewelTriggersFor` returns a frozen, shared empty array, so
     * this allocates nothing and iterates zero times whatever the cadence. A
     * `pet` blow and a `jewel` blow get the same empty array for a DIFFERENT
     * reason - the closed table forbids them - which is the contract this gate
     * delivers.
     */
    for (const effect of jewelTriggersFor(origin)) {
      throw new RangeError(`no jewel effect is registered in this gate: ${effect}`);
    }
    if (target.health > 0) {
      return;
    }
    const wasBoss = target.kind === 'boss';
    /*
     * O veneno morre com ele (gate V28G.M).
     *
     * Antes do evento de morte e antes de qualquer recompensa, porque o que
     * acabou foi a coisa que os carregava. O §"se o alvo morrer, todos os
     * stacks daquele alvo são removidos" não distingue quem os aplicou - numa
     * caçada só há um atacante, mas a regra é sobre o ALVO nos dois modos.
     */
    this.clearPoisonOf(target.id);
    /*
     * E as marcas de sangramento com ele (gate V28G.N).
     *
     * Pela mesma razão e no mesmo instante: o §"desaparece quando o alvo morre"
     * é sobre o ALVO, e o que acabou foi a coisa que as carregava. Dano
     * acumulado que o jogador nunca chegou a mandar rebentar não sobrevive ao
     * inimigo que o levava - é o preço de escolher quando gastar.
     */
    this.clearBleedingOf(target.id);
    /*
     * E os relógios de contacto das serras sobre ele (gate V28G.P).
     *
     * O §"ao morrer um alvo, seu estado de contato é removido" é sobre o ALVO, e
     * o que acabou foi a coisa que os carregava. Sem esta linha, um mob novo que
     * reutilizasse o id herdaria o intervalo já gasto do anterior.
     */
    this.clearSawContactsOf(target.id);
    this.events.push({ type: 'kill', targetId: target.id, wasBoss, x: target.x, y: target.y });
    this.kills += 1;
    this.runKills += 1;
    // Gold is settled from CUMULATIVE totals, before and after this kill.
    //
    // Every layer of it is a fraction: 1 Gold per 2 mobs from the balance, a
    // flat bonus per enemy from Money Money or Yeeeaaah, and a percentage from
    // Rich or Other Game. Computing per kill and rounding would quietly throw
    // each remainder away; taking the difference between two cumulative totals
    // keeps all of them exact, and keeps them exact across a checkpoint, an
    // F5, a death, a combine and a reconnection - because the counters this
    // reads are authoritative state that survives all of those.
    const goldBefore = this.goldOwed();
    let xpGain: bigint;
    if (wasBoss) {
      // Stated outright, not derived from the mob: retuning a mob must never
      // move the boss by accident.
      xpGain = bossBaseRewardForMap(HUNT_BALANCE.boss.xpReward, this.config.mapIndex);
      this.bossKills += 1;
      // The tier's base, accrued once, BEFORE the Gold diff below is taken -
      // so the bonuses apply to it exactly as they apply to a mob's.
      this.bossGoldBase += bossGoldForTier(
        bossBaseRewardForMap(HUNT_BALANCE.boss.goldReward, this.config.mapIndex),
        // A boss cannot be on the field without a tier - `spawnBoss` refuses
        // - so this is never the fallback in practice. It is spelled out
        // rather than asserted because a crash mid-settlement would be a worse
        // failure than a boss that paid the floor.
        this.encounterTier() ?? DEFAULT_BOSS_TIER,
      );
    } else {
      // XP accrues the same way, on its own counter: 4 XP every 3 kills, so
      // any three consecutive kills are worth exactly 4 wherever a boundary
      // happens to fall. Artifacts do not touch XP.
      const xpBefore = normalKillXp(this.normalKills, this.config.mapIndex);
      this.normalKills += 1;
      xpGain = normalKillXp(this.normalKills, this.config.mapIndex) - xpBefore;
    }
    const goldGain = this.goldOwed() - goldBefore;
    this.grantXp(xpGain);
    this.grantGold(goldGain);
    if (wasBoss) {
      this.boss = null;
      this.events.push({ type: 'boss-defeated', bossId: target.id });
      if (this.config.serverManagedBoss === true) {
        /*
         * Unbound until the server draws the next encounter. It cannot be done
         * here: the next tier and delay need cryptographic entropy, and the
         * simulation has none it is allowed to use. The gap lasts at most one
         * settlement window out of a three-to-sixteen minute delay.
         */
        this.bossEncounter = null;
        this.bossCountdownMs = 0;
      } else {
        // Legacy run with no server encounter: re-arm from its own stream.
        this.bossCountdownMs = this.rollBossDelayMs();
      }
    } else {
      this.mobs = this.mobs.filter((mob) => mob.id !== target.id);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Progression and metrics                                           */
  /* ------------------------------------------------------------------ */

  /**
   * Total Gold this run has earned from kills, artifacts and equipment
   * included.
   *
   * Cumulative and exact: mobs and bosses are settled separately because their
   * base rewards differ, but BOTH receive the flat Gold per enemy and BOTH
   * receive the percentage - there is no rule that treats the boss specially,
   * and there is no second formula for offline farm either. The Receptor's
   * thousandths of a Gold per enemy survive precisely because this is a running
   * total that is floored once, never a per-kill amount that is rounded.
   */
  private goldOwed(): bigint {
    const passives = this.passives;
    const equipment = this.equipment;
    const fromMobs = goldWithPassives(
      normalKillGold(this.normalKills, this.config.mapIndex),
      BigInt(this.normalKills),
      passives,
      equipment,
    );
    const fromBosses = goldWithPassives(
      // The accrued base, not a count times a constant: six tiers are six
      // different amounts and only a running total can carry them.
      this.bossGoldBase,
      BigInt(this.bossKills),
      passives,
      equipment,
    );
    return fromMobs + fromBosses;
  }

  private grantXp(amount: bigint): void {
    this.sessionXpTotal += amount;
    const before = this.progression.level;
    // One shared function, no per-level loop: the same call handles one level
    // and a million of them in the same handful of operations.
    this.progression = applyXpGain(this.progression, amount).state;
    if (this.progression.level > before) {
      this.events.push({ type: 'level-up', newLevel: this.progression.level.toString(10) });
    }
  }

  private grantGold(amount: bigint): void {
    this.gold += amount;
    this.runGold += amount;
    this.sessionGoldTotal += amount;
    if (this.firstGoldAtMs === null) {
      this.firstGoldAtMs = this.simTimeMs;
    }
    // The rolling window is presentation only; the authoritative totals above
    // stay bigint.
    this.goldEvents.push({ t: this.simTimeMs, amount: Number(amount) });
    pruneWindow(this.goldEvents, this.simTimeMs, HUNT_BALANCE.metricsWindowMs);
  }

  private recordDamage(amount: number): void {
    this.runDamage += amount;
    this.sessionDamageTotal += amount;
    if (this.firstDamageAtMs === null) {
      this.firstDamageAtMs = this.simTimeMs;
    }
    this.damageEvents.push({ t: this.simTimeMs, amount });
    pruneWindow(this.damageEvents, this.simTimeMs, HUNT_BALANCE.metricsWindowMs);
  }

  /**
   * Confirmed point allocation. Validates against the free-point balance so a
   * double-click can never allocate twice or go negative.
   */
  allocatePoints(allocation: { damage?: bigint; health?: bigint; speed?: bigint }): boolean {
    const healthPts = allocation.health ?? 0n;
    const result = allocateProgressionPoints(this.progression, {
      damage: allocation.damage ?? 0n,
      health: healthPts,
      attackSpeed: allocation.speed ?? 0n,
    });
    if (!result.ok) {
      return false;
    }
    this.progression = result.state;
    if (healthPts > 0n) {
      // Raising max health also heals by the same flat amount.
      this.health = Math.min(
        this.maxHealth(),
        this.health + renderablePoints(healthPts) * HUNT_BALANCE.allocation.healthPerPoint,
      );
    }
    return true;
  }

  private metricWindow(
    events: readonly TimedAmount[],
    firstAt: number | null,
    sessionTotal: number,
  ): MetricWindow {
    const windowTotal = events.reduce((sum, event) => sum + event.amount, 0);
    const elapsedMs = firstAt === null ? null : this.simTimeMs - firstAt;
    return {
      windowTotal,
      elapsedMs,
      windowFull: elapsedMs !== null && elapsedMs >= HUNT_BALANCE.metricsWindowMs,
      sessionTotal,
    };
  }

  damageMetric(): MetricWindow {
    pruneWindow(this.damageEvents, this.simTimeMs, HUNT_BALANCE.metricsWindowMs);
    return this.metricWindow(this.damageEvents, this.firstDamageAtMs, this.sessionDamageTotal);
  }

  goldMetric(): MetricWindow {
    pruneWindow(this.goldEvents, this.simTimeMs, HUNT_BALANCE.metricsWindowMs);
    // The rolling metric is presentation; the exact total lives in bigint.
    return this.metricWindow(
      this.goldEvents,
      this.firstGoldAtMs,
      renderableAmount(this.sessionGoldTotal),
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Read access for the renderer / UI                                 */
  /* ------------------------------------------------------------------ */

  getPhase(): HuntPhase {
    return this.phase;
  }

  /**
   * Simulated milliseconds since this run began.
   *
   * The same number `exportSnapshot()` reports, without building a whole
   * snapshot to reach it. It exists because continuity is asserted ON this
   * clock - a run that goes backwards inside one session is the shape two
   * shipped defects took - and reading it at every adoption must not cost an
   * export.
   */
  getTickMs(): number {
    return this.simTimeMs;
  }

  getMobs(): readonly MobEntity[] {
    return this.mobs;
  }

  getBoss(): BossEntity | null {
    return this.boss;
  }

  getProjectiles(): readonly ProjectileEntity[] {
    return this.projectiles;
  }

  getDeathStats(): DeathStats | null {
    return this.deathStats;
  }

  getDeathCause(): 'boss-breach' | 'mob-leaks' | null {
    return this.lastDeathCause;
  }

  bossCountdownSeconds(): number | null {
    return this.boss ? null : Math.max(0, Math.ceil(this.bossCountdownMs / 1000));
  }

  drainEvents(): readonly HuntEvent[] {
    const drained = this.events;
    this.events = [];
    return drained;
  }

  /** The authoritative progression this run predicts from. */
  getProgression(): ProgressionState {
    return this.progression;
  }

  getGold(): bigint {
    return this.gold;
  }

  getProgress() {
    const required = xpRequiredForLevel(this.progression.level);
    return {
      level: this.progression.level,
      xpIntoLevel: this.progression.xpIntoLevel,
      lifetimeXp: this.progression.lifetimeXp,
      xpForNextLevel: required,
      // Basis points rather than a ratio: the division happens once, in
      // bigint, so an enormous level never turns the bar into NaN.
      xpProgressBps: Number((this.progression.xpIntoLevel * 10_000n) / required),
      gold: this.gold,
      freePoints: this.progression.freePoints,
      damagePoints: this.progression.damagePoints,
      healthPoints: this.progression.healthPoints,
      speedPoints: this.progression.attackSpeedPoints,
      health: this.health,
      kills: this.kills,
      runKills: this.runKills,
    };
  }

  /** Everything the Overview panel needs, in one call. */
  getOverviewSnapshot() {
    const progress = this.getProgress();
    const ship = shipCatalogEntry(this.config.shipIndex);
    return {
      phase: this.phase,
      mapIndex: this.config.mapIndex,
      shipRarity: ship.rarity,
      // Progression crosses to the UI as decimal strings: there is no level
      // cap, so a JSON number would stop being exact long before the curve
      // does. `xpProgressBps` is the only derived ratio, computed once in
      // bigint so the XP bar can never become NaN.
      level: progress.level.toString(10),
      xpIntoLevel: progress.xpIntoLevel.toString(10),
      lifetimeXp: progress.lifetimeXp.toString(10),
      xpForNextLevel: progress.xpForNextLevel.toString(10),
      xpProgressBps: progress.xpProgressBps,
      gold: progress.gold.toString(10),
      health: progress.health,
      maxHealth: this.maxHealth(),
      freePoints: progress.freePoints.toString(10),
      /** Clamped copy for panel arithmetic; the string above is authoritative. */
      freePointsRenderable: renderableAmount(progress.freePoints),
      baseDamage: this.baseDamage(),
      damageBonusPercent: this.damageBonusPercent(),
      /**
       * The bonus attack, on its own line.
       *
       * Reported separately because it is not inside anything else: it is not
       * in the base, not in the percentage, and a panel that folded it into
       * either would be telling the player a formula the arena does not use.
       */
      bonusAttackPerProjectile: this.bonusAttackPerProjectile(),
      totalDamagePerProjectile: this.totalDamagePerProjectile(),
      baseAttacksPerSecond: this.baseAttacksPerSecond(),
      attackSpeedBonusPercent: this.attackSpeedBonusPercent(),
      totalAttacksPerSecond: this.totalAttacksPerSecond(),
      /*
       * **A cadência que a caça deixa emitir, ao lado da pedida** (V28I.CF.3).
       *
       * As duas, e um booleano que diz se o teto está a morder. Mostrar só uma
       * seria mentir ao jogador sobre a nave dele — numa direção ou na outra.
       */
      emittedAttacksPerSecond: this.emittedAttacksPerSecond(),
      cadenceLimited: this.cadenceIsLimited(),
      baseHealth: this.baseHealthValue(),
      healthBonusPercent: this.healthBonusPercent(),
      cannonRarity: this.config.cannonRarity,
      cannonLevel: this.config.cannonLevel,
      cannonCount: cannonCountForLevel(this.config.cannonLevel),
      projectilesPerCannon: this.projectilesPerCannon(),
      projectilesPerVolley: this.projectilesPerVolley(),
      damageMetric: this.damageMetric(),
      goldMetric: this.goldMetric(),
      /**
       * The active pet, or null. Everything the Overview reports about it is
       * derived here so the panel never recomputes a combat number of its own.
       *
       * `damageDealt` is a SHARE of the session damage above, never an
       * addition to it: the pet's hits went through the same `applyHit` and
       * were already counted once.
       */
      pet: this.pet
        ? {
            definitionId: this.pet.definitionId,
            evolution: this.pet.evolution,
            muzzles: petProjectileCount(this.pet.definitionId, this.pet.evolution),
            damagePerBullet: petBulletDamage(
              this.pet.definitionId,
              this.totalDamagePerProjectile(),
              this.pet.damageFoodCount,
            ),
            bulletsPerSecond: petBulletsPerSecond(
              this.pet.definitionId,
              this.pet.evolution,
              this.pet.attackSpeedFoodCount,
            ),
            damagePerSecond: petDamagePerSecond(
              this.pet.definitionId,
              this.pet.evolution,
              this.totalDamagePerProjectile(),
              this.pet.damageFoodCount,
              this.pet.attackSpeedFoodCount,
            ),
            damageFoodCount: this.pet.damageFoodCount,
            attackSpeedFoodCount: this.pet.attackSpeedFoodCount,
            damageDealt: this.pet.damageDealt,
          }
        : null,
      mobsOnField: this.mobs.length + (this.boss ? 1 : 0),
      /**
       * Quantas marcas de sangramento estão espalhadas AGORA (gate V28G.N).
       *
       * O número que o botão `DETONAR ×N` mostra, respondido pela simulação
       * autoritativa e nunca contado pela interface a partir de outra lista.
       * Aqui há um autor só - o jogador - portanto não há filtro de autoria a
       * aplicar, ao contrário do duelo.
       *
       * Zero é exatamente quando o comando é um `no-op` e o botão fica
       * desativado, e é o mesmo número em qualquer ecrã sem corrida.
       */
      bleedingMarks: this.bleeding.length,
      kills: progress.kills,
      bossCountdownSeconds: this.bossCountdownSeconds(),
      bossActive: this.boss !== null,
      bossHealth: this.boss ? { current: this.boss.health, max: this.boss.maxHealth } : null,
      survivedMs: this.simTimeMs - this.runStartMs,
    };
  }
}

export type OverviewSnapshot = ReturnType<HuntSimulation['getOverviewSnapshot']>;
